/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  КОНТРАКТНАЯ ПОВЕРХНОСТЬ API — единственный модуль, через который
 *  приложение видит бэкенд
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Всё остальное (`lib/`, `api/`, `stores/`, `views/`) импортирует типы **только
 * отсюда**. Источник истины — `acr-backend/api/admin/v1/*.proto`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ПОЧЕМУ ЗДЕСЬ НЕТ `import ... from '@/types/api.gen'`
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `npm run api:types` по-прежнему собирает `src/types/api.gen.ts` из
 * `acr-backend/openapi.yaml`, и файл полезен, чтобы **глазами сверить** дрейф
 * контракта. Но выводить из него типы нельзя, и причина ровно одна:
 *
 *     gnostic в этом проекте запущен БЕЗ `enum_type=string`
 *     (`acr-backend/buf.gen.yaml`), поэтому КАЖДОЕ enum-поле приезжает
 *     в спеку как `format: enum` → `number`.
 *
 * А по проводу enum'ы едут **строками** (`"USER_STATUS_ACTIVE"`) — так
 * настроен кодек в `internal/server/codec.go`. То есть сгенерированный
 * `status?: number` не просто неточен, он противоречит реальному ответу: с ним
 * `p.status === 'PARTICIPANT_STATUS_PARTICIPATING'` не компилировалось бы, а
 * `p.status === 1` компилировалось бы и никогда не срабатывало.
 *
 * Перекрывать пришлось бы почти каждое сообщение (enum есть у User, Event,
 * Participant, ParticipantField, ImportDecision, ParticipantImportInput и во
 * всех их запросах), после чего от генератора не осталось бы ничего, кроме
 * шума. Поэтому типы ниже рукописные.
 *
 * Если контракт когда-нибудь начнут генерировать с `enum_type=string` —
 * прослойку имеет смысл переписать на `Required<Schemas[...]>`, как в
 * scan-admin. До тех пор дрейф ловится не компилятором, а ревью `.proto`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ФОРМАТ ПРОВОДА — kratos v3, кодек `json`, перерегистрированный на protojson
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `internal/server/codec.go` ставит `EmitUnpopulated: true` и
 * `UseProtoNames: false`. Отсюда пять следствий, и все пять учтены в типах:
 *
 * 1. **Ключи — lowerCamelCase.** В `.proto` написано `full_name`, по проводу
 *    приезжает `fullName`. В `.proto` за именем поля не подсматриваем.
 *
 * 2. **Присутствует КАЖДОЕ поле в КАЖДОМ ответе.** «Пусто» кодируется
 *    значением, а не отсутствием ключа:
 *      • скаляр (string/number/bool) → `""`, `0`, `false`;
 *      • **message-поле** (включая `Timestamp`) → **`null`** ⇒ тип `T | null`,
 *        **не** `T?`;
 *      • `repeated` → `[]` (никогда не `null`);
 *      • `map` → `{}`.
 *    Практический вывод: `resp.events.length` и `err.metadata[k]` читаются
 *    безусловно, без `?.` и без `?? []`.
 *
 * 3. **Исключение — proto3 `optional`.** Такие поля компилируются в
 *    синтетический `oneof`, и `EmitUnpopulated` на них не распространяется:
 *    невыставленное поле **отсутствует в JSON целиком**. Только их типизируем
 *    `T?` — это `Update*Request` (пользователь, мероприятие, участник).
 *
 * 4. **Enum'ы едут строками**: `"EVENT_STATUS_PLANNED"`, не числами. Нулевое
 *    значение (`*_UNSPECIFIED`) тоже приезжает именем.
 *
 * 5. **Timestamp — RFC3339 в UTC с литеральной `Z`**, дробная часть печатается,
 *    только когда не нулевая: `2026-08-13T14:01:39.340Z` и
 *    `2026-08-13T14:01:39Z` — обе формы валидны и обе встречаются в одном
 *    ответе. ⇒ Разбирать **только** `new Date(iso)`; резать по фиксированным
 *    позициям нельзя, длина строки плавает.
 *
 * 6. **Пагинация объявлена `uint32`** ровно затем, чтобы приезжать числами
 *    (`int64` protojson отдаёт строками — здесь такого нет).
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЖИВАЯ МИНА ТРАНСПОРТА: `Content-Type` обязателен даже на пустом теле
 * ───────────────────────────────────────────────────────────────────────────
 *
 * kratos требует `Content-Type: application/json` на **каждом** запросе, у
 * которого в `google.api.http` объявлен `body`, — включая те, чьё тело пустое.
 * Без второго аргумента axios заголовок не проставит (ставить его нечему:
 * `transformRequest` смотрит на тело), декодер не найдёт кодек и ответит 400.
 *
 *     http.post('/api/v1/auth/sessions/revoke-others')      // ← 400
 *     http.post('/api/v1/auth/sessions/revoke-others', {})  // ← 200 OK
 *
 * Страховка стоит в `lib/http.ts`: request-интерцептор подставляет
 * `config.data ??= {}` любому POST/PUT/PATCH без тела, поэтому забыть второй
 * аргумент физически безопасно. Явный `{}` в `api/*` всё равно пишем — чтобы
 * читающему было видно, что пустое тело осознанно.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  Прочее, что нужно знать вызывающему коду
 * ───────────────────────────────────────────────────────────────────────────
 *
 * • Авторизация — `Authorization: Bearer <accessToken>`.
 * • `baseURL` пустой: пути в `google.api.http` уже начинаются с `/api/v1`,
 *   прокси Vite ничего не переписывает.
 * • Идентификаторы в путях (`{eventId}`, `{id}`) в тело **не** попадают —
 *   поэтому в `*Request` ниже их нет.
 * • `POST /api/v1/auth/refresh` обязан быть single-flight: сервер ротирует
 *   refresh-токен на каждое использование.
 */

/* ═════════════════════════════════════════════════════════════════════════
   1. РОЛИ И СТАТУСЫ
   ═════════════════════════════════════════════════════════════════════════
   `enum` в TS запрещён (`erasableSyntaxOnly: true` в tsconfig.app.json), да и
   не нужен: `as const`-массив даёт и union для типов, и итерируемый список
   для `<Select>`, и рантайм-проверку `includes` — одним объявлением.

   Полнота проверяется в обе стороны бесплатно: списки объявлены через
   `satisfies readonly X[]` (лишнее значение → ошибка компиляции), а карты
   подписей — через тотальный `Record<X, string>` (недостающее → ошибка). */

/* ─────────────────────────── СИСТЕМНАЯ РОЛЬ ─────────────────────────── */

/**
 * `admin.v1.SystemRole` — роль **учётной записи**, ни к какому мероприятию не
 * привязанная.
 *
 * ⚠️ Ролей мероприятия (`owner`, `viewer`) здесь нет и быть не может: они
 * принадлежат паре (мероприятие, пользователь) и живут в `MemberRole`. Один и
 * тот же человек бывает владельцем одного мероприятия и наблюдателем другого —
 * на уровне учётной записи такой вопрос просто не задан.
 *
 * Два типа объявлены отдельно **намеренно**: один enum на оба понятия сделал бы
 * `role === 'ADMIN'` и `role === 'OWNER'` сравнимыми, и первое же написанное
 * поверх них `>=` было бы повышением привилегий.
 */
export type SystemRole = 'SYSTEM_ROLE_UNSPECIFIED' | 'SYSTEM_ROLE_USER' | 'SYSTEM_ROLE_ADMIN'
export const SYSTEM_ROLES = [
  'SYSTEM_ROLE_UNSPECIFIED',
  'SYSTEM_ROLE_USER',
  'SYSTEM_ROLE_ADMIN',
] as const satisfies readonly SystemRole[]

/** Роли, которые реально можно назначить: `*_UNSPECIFIED` — протобуфный ноль, а не выбор человека. */
export const ASSIGNABLE_SYSTEM_ROLES = [
  'SYSTEM_ROLE_USER',
  'SYSTEM_ROLE_ADMIN',
] as const satisfies readonly SystemRole[]

/**
 * Русские подписи системных ролей — **единственное** место, где они живут.
 * Карта тотальная, поэтому индексация всегда даёт строку и `?? fallback`
 * по вьюхам не расползается.
 */
export const SYSTEM_ROLE_LABELS: Record<SystemRole, string> = {
  SYSTEM_ROLE_UNSPECIFIED: 'Не указана',
  SYSTEM_ROLE_USER: 'Пользователь',
  SYSTEM_ROLE_ADMIN: 'Администратор',
}

/**
 * Пояснение к роли для форм. `SYSTEM_ROLE_USER` сам по себе не даёт **ничего** —
 * все полномочия приходят из членства в мероприятиях, и человек, которому
 * завели учётку и забыли добавить в мероприятие, видит пустой список. Это
 * ожидаемое состояние, и форма обязана про него сказать заранее.
 */
export const SYSTEM_ROLE_HINTS: Record<SystemRole, string> = {
  SYSTEM_ROLE_UNSPECIFIED: '',
  SYSTEM_ROLE_USER: 'Доступ только к тем мероприятиям, в состав которых его добавили.',
  SYSTEM_ROLE_ADMIN: 'Полный доступ ко всем мероприятиям и к управлению пользователями.',
}

/* ─────────────────────────── СТАТУС УЧЁТКИ ──────────────────────────── */

/** `admin.v1.UserStatus`. Удаление пользователя мягкое — оно переводит в `DISABLED`. */
export type UserStatus = 'USER_STATUS_UNSPECIFIED' | 'USER_STATUS_ACTIVE' | 'USER_STATUS_DISABLED'
export const USER_STATUSES = [
  'USER_STATUS_UNSPECIFIED',
  'USER_STATUS_ACTIVE',
  'USER_STATUS_DISABLED',
] as const satisfies readonly UserStatus[]

/** Статусы, которые реально можно выставить через `PATCH /users/{id}`. */
export const ASSIGNABLE_USER_STATUSES = [
  'USER_STATUS_ACTIVE',
  'USER_STATUS_DISABLED',
] as const satisfies readonly UserStatus[]

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  USER_STATUS_UNSPECIFIED: 'Не указан',
  USER_STATUS_ACTIVE: 'Активен',
  USER_STATUS_DISABLED: 'Отключён',
}

/* ─────────────────────── РОЛЬ ВНУТРИ МЕРОПРИЯТИЯ ────────────────────── */

/**
 * `admin.v1.MemberRole` — роль в **конкретном** мероприятии.
 *
 * ⚠️ Из порядка значений **ничего** не выводится: проверки сравнивают роль с
 * перечисленными явно, а не через `>=`. Ролей теперь три, и номер у новой — 3,
 * то есть в проводе она стоит ПОСЛЕ наблюдателя, хотя прав у неё больше.
 * Любое `role >= …` на этом и ломается — ровно та ошибка, ради предотвращения
 * которой на бэкенде `MemberRole` и `SystemRole` сделаны разными Go-типами
 * (см. `internal/biz/role.go`, шапка файла).
 *
 * ⚠️ `MEMBER_ROLE_VIEWER_PLUS` — «Наблюдатель+»: обычный наблюдатель, которому
 * дополнительно можно писать значение дополнительного поля с
 * `highlightOnScan`. Право привязано к ПОЛЮ, а не к уровню доступа: подсветка
 * уже означает «эту графу проверяют на входе», поэтому она же определяет, что
 * человеку на двери можно править. Второго признака нет и не будет. Следствие,
 * принятое сознательно: сняв подсветку, владелец забирает это право у всех
 * «наблюдателей+» сразу.
 */
export type MemberRole =
  | 'MEMBER_ROLE_UNSPECIFIED'
  | 'MEMBER_ROLE_OWNER'
  | 'MEMBER_ROLE_VIEWER'
  | 'MEMBER_ROLE_VIEWER_PLUS'

/**
 * Все значения enum'а — **в проводном порядке номеров** (0, 1, 2, 3).
 *
 * ⚠️ Это НЕ порядок для показа человеку. Новые роли дописываются сюда в конец,
 * потому что номера в proto — это провод, их не переупорядочивают. Порядок для
 * экранов задаётся отдельно, см. `ASSIGNABLE_MEMBER_ROLES` и `memberRoleRank`.
 */
export const MEMBER_ROLES = [
  'MEMBER_ROLE_UNSPECIFIED',
  'MEMBER_ROLE_OWNER',
  'MEMBER_ROLE_VIEWER',
  'MEMBER_ROLE_VIEWER_PLUS',
] as const satisfies readonly MemberRole[]

/**
 * Роли, которые реально можно назначить участнику состава, **в том порядке, в
 * котором их показывают человеку**: владелец → наблюдатель+ → наблюдатель,
 * то есть по убыванию полномочий.
 *
 * ⚠️ ПОРЯДОК ЗАДАН ЗДЕСЬ ЯВНО И НЕ СОВПАДАЕТ НИ С НОМЕРАМИ ENUM'А, НИ С
 * ПОРЯДКОМ ОТВЕТА СЕРВЕРА. Состав приезжает отсортированным по колонке роли
 * (`ent.Asc(eventmember.FieldRole)`), то есть лексикографически:
 * `owner` < `viewer` < `viewer_plus` — «наблюдатель+» оказывается в самом
 * конце, ниже обычных наблюдателей. Полагаться на ответ значило бы показать
 * список, в котором роли идут вперемешку по силе.
 */
export const ASSIGNABLE_MEMBER_ROLES = [
  'MEMBER_ROLE_OWNER',
  'MEMBER_ROLE_VIEWER_PLUS',
  'MEMBER_ROLE_VIEWER',
] as const satisfies readonly MemberRole[]

/**
 * Вес роли для сортировки списков: чем меньше, тем выше в списке.
 *
 * ⚠️ Это порядок ПОКАЗА, а не порядок прав. Ни одна проверка «можно ли»
 * не имеет права его читать: сравнение весов — это то самое «сравнение ролей
 * по порядку», которое ломается на третьей роли. Права спрашивают у
 * `canManage` / `canEditScanFields` в `stores/event.ts`, и там роли
 * перечислены поимённо.
 */
const MEMBER_ROLE_RANK: Record<MemberRole, number> = {
  MEMBER_ROLE_OWNER: 0,
  MEMBER_ROLE_VIEWER_PLUS: 1,
  MEMBER_ROLE_VIEWER: 2,
  // Роли без значения в списках быть не должно, но если она всё же приедет —
  // пусть стоит последней, а не первой.
  MEMBER_ROLE_UNSPECIFIED: 3,
}

/** Компаратор для `.sort()`: владелец → наблюдатель+ → наблюдатель. */
export function memberRoleRank(role: MemberRole): number {
  return MEMBER_ROLE_RANK[role] ?? MEMBER_ROLE_RANK.MEMBER_ROLE_UNSPECIFIED
}

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  MEMBER_ROLE_UNSPECIFIED: 'Не указана',
  MEMBER_ROLE_OWNER: 'Владелец',
  MEMBER_ROLE_VIEWER: 'Наблюдатель',
  MEMBER_ROLE_VIEWER_PLUS: 'Наблюдатель+',
}

export const MEMBER_ROLE_HINTS: Record<MemberRole, string> = {
  MEMBER_ROLE_UNSPECIFIED: '',
  MEMBER_ROLE_OWNER: 'Полный доступ к мероприятию, включая управление его составом.',
  MEMBER_ROLE_VIEWER: 'Только чтение: списки, карточки участников, сканирование QR.',
  // ⚠️ Формулировка называет ровно одно отличие и его цену: право приходит от
  // подсветки поля, а не от роли, и исчезает вместе с ней.
  MEMBER_ROLE_VIEWER_PLUS:
    'Тот же наблюдатель, но может отмечать графы, подсвеченные для сканирования, — '
    + 'и в сканере, и в карточке участника. Больше ничего: ни правки ФИО, ни выдачи '
    + 'аккредитации, ни управления составом. Список таких граф задаёт подсветка в '
    + 'конструкторе полей: снимете её — право на эту графу пропадёт.',
}

/* ─────────────────────── СТАТУС МЕРОПРИЯТИЯ ─────────────────────────── */

/**
 * `admin.v1.EventStatus`.
 *
 * Переходы между статусами на сервере **не ограничены**: любой статус можно
 * поставить любым. Правил вида «нельзя завершить то, что не начиналось» нет —
 * поле хранит то, что прислали, — поэтому и в UI порядок статусов не значит
 * порядок жизненного цикла.
 */
export type EventStatus =
  | 'EVENT_STATUS_UNSPECIFIED'
  | 'EVENT_STATUS_PLANNED'
  | 'EVENT_STATUS_IN_PROGRESS'
  | 'EVENT_STATUS_COMPLETED'
  | 'EVENT_STATUS_CANCELLED'
export const EVENT_STATUSES = [
  'EVENT_STATUS_UNSPECIFIED',
  'EVENT_STATUS_PLANNED',
  'EVENT_STATUS_IN_PROGRESS',
  'EVENT_STATUS_COMPLETED',
  'EVENT_STATUS_CANCELLED',
] as const satisfies readonly EventStatus[]

/**
 * Статусы, которые реально можно выставить. `UPDATE` отвергает
 * `EVENT_STATUS_UNSPECIFIED` (на пути записи он ничего не значит), `CREATE`
 * трактует его как «взять умолчание», то есть `PLANNED`.
 */
export const ASSIGNABLE_EVENT_STATUSES = [
  'EVENT_STATUS_PLANNED',
  'EVENT_STATUS_IN_PROGRESS',
  'EVENT_STATUS_COMPLETED',
  'EVENT_STATUS_CANCELLED',
] as const satisfies readonly EventStatus[]

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  EVENT_STATUS_UNSPECIFIED: 'Не указан',
  EVENT_STATUS_PLANNED: 'Запланировано',
  EVENT_STATUS_IN_PROGRESS: 'Идёт',
  EVENT_STATUS_COMPLETED: 'Завершено',
  EVENT_STATUS_CANCELLED: 'Отменено',
}

/* ─────────────────────── СТАТУС УЧАСТНИКА ───────────────────────────── */

/** `admin.v1.ParticipantStatus`. Это **не** статус учётной записи и не статус мероприятия. */
export type ParticipantStatus =
  | 'PARTICIPANT_STATUS_UNSPECIFIED'
  | 'PARTICIPANT_STATUS_PARTICIPATING'
  | 'PARTICIPANT_STATUS_NOT_PARTICIPATING'
export const PARTICIPANT_STATUSES = [
  'PARTICIPANT_STATUS_UNSPECIFIED',
  'PARTICIPANT_STATUS_PARTICIPATING',
  'PARTICIPANT_STATUS_NOT_PARTICIPATING',
] as const satisfies readonly ParticipantStatus[]

/**
 * Статусы, которые реально можно выставить: `SetParticipantStatus` отвергает
 * ноль, `CreateParticipant` трактует его как `PARTICIPATING`.
 */
export const ASSIGNABLE_PARTICIPANT_STATUSES = [
  'PARTICIPANT_STATUS_PARTICIPATING',
  'PARTICIPANT_STATUS_NOT_PARTICIPATING',
] as const satisfies readonly ParticipantStatus[]

export const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatus, string> = {
  PARTICIPANT_STATUS_UNSPECIFIED: 'Не указан',
  PARTICIPANT_STATUS_PARTICIPATING: 'Участвует',
  PARTICIPANT_STATUS_NOT_PARTICIPATING: 'Не участвует',
}

/* ─────────────────── СТАТУС ВЫДАЧИ АККРЕДИТАЦИИ ────────────────────────
 *
 * ⚠️ ЭТО НЕ `ParticipantStatus`. Их путают на слове «статус», а не по смыслу:
 *
 *   ParticipantStatus     участвует ли человек в соревновании;
 *   AccreditationStatus   отдали ли ему бейдж на руки.
 *
 * Ни одно не выводится из другого: снятого с соревнований могли аккредитовать
 * час назад, а допущенный может ещё стоять в очереди на стойке. Отдельный тип,
 * отдельные подписи, отдельная колонка в таблице.
 *
 * Проставляется ВРУЧНУЮ и двумя путями: отметкой прямо в строке списка (по
 * одному или пачкой — `setAccreditationStatus`) и полем формы в карточке
 * участника. Печать бейджа его не меняет — отправить карточку на принтер и
 * вручить её человеку это разные события. */

export type AccreditationStatus =
  | 'ACCREDITATION_STATUS_UNSPECIFIED'
  | 'ACCREDITATION_STATUS_NOT_ISSUED'
  | 'ACCREDITATION_STATUS_ISSUED'

/**
 * Значения, которые реально можно выставить. Ноль означает «не менять» и в
 * форме выбирать его незачем.
 */
export const ASSIGNABLE_ACCREDITATION_STATUSES = [
  'ACCREDITATION_STATUS_NOT_ISSUED',
  'ACCREDITATION_STATUS_ISSUED',
] as const satisfies readonly AccreditationStatus[]

export const ACCREDITATION_STATUS_LABELS: Record<AccreditationStatus, string> = {
  ACCREDITATION_STATUS_UNSPECIFIED: 'Не указано',
  ACCREDITATION_STATUS_NOT_ISSUED: 'Не выдана',
  ACCREDITATION_STATUS_ISSUED: 'Выдана',
}

/* ─────────────────── ТИП ДОПОЛНИТЕЛЬНОГО ПОЛЯ ───────────────────────── */

/**
 * `admin.v1.ParticipantFieldType` — семь типов дополнительного поля.
 *
 * Enum, а не bool «это галочка», ровно затем, чтобы добавление типа было
 * добавлением значения СЮДА, а не изобретением второго признака рядом. Так и
 * вышло: сначала здесь была одна галочка.
 *
 * ⚠️ КАЖДЫЙ ТИП ЖИВЁТ В СВОЁМ ПОЛЕ ЗНАЧЕНИЯ, а не в одной перегруженной
 * строке — см. `ParticipantFieldValue`:
 *
 *     checkbox                  → `boolValue`
 *     text / textarea / select  → `textValue`
 *     date / datetime           → `dateValue`
 *     file                      → `file`
 *
 * Одна текстовая колонка на всех заставила бы КАЖДОГО читателя (список,
 * фильтр, экспорт, бейдж, сканер) знать, как разобрать чужой формат, и первое
 * же расхождение между ними было бы невидимым.
 *
 * ⚠️ ТИП ЗАДАЁТСЯ ОДИН РАЗ И НЕ МЕНЯЕТСЯ: `UpdateParticipantFieldRequest` его
 * не содержит. У уже проставленных значений нет осмысленного перевода из
 * галочки в текст. Нужен другой тип — заводится другое поле.
 */
export type ParticipantFieldType =
  | 'PARTICIPANT_FIELD_TYPE_UNSPECIFIED'
  | 'PARTICIPANT_FIELD_TYPE_CHECKBOX'
  | 'PARTICIPANT_FIELD_TYPE_TEXT'
  | 'PARTICIPANT_FIELD_TYPE_TEXTAREA'
  | 'PARTICIPANT_FIELD_TYPE_SELECT'
  | 'PARTICIPANT_FIELD_TYPE_FILE'
  | 'PARTICIPANT_FIELD_TYPE_DATE'
  | 'PARTICIPANT_FIELD_TYPE_DATETIME'
  | 'PARTICIPANT_FIELD_TYPE_COUNTRY'

/**
 * Порядок для выпадающего списка в конструкторе — **не** порядок enum'а.
 *
 * `UNSPECIFIED` сюда не входит намеренно: выбрать «не указан» в конструкторе
 * нельзя, это значение существует только на проводе (сервер читает его как
 * `CHECKBOX` ради клиента, который про типы не знает). Список начинается с
 * галочки и текста, потому что их заводят чаще всего.
 */
export const PARTICIPANT_FIELD_TYPES = [
  'PARTICIPANT_FIELD_TYPE_CHECKBOX',
  'PARTICIPANT_FIELD_TYPE_TEXT',
  'PARTICIPANT_FIELD_TYPE_TEXTAREA',
  'PARTICIPANT_FIELD_TYPE_SELECT',
  'PARTICIPANT_FIELD_TYPE_DATE',
  'PARTICIPANT_FIELD_TYPE_DATETIME',
  'PARTICIPANT_FIELD_TYPE_COUNTRY',
  'PARTICIPANT_FIELD_TYPE_FILE',
] as const satisfies readonly ParticipantFieldType[]

export const PARTICIPANT_FIELD_TYPE_LABELS: Record<ParticipantFieldType, string> = {
  PARTICIPANT_FIELD_TYPE_UNSPECIFIED: 'Не указан',
  PARTICIPANT_FIELD_TYPE_CHECKBOX: 'Галочка',
  PARTICIPANT_FIELD_TYPE_TEXT: 'Текст',
  PARTICIPANT_FIELD_TYPE_TEXTAREA: 'Многострочный текст',
  PARTICIPANT_FIELD_TYPE_SELECT: 'Список',
  PARTICIPANT_FIELD_TYPE_FILE: 'Файл',
  PARTICIPANT_FIELD_TYPE_DATE: 'Дата',
  PARTICIPANT_FIELD_TYPE_DATETIME: 'Дата и время',
  PARTICIPANT_FIELD_TYPE_COUNTRY: 'Страна',
}

/**
 * Пояснение под названием типа в конструкторе.
 *
 * Нужны потому, что «Текст» и «Многострочный текст» различаются ТОЛЬКО
 * отрисовкой, а «Галочка» и «Список из да/нет» — тем, как по ним потом
 * фильтруют. Человек, заводящий поле, выбирает один раз и навсегда (тип не
 * меняется), поэтому цена ошибки здесь — заведённое заново поле и потерянные
 * значения.
 */
export const PARTICIPANT_FIELD_TYPE_HINTS: Record<ParticipantFieldType, string> = {
  PARTICIPANT_FIELD_TYPE_UNSPECIFIED: '',
  PARTICIPANT_FIELD_TYPE_CHECKBOX: 'Отметка «есть / нет»: взвешивание, оплата, согласие.',
  PARTICIPANT_FIELD_TYPE_TEXT: 'Одна строка: номер, разряд, город.',
  PARTICIPANT_FIELD_TYPE_TEXTAREA: 'Несколько строк: примечание, состав команды.',
  PARTICIPANT_FIELD_TYPE_SELECT: 'Выбор одного из заданных вариантов.',
  PARTICIPANT_FIELD_TYPE_FILE: 'Приложенный документ: справка, скан страховки. До 10 МиБ.',
  PARTICIPANT_FIELD_TYPE_DATE: 'Дата без времени: рождение, срок действия справки.',
  PARTICIPANT_FIELD_TYPE_DATETIME: 'Дата вместе со временем: когда пройдено взвешивание.',
  PARTICIPANT_FIELD_TYPE_COUNTRY:
    'Страна из общего справочника системы: печатается на бейдже флагом и названием.',
}

/** Тип, у которого есть список вариантов. Копия `biz.ParticipantFieldType.UsesOptions`. */
export function fieldTypeUsesOptions(type: ParticipantFieldType): boolean {
  return type === 'PARTICIPANT_FIELD_TYPE_SELECT'
}

/**
 * Тип, чьё значение НЕЛЬЗЯ записать через `SetParticipantFieldValue`.
 *
 * Ровно один — `file`: содержимое едет multipart'ом по своему адресу, а этот
 * RPC для него принимает только `clear: true`. Проверка вынесена сюда, чтобы
 * таблица и форма не переизобретали её сравнением со строковым литералом.
 */
export function fieldTypeIsFile(type: ParticipantFieldType): boolean {
  return type === 'PARTICIPANT_FIELD_TYPE_FILE'
}

/* ═════════════════════════════════════════════════════════════════════════
   2. БАЗОВЫЕ СООБЩЕНИЯ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * `admin.v1.User`. Хеш пароля наружу не отдаётся никогда — его нет в сообщении.
 *
 * • `fullName` — `""`, когда не заполнено. Это скаляр, `null` в нём не бывает.
 * • `lastLoginAt` — `Timestamp`, то есть message-поле ⇒ **`null`** у того, кто
 *   ещё ни разу не входил. Единственное nullable-поле пользователя, и то самое
 *   место, где `new Date(u.lastLoginAt)` молча даёт 1970 год — форматтеры в
 *   `lib/format.ts` возвращают на `null` прочерк.
 * • `role` — **системная**. Ролей в конкретных мероприятиях здесь нет, их
 *   отдаёт `ListEventMembers`.
 */
export interface User {
  /** UUIDv4. */
  id: string
  /** Уникальный, хранится в нижнем регистре. */
  email: string
  /** Уникальный, `^[a-zA-Z0-9._-]{3,64}$`. */
  username: string
  /** Отображаемое имя; может быть пустой строкой. */
  fullName: string
  status: UserStatus
  createdAt: string
  updatedAt: string
  /** `null`, если пользователь ни разу не входил. */
  lastLoginAt: string | null
  role: SystemRole
}

/**
 * `admin.v1.Session` — одна строка = одно устройство.
 *
 * Хеш refresh-токена ротируется в этой же строке, поэтому список сессий — это
 * список устройств, а не поколений токенов.
 *
 * • `deviceLabel` — уже разобранный на сервере User-Agent («Chrome на macOS»).
 *   Приоритетнее клиентского `parseUserAgent()`; тот — фолбэк на пустую строку.
 * • `expiresAt` — **абсолютный** срок от момента логина, ротацией не двигается.
 * • `isCurrent` сервер считает сам, сравнивая с claim `sid`. В админском списке
 *   чужих сессий он всегда `false`.
 */
export interface Session {
  /** UUIDv4. Совпадает с claim `sid` в access-токене этой сессии. */
  id: string
  ip: string
  userAgent: string
  deviceLabel: string
  createdAt: string
  lastUsedAt: string
  expiresAt: string
  rememberMe: boolean
  isCurrent: boolean
}

/**
 * `admin.v1.PageInfo`. Все четыре поля — `uint32`, приезжают числами.
 * `page` — единичная база (первая страница = `1`).
 */
export interface PageInfo {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

/** `admin.v1.TokenPair`. */
export interface TokenPair {
  /** JWT HS256. Кладётся в `Authorization: Bearer`. */
  accessToken: string
  /** `base64url(32 случайных байта)`. В БД лежит только его sha256-хеш. */
  refreshToken: string
  /** TTL **access**-токена в секундах (по конфигу — 900). */
  expiresIn: number
  /** Абсолютный срок refresh-сессии. При ротации не меняется. */
  refreshExpiresAt: string
  /** Всегда `"Bearer"`. */
  tokenType: string
}

/**
 * Ответ на RPC с пустым сообщением: по проводу — буквально `{}`.
 * В роли запроса напоминает, что тело всё равно нужно отправить (см. мину
 * с `Content-Type` в шапке файла).
 */
export type EmptyMessage = Record<string, never>

/* ═════════════════════════════════════════════════════════════════════════
   3. AUTH — `AuthService`, префикс `/api/v1/auth`
   ═════════════════════════════════════════════════════════════════════════

   Про nullability в `*Response` этого раздела: `tokens`, `user`, `session` —
   message-поля, и формально protojson мог бы отдать в них `null`. Здесь они
   типизированы **не**-nullable сознательно: сервер заполняет их на каждом
   успешном ответе, и `null` там означал бы баг бэкенда, а не штатное
   состояние. Из полей верхнего уровня штатно бывает `null` только `pageInfo`
   в списках, и оно так и типизировано. */

/**
 * `POST /api/v1/auth/login` — публичный.
 *
 * `login` принимает **email или username** одним полем; сервер сам разбирает.
 * `rememberMe` определяет и срок refresh-токена на сервере (12 ч / 30 дней),
 * и хранилище на клиенте (`sessionStorage` / `localStorage`).
 *
 * ⚠️ Нижняя граница пароля здесь — 1 символ, а не 8, и это не небрежность:
 * короткий ввод обязан получать `INVALID_CREDENTIALS`, а не
 * `VALIDATION_FAILED`, иначе форма входа превращается в оракул политики
 * паролей. Клиентская схема формы обязана вести себя так же.
 */
export interface LoginRequest {
  /** Длина 3..255. */
  login: string
  /** Длина 1..128 — см. комментарий выше. */
  password: string
  /** `true` — refresh живёт 30 дней, `false` — 12 часов. */
  rememberMe: boolean
}

export interface LoginResponse {
  tokens: TokenPair
  user: User
  /** Своя же сессия — чтобы фронт сразу знал свой `sessionId`. */
  session: Session
}

/**
 * `POST /api/v1/auth/refresh` — публичный.
 *
 * ⚠️ Вызов обязан быть single-flight: сервер ротирует refresh на каждое
 * использование, два параллельных вызова гонятся, и второй предъявит уже
 * погашенный токен. Серверное окно 30 с — страховка от гонки вкладок, а не
 * разрешение фанаутить.
 */
export interface RefreshRequest {
  /** Длина 1..512. */
  refreshToken: string
}

export interface RefreshResponse {
  tokens: TokenPair
  /** Перечитанный из БД пользователь: его могли отключить или разжаловать между refresh'ами. */
  user: User
}

/**
 * `POST /api/v1/auth/logout` — публичный и идемпотентный: неизвестный,
 * просроченный или уже погашенный токен тоже даёт 200.
 */
export interface LogoutRequest {
  /** Максимум 512. Нижней границы нет: операция идемпотентна. */
  refreshToken: string
}

export type LogoutResponse = EmptyMessage

/**
 * `GET /api/v1/auth/me`.
 *
 * ⚠️ **Списка `permissions` здесь нет** — в отличие от scan-backend. Права в
 * этой системе не плоские: системная роль решает одно (`users.*`), а роль в
 * мероприятии — другое, и она у одного человека разная в разных мероприятиях.
 * Поле `2` в `GetMeResponse` зарезервировано под будущий RBAC; до тех пор
 * аффордансы UI считаются из `user.role` (система) и из `EventMember.role`
 * текущего мероприятия — см. `composables/useAuth.ts` и `stores/event.ts`.
 */
export interface GetMeResponse {
  user: User
}

/**
 * `POST /api/v1/auth/me/password` — смена **своего** пароля.
 * Защищён тем же rate-limit'ом, что и логин: проверка текущего пароля — оракул.
 *
 * ⚠️ Нижняя граница `currentPassword` — 1: это **проверка**, а не установка,
 * и неверная длина обязана давать `WRONG_CURRENT_PASSWORD`, а не
 * `VALIDATION_FAILED`.
 */
export interface ChangeMyPasswordRequest {
  /** Длина 1..128. */
  currentPassword: string
  /** Длина 8..128 на транспорте; композиция проверяется в biz и даёт `WEAK_PASSWORD`. */
  newPassword: string
}

export interface ChangeMyPasswordResponse {
  /** Новая пара: текущая сессия ротируется, поэтому оба токена надо заменить немедленно. */
  tokens: TokenPair
  /** Сколько чужих устройств выкинуло этой сменой (текущее не считается). */
  revokedSessions: number
}

/* ═════════════════════════════════════════════════════════════════════════
   4. СЕССИИ — свои (`/auth/sessions`) и чужие (`/users/{id}/sessions`)
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Ответ обоих списков сессий:
 *   `GET /api/v1/auth/sessions`        — свои, любая роль;
 *   `GET /api/v1/users/{id}/sessions`  — чужие, только admin.
 *
 * Пагинации нет намеренно: сессий единицы. `total` равен длине массива и
 * отдаётся отдельно ради счётчика в UI.
 */
export interface ListSessionsResponse {
  sessions: Session[]
  total: number
}

/** @see ListSessionsResponse — `GET /api/v1/auth/sessions` */
export type ListMySessionsResponse = ListSessionsResponse

/** @see ListSessionsResponse — `GET /api/v1/users/{id}/sessions` */
export type ListUserSessionsResponse = ListSessionsResponse

/**
 * `DELETE /api/v1/auth/sessions/{sessionId}`.
 *
 * Запрос скоупится сервером по владельцу, поэтому чужой `sessionId` даёт
 * `SESSION_NOT_FOUND`, а не `FORBIDDEN`: эндпоинт не подтверждает существование
 * чужих строк.
 */
export type RevokeSessionResponse = EmptyMessage

/** `POST /api/v1/auth/sessions/revoke-others` — «выйти на всех остальных устройствах». */
export type RevokeOtherSessionsRequest = EmptyMessage

export interface RevokeOtherSessionsResponse {
  /** Сколько сессий погашено (текущая не в счёт). */
  revoked: number
}

/** `DELETE /api/v1/users/{id}/sessions` — admin гасит все сессии пользователя. */
export interface RevokeUserSessionsResponse {
  revoked: number
}

/* ═════════════════════════════════════════════════════════════════════════
   5. ПОЛЬЗОВАТЕЛИ — `UserService`, префикс `/api/v1/users`
   ═════════════════════════════════════════════════════════════════════════

   ⚠️ ВЕСЬ СЕРВИС ДОСТУПЕН ТОЛЬКО admin'у, каждый RPC без исключения — включая
   чтение. Это не осторожность: `CreateUser` и `SetUserRole` назначают системную
   роль, так что любой не-admin, добравшийся сюда, выдаёт admin'а себе. */

/**
 * Допустимые значения `orderBy` в `GET /api/v1/users` — список закрыт на
 * сервере. Ведущий `-` — убывание, `""` — серверный дефолт (`-created_at`).
 */
export const USER_ORDER_BY = [
  '',
  'created_at',
  '-created_at',
  'email',
  '-email',
  'username',
  '-username',
] as const
export type UserOrderBy = typeof USER_ORDER_BY[number]

/**
 * `GET /api/v1/users`. Едет **query-строкой**, не телом. Все поля опциональны:
 * не отправили — сервер взял дефолт.
 *
 * `status` и `role` симметричны: **невыставленный фильтр не фильтрует**. Без
 * `status` приходят и активные, и отключённые — в UI это три состояния
 * «Все» / «Активные» / «Отключённые», и `null` в сторе означает именно «все».
 * `*_UNSPECIFIED` эквивалентен отсутствию ключа, но отсутствие честнее.
 */
export interface ListUsersRequest {
  /** 1-based. `0` — «страница по умолчанию». */
  page?: number
  /** `0` — умолчание (20). Максимум — 100. */
  pageSize?: number
  /** Подстрока по email, username и fullName. Максимум 255. */
  search?: string
  status?: UserStatus
  orderBy?: UserOrderBy
  role?: SystemRole
}

/**
 * ⚠️ `pageInfo` — честный `PageInfo | null`: это message-поле, и «не
 * выставлено» protojson кодирует ключом со значением `null` (не `{}` и не
 * отсутствием ключа). Читать только через `?.`:
 *
 *     const total = resp.pageInfo?.totalItems ?? 0
 *
 * `users` при этом — всегда массив, `[]` в худшем случае.
 */
export interface ListUsersResponse {
  users: User[]
  pageInfo: PageInfo | null
}

/**
 * `POST /api/v1/users`.
 *
 * Новый пользователь **всегда активен**: чтобы завести отключённого, нужен
 * последующий `PATCH`. `role` со значением `SYSTEM_ROLE_UNSPECIFIED` означает
 * `SYSTEM_ROLE_USER` — чтобы завести админа, роль надо назвать вслух.
 */
export interface CreateUserRequest {
  /** Email, максимум 255. */
  email: string
  /** `^[a-zA-Z0-9._-]{3,64}$`. */
  username: string
  /** Максимум 128; пустая строка — легальное «не заполнено». */
  fullName: string
  /** Длина 8..128 на транспорте; композиция проверяется в biz. */
  password: string
  role: SystemRole
}

export interface CreateUserResponse {
  user: User
}

export interface GetUserResponse {
  user: User
}

/**
 * `PATCH /api/v1/users/{id}` — **частичное обновление через proto3 `optional`**,
 * не `FieldMask`. Отсюда три правила, и они не интуитивны:
 *
 * 1. **Ключ отсутствует → поле не меняется.** Именно поэтому все поля здесь
 *    `T?`, а не `T | null` — в отличие от всего остального в этом файле. Тело
 *    собирается только из тронутых полей (`dirty` во vee-validate), а не из
 *    всего объекта формы.
 * 2. **`null` НЕ очищает поле.** `{"fullName": null}` protojson декодирует как
 *    *unset*, то есть ровно как отсутствие ключа. Обнулить что-либо `null`'ом
 *    по проводу невозможно.
 * 3. **Чтобы записать пустое значение, шлите `""`.**
 *
 * Роль здесь не меняется — для неё отдельный `PUT /users/{id}/role`, у которого
 * свой побочный эффект. Перевод в `USER_STATUS_DISABLED` гасит все сессии.
 */
export interface UpdateUserRequest {
  email?: string
  username?: string
  fullName?: string
  status?: UserStatus
}

export interface UpdateUserResponse {
  user: User
}

/**
 * `PUT /api/v1/users/{id}/role`.
 *
 * Отдельный RPC, а не поле в `PATCH`, потому что у него свой побочный эффект:
 * **все сессии цели гасятся немедленно**. Гашение обязательно, а не желательно —
 * системная роль едет в claim access-токена, поэтому разжалованный админ иначе
 * оставался бы админом до истечения токена.
 */
export interface SetUserRoleRequest {
  /** `SYSTEM_ROLE_UNSPECIFIED` запрещён. */
  role: SystemRole
}

export interface SetUserRoleResponse {
  user: User
  /** Сессии гасятся ВСЕГДА, даже если роль фактически не изменилась. */
  revokedSessions: number
}

/**
 * `POST /api/v1/users/{id}/password` — административный сброс чужого пароля.
 * Текущий пароль не спрашивается (в этом и разница с `ChangeMyPassword`);
 * все сессии цели гасятся.
 */
export interface ResetUserPasswordRequest {
  /** Длина 8..128. */
  newPassword: string
}

export interface ResetUserPasswordResponse {
  revokedSessions: number
}

/**
 * `DELETE /api/v1/users/{id}` — удаление **мягкое**: статус переводится в
 * `USER_STATUS_DISABLED`, сессии гасятся, строка в БД остаётся. В ответе
 * приезжает обновлённый пользователь — его кладут прямо в стор, а не
 * вычёркивают строку из списка.
 *
 * Инварианты `CANNOT_MODIFY_SELF` и `LAST_ADMIN` проверяются здесь же (409).
 */
export interface DeleteUserResponse {
  user: User
  revokedSessions: number
}

/* ═════════════════════════════════════════════════════════════════════════
   6. МЕРОПРИЯТИЯ — `EventService`, префикс `/api/v1/events`
   ═════════════════════════════════════════════════════════════════════════

   ⚠️ МЕРОПРИЯТИЕ — ЭТО СКОУП. Всё, что ниже (справочники, поля, участники,
   импорт), принадлежит одному мероприятию, и права считаются по нему:

     admin        проходит всё без ограничений;
     owner        полный доступ к СВОИМ мероприятиям, включая состав;
     viewer       только чтение своих мероприятий;
     не участник  мероприятия для него не существует.

   ⚠️ «НЕ СУЩЕСТВУЕТ» БУКВАЛЬНО: чужое мероприятие отдаёт **404
   EVENT_NOT_FOUND**, а не 403. Разница принципиальна — 403 подтверждает, что
   такой id есть, и превращает перебор id в перепись мероприятий системы.
   `FORBIDDEN` (403) отдаётся только там, где участник УЖЕ знает о
   существовании мероприятия и ему не хватает роли: viewer, пытающийся править.

   Практическое следствие для UI: `EVENT_NOT_FOUND` нельзя показывать как
   «доступ запрещён» — для человека, которого просто не добавили в состав, это
   и правда «нет такого мероприятия». */

/** `admin.v1.Event`. */
export interface Event {
  /** UUIDv4. */
  id: string
  /**
   * Название. Непустое, максимум 255 символов. **Не уникально**: два турнира с
   * одинаковым названием в разные годы — норма, а не ошибка ввода.
   */
  name: string
  /**
   * Начало проведения. `Timestamp`, а не `Date`, хотя поле называется «дата»:
   * мероприятие начинается в конкретный час.
   */
  startsAt: string
  /** Конец проведения. Не раньше `startsAt`. */
  endsAt: string
  status: EventStatus
  createdAt: string
  updatedAt: string
}

/**
 * Допустимые значения `orderBy` в `GET /api/v1/events`.
 *
 * ⚠️ Дефолт (`""`) — **`-starts_at`**, а не `-created_at` как у пользователей:
 * список мероприятий читают, чтобы понять «что и когда проходит», а не «что мы
 * недавно завели».
 */
export const EVENT_ORDER_BY = [
  '',
  'starts_at',
  '-starts_at',
  'created_at',
  '-created_at',
  'name',
  '-name',
] as const
export type EventOrderBy = typeof EVENT_ORDER_BY[number]

/**
 * `GET /api/v1/events`. Едет query-строкой.
 *
 * admin видит все мероприятия; остальные — только те, в которых состоят.
 * Фильтр применяется **в запросе**, а не отсеиванием после выборки, поэтому
 * `pageInfo.totalItems` считается уже по доступным мероприятиям и страницы
 * не оказываются полупустыми.
 */
export interface ListEventsRequest {
  page?: number
  pageSize?: number
  /** Подстрока по названию. Максимум 255. */
  search?: string
  /** Невыставленный фильтр не фильтрует — приходят в том числе отменённые. */
  status?: EventStatus
  orderBy?: EventOrderBy
  /**
   * Оставить только мероприятия, начинающиеся С указанного момента.
   *
   * ⚠️ Граница **включается**: `startsAt >= startsFrom`. Поэтому поле
   * называется `startsFrom`, а не `startsAfter`. Тип — момент (RFC3339), а не
   * дата: календарный день на клиенте превращается в полночь **в его зоне**,
   * сервер не додумывает, где проходит граница суток.
   */
  startsFrom?: string
}

export interface ListEventsResponse {
  events: Event[]
  /** ⚠️ `PageInfo | null` — см. `ListUsersResponse`. */
  pageInfo: PageInfo | null
}

export interface GetEventResponse {
  event: Event
}

/**
 * `POST /api/v1/events` — **только admin**.
 *
 * ⚠️ Мероприятие создаётся **без владельцев**: admin назначает их следующим
 * шагом через `AddEventMember`. Значит, между созданием и назначением
 * мероприятие какое-то время не видно никому, кроме админов, — это ожидаемое
 * состояние, а не переходный дефект, и форма создания обязана вести человека
 * дальше, к составу.
 */
export interface CreateEventRequest {
  /** Непустое, максимум 255. */
  name: string
  startsAt: string
  /** Не раньше `startsAt`. */
  endsAt: string
  /** `EVENT_STATUS_UNSPECIFIED` означает `EVENT_STATUS_PLANNED`. */
  status: EventStatus
}

export interface CreateEventResponse {
  event: Event
}

/**
 * `PATCH /api/v1/events/{id}` — admin или owner. Частичное обновление через
 * proto3 `optional`: правила те же, что у `UpdateUserRequest`.
 *
 * ⚠️ Проверка «конец не раньше начала» выполняется по **результату** применения
 * патча, а не по присланным полям: сдвинуть только одну из двух дат — самый
 * обычный способ получить мероприятие, которое кончается до начала. Форма
 * поэтому обязана валидировать пару дат целиком, а не только тронутое поле.
 *
 * `EVENT_STATUS_UNSPECIFIED` здесь запрещён: на пути записи он ничего не
 * значит, и пропустить его означало бы молча не сделать ничего.
 */
export interface UpdateEventRequest {
  name?: string
  startsAt?: string
  endsAt?: string
  status?: EventStatus
}

export interface UpdateEventResponse {
  event: Event
}

/**
 * `DELETE /api/v1/events/{id}` — admin или owner. Удаление **жёсткое**: строка
 * исчезает, и вместе с ней каскадом уходят справочники, поля и участники.
 *
 * Мягкого удаления нет намеренно: для «мероприятие не состоится» есть статус
 * `EVENT_STATUS_CANCELLED`, и вторая форма того же самого только порождала бы
 * вопрос, чем они отличаются. В UI это значит, что подтверждение обязано
 * называть последствия, а не спрашивать «вы уверены?».
 */
export interface DeleteEventResponse {
  /** Удалённое мероприятие — последнее, что о нём известно. */
  event: Event
}

/**
 * `POST /api/v1/events/{id}/copy` — **только admin**.
 *
 * Копия мероприятия целиком, КРОМЕ участников: переносятся состав, зоны
 * доступа, роли участников вместе с выданными им зонами, поля конструктора
 * (тип, привязка к роли, подсветка, варианты, обязательность и порядок) и макет
 * бейджа вместе с загруженными подложками и логотипами.
 *
 * ⚠️ **Участники (`participants`) не копируются**, и вместе с ними — значения
 * полей, файлы и фотографии. Копия — заготовка следующего соревнования, а не
 * его стартовый список. Об этом обязана сказать форма: «скопировать» звучит
 * как «скопировать всё».
 *
 * Статус копии — всегда `EVENT_STATUS_PLANNED`, независимо от статуса
 * исходника: мероприятие без единого участника не может быть «завершено».
 *
 * У всего скопированного НОВЫЕ id, и внутренние ссылки переадресованы сервером.
 * Для фронта это значит, что после копирования никакие закешированные id
 * исходника к копии не относятся — экраны копии грузятся с нуля.
 */
export interface CopyEventRequest {
  /**
   * Название копии. Обязательно, максимум 255 символов.
   *
   * Без значения по умолчанию вроде «… (копия)»: копия почти всегда делается
   * «на следующий год», то есть у неё своё название, а подставленное
   * автоматически пришлось бы переименовывать сразу же.
   */
  name: string
  /**
   * Даты копии. Не заданы — берутся у исходника.
   *
   * ⚠️ Проверка «конец не раньше начала» выполняется по **результату**
   * подстановки: прислать одну дату законно, и сравнивается она со второй,
   * унаследованной от исходника. Форма поэтому обязана показывать обе даты, а
   * не только ту, которую трогают.
   */
  startsAt?: string
  endsAt?: string
}

export interface CopyEventResponse {
  /** Созданная копия. Статус — `EVENT_STATUS_PLANNED`. */
  event: Event
  summary: CopyEventSummary
}

/**
 * Что именно перенеслось. Отдаётся, чтобы подтверждение могло сказать это
 * словами — иначе единственный способ убедиться, что перенеслось всё, открыть
 * по очереди шесть экранов копии.
 */
export interface CopyEventSummary {
  accessZones: number
  /**
   * Записи справочника уровней доступа.
   *
   * Отдельным числом от `accessZones`, а не в сумме: это два разных
   * справочника, и «скопировано 12 записей» не отвечает на вопрос, ради
   * которого подтверждение читают, — «уровни-то доехали?».
   */
  accessLevels: number
  participantRoles: number
  participantFields: number
  /** Строки состава: те же пользователи с теми же ролями доступа. */
  members: number
  /** Был ли у исходника макет бейджа. `false` — макета не было; это не ошибка. */
  badgeTemplate: boolean
  /**
   * Сколько картинок макета переложено под новый id. Внешние `https://`-ссылки
   * сюда не входят — они никуда не перекладывались.
   */
  badgeAssets: number
}

/* ─────────────────────────── СОСТАВ ─────────────────────────────────── */

/** `admin.v1.EventMember` — пара (мероприятие, пользователь) с ролью. */
export interface EventMember {
  eventId: string
  userId: string
  role: MemberRole
  /** Момент добавления в мероприятие. */
  createdAt: string
  /** Момент последней смены роли. */
  updatedAt: string
  /**
   * Сам пользователь, чтобы список состава не требовал второго запроса за
   * именами. Формально message-поле ⇒ может быть `null`; сервер его заполняет,
   * но `?.` в шаблоне дешевле, чем белый экран.
   */
  user: User | null
}

/**
 * `GET /api/v1/events/{eventId}/members` — виден **любому** участнику, включая
 * viewer'а: знать, к кому идти за доступом, — это чтение, а не полномочие.
 *
 * Пагинации нет намеренно: состав — это единицы человек, а не лента.
 */
export interface ListEventMembersResponse {
  members: EventMember[]
  total: number
}

/**
 * `POST /api/v1/events/{eventId}/members` — admin или owner.
 *
 * Именно здесь owner «добавляет других owner'ов»: роль назначаемого
 * указывается явно и может быть любой, включая `MEMBER_ROLE_OWNER`.
 *
 * ⚠️ Повторное добавление — это **409**, а не молчаливая смена роли:
 * «добавить» и «переназначить» разные намерения, и склеивать их значит
 * превращать опечатку в понижение чужих прав. Для смены есть `SetEventMemberRole`.
 */
export interface AddEventMemberRequest {
  userId: string
  /** `MEMBER_ROLE_UNSPECIFIED` запрещён. */
  role: MemberRole
}

export interface AddEventMemberResponse {
  member: EventMember
}

/**
 * `PUT /api/v1/events/{eventId}/members/{userId}` — admin или owner.
 *
 * ⚠️ `409 CANNOT_MODIFY_SELF` — owner не может понизить или исключить сам себя:
 * он немедленно потеряет доступ, которым только что распоряжался. admin такого
 * ограничения не имеет — он в мероприятии не состоит.
 */
export interface SetEventMemberRoleRequest {
  /** `MEMBER_ROLE_UNSPECIFIED` запрещён. */
  role: MemberRole
}

export interface SetEventMemberRoleResponse {
  member: EventMember
}

/**
 * `POST /api/v1/events/{eventId}/members/create-user` — завести НОВУЮ учётную
 * запись и сразу включить её в состав. admin или owner мероприятия.
 *
 * Существует потому, что `AddEventMember` требует `userId`, а узнать его
 * владелец не может: весь `UserService` доступен только администратору,
 * включая чтение.
 *
 * ⚠️ **ПОЛЯ СИСТЕМНОЙ РОЛИ ЗДЕСЬ НЕТ, И ЭТО НЕ УПУЩЕНИЕ КОНТРАКТА.** Заводимая
 * запись всегда `SYSTEM_ROLE_USER` — сама по себе она не даёт ничего, все
 * полномочия приходят из членства в мероприятиях, а единственное членство,
 * которое выдаёт этот вызов, — в мероприятии, которым вызывающий и так
 * распоряжается. Появись здесь выбор системной роли, владелец одного
 * соревнования заводил бы себе администратора системы и знал бы его пароль.
 *
 * `role` ниже — **`MemberRole`**, роль в этом мероприятии. Не перепутайте: два
 * типа разведены именно для того, чтобы подстановка одного вместо другого не
 * компилировалась.
 *
 * ⚠️ Пароль назначает заводящий, то есть он знает учётные данные созданного
 * человека. Пока запись остаётся обычным пользователем, это ничего не добавляет
 * к его правам; опасным это станет, если администратор потом повысит такую
 * запись. Смены пароля при первом входе в системе нет — интерфейс обязан
 * сказать об этом вслух, а не прятать.
 *
 * Операция атомарна: учётная запись и строка состава пишутся одной
 * транзакцией, поэтому «аккаунт создался, а в состав не попал» невозможно.
 */
export interface CreateEventMemberUserRequest {
  /** Email, максимум 255. `409 EMAIL_ALREADY_EXISTS`, если адрес занят. */
  email: string
  /** `^[a-zA-Z0-9._-]{3,64}$`. `409 USERNAME_ALREADY_EXISTS`. */
  username: string
  /** Может быть пустым, максимум 128. */
  fullName: string
  /** Длина 8..128 на транспорте; композиция проверяется политикой и даёт `WEAK_PASSWORD`. */
  password: string
  /** Роль **в мероприятии**. `MEMBER_ROLE_UNSPECIFIED` запрещён. */
  role: MemberRole
}

export interface CreateEventMemberUserResponse {
  /** Созданный участник состава вместе с заведённой учётной записью. */
  member: EventMember
}

/**
 * `POST /api/v1/events/{eventId}/members/{userId}/device-ticket` — выпустить
 * ОДНОРАЗОВЫЙ код входа для устройства оператора. admin или owner.
 *
 * Владелец показывает QR на своём экране, телефон оператора его считывает и
 * оказывается авторизован. Оператору не нужно ни имени, ни пароля, ни
 * клавиатуры — на входе в зал в этом весь смысл.
 *
 * ⚠️ ЭТО ПРЕДЪЯВИТЕЛЬСКИЙ CREDENTIAL, ПОКАЗАННЫЙ В КОМНАТЕ С ЛЮДЬМИ. Кто увидел
 * экран — тот может войти. Отсюда всё остальное:
 *
 *   • код живёт 3 минуты (сервер, не настройка) — фотография экрана
 *     обесценивается раньше, чем снявший выйдет из зала;
 *   • код одноразовый: первое погашение выигрывает;
 *   • код НИКОГДА не выпускается на администратора системы и на отключённую
 *     учётную запись — сервер откажет `VALIDATION_FAILED`;
 *   • только на того, кто уже в составе.
 *
 * ⚠️ Выданная сессия — **обычная** сессия этого пользователя, она не ограничена
 * мероприятием: скоупа у сессий в системе нет. Значит владелец может получить
 * рабочую сессию любого не-администратора из своего состава, включая его доступ
 * к другим мероприятиям. Это осознанный остаточный риск, он ограничен и
 * записан в аудит — подробнее в `acr-backend/internal/biz/devicelogin.go`.
 */
export interface IssueDeviceLoginTicketResponse {
  /**
   * Открытый код, `base64url(32 байта)`, 43 символа.
   *
   * ⚠️ Приходит **ровно один раз** и нигде не хранится: на сервере лежит только
   * его sha256. Показать повторно невозможно — потеряли, выпускайте новый.
   * Не кладите его ни в стор, ни в URL, ни в localStorage.
   */
  ticket: string
  /** Момент, после которого код недействителен. */
  expiresAt: string
  /**
   * Сколько секунд коду осталось жить на момент ответа.
   *
   * ⚠️ Обратный отсчёт считаем от ЭТОГО значения, а не от разницы с
   * `expiresAt`: часы телефона могут быть сдвинуты, и тогда свежий код
   * показывался бы истёкшим (или наоборот — живым после смерти).
   */
  expiresIn: number
}

/**
 * `POST /api/v1/auth/device-ticket` — **публичный**: у телефона оператора ещё
 * нечем аутентифицироваться, получение сессии и есть цель вызова.
 *
 * ⚠️ ЛЮБАЯ НЕУДАЧА ОТВЕЧАЕТ ОДИНАКОВО — `401 DEVICE_TICKET_INVALID`: кода нет,
 * код погашен, код просрочен, строка вообще не код, учётку успели отключить.
 * Различать их наружу означало бы подсказывать перебирающему, какая из его
 * догадок была настоящей. Интерфейс обязан не выдумывать разницу, которой ему
 * не дали, — «покажите код заново» подходит ко всем случаям сразу.
 *
 * `429 RATE_LIMITED` — бюджет считается по адресу, а не по коду.
 */
export interface RedeemDeviceLoginTicketRequest {
  /** Ровно то, что прочитал сканер. */
  ticket: string
}

/** Ответ той же формы, что и у логина. Сессия выдаётся без «запомнить меня». */
export interface RedeemDeviceLoginTicketResponse {
  tokens: TokenPair
  user: User
  session: Session
}

/** `DELETE /api/v1/events/{eventId}/members/{userId}`. @see SetEventMemberRoleRequest */
export type RemoveEventMemberResponse = EmptyMessage

/* ═════════════════════════════════════════════════════════════════════════
   7. СПРАВОЧНИК РОЛЕЙ УЧАСТНИКОВ — `ParticipantRoleService`
   ═════════════════════════════════════════════════════════════════════════

   ⚠️ ЭТО НЕ ПРАВА ДОСТУПА. Роль участника — то, кем человек приехал на
   соревнование («спортсмен», «тренер», «представитель команды»); она ничего не
   разрешает и ничего не запрещает. Права описывает `MemberRole`, и эти два
   понятия не пересекаются нигде: ни в проверках, ни в таблицах, ни в токене.

   Справочник **локален** для мероприятия: общего списка на систему нет и не
   подразумевается. */

/** `admin.v1.ParticipantRole` — запись справочника. Кроме названия у неё ничего нет. */
export interface ParticipantRole {
  id: string
  eventId: string
  /**
   * Непустое, максимум 128, уникально **в пределах мероприятия** без учёта
   * регистра: два «Спортсмена» в одном выпадающем списке — опечатка, которую
   * некому будет объяснить.
   */
  name: string
  /**
   * Цвет роли — `#rrggbb` строчными **либо пустая строка**.
   *
   * ⚠️ **Пустая строка — это значение, а не отсутствие поля.** Она означает
   * «цвет не задан»: роль без цвета печатается ровно так, как настроено в
   * макете, а роль с цветом — этим цветом. Куда именно он ляжет, в строку или
   * в заливку подложки, решает `BadgeRoleProps.colorMode`; САМ цвет задаётся
   * только здесь, и перебить его макет по-прежнему не может.
   *
   * ⚠️ **Это свойство печати, а не смысл.** Из него ничего не выводится и по
   * нему ничего не сортируется: цвет существует ради бейджа, который должен
   * сказать «тренер» с трёх метров, где само слово уже не читается. Не
   * уникален — две роли могут делить цвет ровно так же, как две зоны.
   */
  color: string
  /**
   * Зоны доступа роли — **только id**, отсортированные по триграмме зоны (тот
   * же порядок, что и в `ListAccessZones`).
   *
   * ⚠️ **И это по-прежнему не права доступа.** Список говорит, какие плашки
   * попадут на бейдж человека с этой ролью, и ничего не говорит о том, что он
   * может в приложении.
   *
   * Не вложенные `AccessZone`: зона — запись отдельного справочника, который
   * стор и так держит целиком. Вложенная копия разошлась бы с оригиналом ровно
   * в момент переименования зоны, и экран показал бы старое имя в одном месте
   * и новое в другом. Разворачивать id в объекты — `accessZones.byId`.
   *
   * Пустой массив — штатное состояние («никуда специально не нужно»), а не
   * «ещё не загрузилось».
   */
  accessZoneIds: string[]
  /**
   * Уровни доступа роли — **только id**, отсортированные по возрастанию номера
   * уровня (тот же порядок, что и в `ListAccessLevels`).
   *
   * ⚠️ **Это ВТОРОЙ, НЕЗАВИСИМЫЙ набор, а не замена `accessZoneIds`.** Зона
   * отвечает на вопрос «куда пускают» (раздевалки, микст-зона), уровень — «
   * насколько глубоко», одной цифрой на бейдже. У роли бывают зоны без
   * уровней, уровни без зон, оба набора сразу и ни одного. Код, который
   * трактует один набор как «новую версию» другого, потеряет половину бейджа.
   *
   * ⚠️ **И это по-прежнему не права доступа.** Ни зоны, ни уровни не решают,
   * что человек может в приложении, — это делают `auth.isAdmin` и
   * `event.canManage`.
   *
   * Не вложенные `AccessLevel` по той же причине, что и у зон: справочник и так
   * целиком в сторе, а вложенная копия разошлась бы с оригиналом в момент
   * правки номера. Разворачивать id в объекты — `accessLevels.byId`.
   *
   * Пустой массив — штатное состояние, а не «ещё не загрузилось».
   */
  accessLevelIds: string[]
  /**
   * Пиктограммы роли — **только id**, в порядке сортировки справочника (тот же
   * порядок, что и в `ListPictograms`).
   *
   * ⚠️ **Третий независимый набор.** Зона — «куда», уровень — «насколько
   * глубоко», пиктограмма — «что положено» (обед, автобус, парковка). Любой из
   * трёх может быть пуст, и это ничего не говорит про два других.
   *
   * ⚠️ **И это по-прежнему не права доступа.**
   *
   * Не вложенные `Pictogram` по той же причине, что и у зон: справочник и так
   * целиком в сторе, а вложенная копия разошлась бы с оригиналом в момент
   * переименования или замены иконки. Разворачивать id в объекты —
   * `pictograms.byId`.
   *
   * Пустой массив — штатное состояние, а не «ещё не загрузилось».
   */
  pictogramIds: string[]
  createdAt: string
  updatedAt: string
}

/**
 * `GET /api/v1/events/{eventId}/participant-roles` — весь справочник, по
 * алфавиту. Читает любой участник мероприятия.
 *
 * Пагинации нет намеренно: постраничный справочник заставил бы клиента листать,
 * чтобы собрать выпадающий список целиком.
 */
export interface ListParticipantRolesResponse {
  participantRoles: ParticipantRole[]
  total: number
}

export interface CreateParticipantRoleRequest {
  /** Непустое, максимум 128. */
  name: string
  /**
   * `#rrggbb` либо пустая строка. Регистр не важен — сервер опускает его сам.
   *
   * Пустая строка означает «цвет не задан» и хранится как есть: на бейдже такая
   * роль печатается цветом из макета.
   */
  color: string
}

export interface CreateParticipantRoleResponse {
  participantRole: ParticipantRole
}

/**
 * `PUT /api/v1/events/{eventId}/participant-roles/{id}` — название и цвет.
 *
 * ⚠️ Зоны сюда НЕ входят, и это не забытое поле. Набор зон правят на другом
 * экране, галочками; если бы он ехал этим же PUT, то
 * есть затирала бы чужую одновременную правку набора. Зоны меняет
 * `SetParticipantRoleAccessZonesRequest`.
 *
 * ⚠️ Уровни — тем более: у них свой эндпоинт и свой тип запроса,
 * `SetParticipantRoleAccessLevelsRequest` (секция 7b), по той же причине.
 */
export interface UpdateParticipantRoleRequest {
  name: string
  /**
   * `#rrggbb` либо пустая строка («цвет не задан»). Это PUT: пустая строка
   * означает «снять цвет», а не «не менять» — оба поля формы уезжают вместе.
   */
  color: string
}

/**
 * `PUT /api/v1/events/{eventId}/participant-roles/{id}/access-zones` — задать
 * набор зон роли ЦЕЛИКОМ.
 *
 * Замена набора, а не «добавить»/«убрать» по одной: на экране это список всех
 * зон площадки с галочками, и пара add/remove превратила бы щелчки по нему в
 * поток запросов, каждый со своим шансом упасть посередине. Здесь набор либо
 * записался целиком, либо не записался.
 *
 * Идемпотентно. Порядок игнорируется, повторы схлопываются — это множество.
 */
export interface SetParticipantRoleAccessZonesRequest {
  /**
   * Полный новый набор. **Пустой массив — законное значение** (снять все зоны),
   * а не «поле не заполнили»: снятие последней галочки обязано сохраняться так
   * же, как любое другое изменение.
   *
   * Каждый id обязан называть зону ЭТОГО мероприятия, иначе
   * `404 ACCESS_ZONE_NOT_FOUND`.
   */
  accessZoneIds: string[]
}

export interface SetParticipantRoleAccessZonesResponse {
  /** Роль с уже применённым набором. */
  participantRole: ParticipantRole
}

export interface UpdateParticipantRoleResponse {
  participantRole: ParticipantRole
}

export interface DeleteParticipantRoleResponse {
  /** Удалённая запись — последнее, что о ней известно. */
  participantRole: ParticipantRole
}

/* ═════════════════════════════════════════════════════════════════════════
   7a. СПРАВОЧНИК ЗОН ДОСТУПА — `AccessZoneService`
   ═════════════════════════════════════════════════════════════════════════

   ⚠️ ЭТО НЕ ПРАВА ДОСТУПА — ни к системе, ни к экранам. Зона доступа это
   участок ПЛОЩАДКИ: раздевалки, зона разминки, микст-зона, техническая зона.
   Права по-прежнему описывают `auth.isAdmin` и `event.canManage`, и ни одна
   проверка в приложении не имеет права смотреть сюда.

   Справочник локален для мероприятия: у каждого соревнования своё здание.

   Порядок работы у организатора: сначала заводятся ВСЕ зоны площадки, потом
   они привязываются к ролям участников. Привязки в этом контракте ещё нет —
   `AccessZoneService` владеет только самим списком. */

/** `admin.v1.AccessZone` — запись справочника. */
export interface AccessZone {
  id: string
  eventId: string
  /**
   * Название целиком: «Зона разминки», «Микст-зона». Непустое, максимум 128,
   * уникально **в пределах мероприятия** без учёта регистра.
   */
  name: string
  /**
   * Триграмма — то, что печатается на бейдже: `AIR`, `LASK`, `BIL`, `ZHZH`.
   *
   * 1–8 **заглавных латинских** букв, без цифр и разделителей. Сервер сам
   * приводит присланное к верхнему регистру, поэтому отправлять `air` можно, а
   * вот `АИР` кириллицей — нет: на бейдже кириллическая «А» неотличима от
   * латинской, и две разные зоны выглядели бы одинаково. Форма обязана
   * повторить это правило, иначе человек узнаёт о нём из 400.
   *
   * Уникальна в пределах мероприятия, и это не косметика: человек на входе
   * читает именно её.
   */
  abbreviation: string
  /**
   * Цвет плашки — `#rrggbb`, ровно 7 символов, **строчными**.
   *
   * Одна каноническая форма: без `#f80`, без альфа-канала, без имён CSS.
   * `<input type="color">` отдаёт ровно её, так что подгонять ничего не нужно.
   */
  color: string
  /**
   * Цвет ТЕКСТА на плашке — `#rrggbb` строчными, **либо пустая строка**.
   *
   * ⚠️ **Пустая строка — это «контраст», а не «не заполнили»**, и это значение
   * по умолчанию. Цвет тогда считается из заливки по яркости
   * (`contrastTextColor`) — тем же правилом, которое действовало всегда, — так
   * что запись без этого поля выглядит ровно как раньше.
   *
   * Непустое значение ПРИОРИТЕТНЕЕ контраста и берётся как есть: белый на
   * жёлтом теперь выразим, и выразим намеренно — бывает фирменный стиль, ради
   * которого это делают осознанно. Ответственность за читаемость переходит к
   * тому, кто выбрал цвет.
   *
   * ⚠️ **В монохромном режиме бейджа не применяется.** Там плашка печатается
   * контуром без заливки, а метка — цветом зоны; подставить сюда выбранный цвет
   * значило бы напечатать белым по белой бумаге ровно у того, кто выбрал белый
   * под тёмную заливку.
   */
  textColor: string
  createdAt: string
  updatedAt: string
}

/**
 * `GET /api/v1/events/{eventId}/access-zones` — весь справочник, **по
 * триграмме**. Читает любой участник мероприятия.
 *
 * Сортировка не по названию намеренно: сверяются с бейджем, на котором
 * написано `LASK`, а не «Зона разминки».
 */
export interface ListAccessZonesResponse {
  accessZones: AccessZone[]
  total: number
}

export interface CreateAccessZoneRequest {
  /** Непустое, максимум 128. */
  name: string
  /** 1–8 заглавных латинских букв. Регистр сервер поднимет сам. */
  abbreviation: string
  /** `#rrggbb`. Обязателен: у зоны без цвета нет плашки. */
  color: string
  /** `#rrggbb` либо пустая строка — «контраст». Пусто по умолчанию. */
  textColor: string
}

export interface CreateAccessZoneResponse {
  accessZone: AccessZone
}

/**
 * `PUT /api/v1/events/{eventId}/access-zones/{id}`.
 *
 * PUT, а не PATCH: изменяемых полей ровно три, все три всегда есть в форме, и
 * «частичное обновление» означало бы возможность прислать название без цвета —
 * состояние, которого у зоны не бывает.
 */
export interface UpdateAccessZoneRequest {
  name: string
  abbreviation: string
  color: string
  /**
   * `#rrggbb` либо пустая строка.
   *
   * ⚠️ Пустая строка здесь СБРАСЫВАЕТ цвет обратно на контраст, а не означает
   * «не менять»: это PUT, и все поля формы приезжают вместе.
   */
  textColor: string
}

export interface UpdateAccessZoneResponse {
  accessZone: AccessZone
}

export interface DeleteAccessZoneResponse {
  /** Удалённая запись — последнее, что о ней известно. */
  accessZone: AccessZone
}

/* ═════════════════════════════════════════════════════════════════════════
   7b. СПРАВОЧНИК УРОВНЕЙ ДОСТУПА — `AccessLevelService`
   ═════════════════════════════════════════════════════════════════════════

   ⚠️ ЭТО НЕ ПРАВА ДОСТУПА — как и зоны из 7a. Права по-прежнему описывают
   `auth.isAdmin` и `event.canManage`, и ни одна проверка в приложении не имеет
   права смотреть сюда. Слово «уровень» тянет к иерархии полномочий сильнее,
   чем «зона», — тем важнее сказать прямо, что её здесь нет.

   ЧЕМ УРОВЕНЬ ОТЛИЧАЕТСЯ ОТ ЗОНЫ. Зона говорит, КУДА человека пускают
   (раздевалки, микст-зона, техническая зона). Уровень говорит, НАСКОЛЬКО
   глубоко, — одной цифрой, которую печатают на бейдже и читают через стойку за
   секунду. Это ДВА НЕЗАВИСИМЫХ набора у роли, а не два имени одного: бывает
   роль с зонами и без уровней, с уровнями и без зон, и с обоими наборами.

   ЧТО СОДЕРЖИТ ЗАПИСЬ. Содержание уровня — сама ЦИФРА (`number`), а не текст:
   описание существует для организатора, который заводит справочник, и на бейдж
   не попадает. Отсюда и сортировка по номеру, и уникальность по нему же.

   Справочник локален для мероприятия, как и зоны: шкала «1..5» одного
   соревнования ничего не значит на другом. */

/** `admin.v1.AccessLevel` — запись справочника. */
export interface AccessLevel {
  id: string
  eventId: string
  /**
   * Номер уровня — целое `0..99`, уникальное **в пределах мероприятия**.
   *
   * ⚠️ **Это СОДЕРЖАНИЕ записи, а не порядковый ключ.** Именно эта цифра
   * печатается на бейдже, и именно её читает человек на входе; всё остальное в
   * записи существует ради того, чтобы организатор помнил, что она значит.
   * Поэтому номер редактируемый (шкалу переигрывают до печати) и поэтому же
   * его уникальность — не «техническое ограничение», а условие, без которого
   * бейдж перестаёт что-либо сообщать.
   *
   * Ноль — законный номер, а не «не задан»: шкалы, начинающиеся с нуля,
   * встречаются. Проверка «есть ли номер» на истинность значения — ошибка.
   *
   * Число, а не строка: JSON-число, `uint32` на проводе. Из `<input>` значение
   * приходит СТРОКОЙ, и превратить его в число обязан клиент — иначе сервер
   * ответит `VALIDATION_FAILED` на `"7"`.
   */
  number: number
  /**
   * Пояснение для организатора: «Только трибуны», «Вся арена, кроме поля».
   * Максимум 256 символов.
   *
   * ⚠️ **Пустая строка — это значение, а не отсутствие поля.** Она означает
   * «описания нет», сервер её не подменяет и ничего вместо неё не
   * подставляет. Уровень без описания — нормальная запись: цифра говорит сама
   * за себя тем, кто уже знает шкалу.
   *
   * На бейдж НЕ попадает: там только номер.
   */
  description: string
  createdAt: string
  updatedAt: string
}

/**
 * `GET /api/v1/events/{eventId}/access-levels` — весь справочник, **по
 * возрастанию номера**. Читает любой участник мероприятия.
 *
 * Пагинации нет намеренно, как и у зон: это десятки записей, из которых
 * собирают набор отметок на бейдже, а не лента.
 *
 * ⚠️ Порядок задаёт сервер, и пересортировывать его на клиенте нельзя: шкала
 * читается снизу вверх, и уровень 10, оказавшийся между 1 и 2 из-за
 * лексикографической сортировки, — классический способ сделать справочник
 * нечитаемым.
 */
export interface ListAccessLevelsResponse {
  accessLevels: AccessLevel[]
  total: number
}

export interface CreateAccessLevelRequest {
  /** Целое `0..99`, свободное в этом мероприятии. Число, не строка. */
  number: number
  /** Максимум 256 символов. Пустая строка — законное значение («описания нет»). */
  description: string
}

export interface CreateAccessLevelResponse {
  accessLevel: AccessLevel
}

/**
 * `PUT /api/v1/events/{eventId}/access-levels/{id}`.
 *
 * PUT, а не PATCH: полей ровно два, оба всегда есть в форме, и «частичное
 * обновление» означало бы, что пустое описание нельзя отличить от «не менять
 * описание». Пустая строка здесь стирает описание — это её работа.
 */
export interface UpdateAccessLevelRequest {
  number: number
  description: string
}

export interface UpdateAccessLevelResponse {
  accessLevel: AccessLevel
}

export interface DeleteAccessLevelResponse {
  /** Удалённая запись — последнее, что о ней известно. */
  accessLevel: AccessLevel
}

/**
 * `PUT /api/v1/events/{eventId}/participant-roles/{id}/access-levels` — задать
 * набор уровней роли ЦЕЛИКОМ.
 *
 * Тип лежит здесь, а не в секции 7, хотя эндпоинт висит на
 * `ParticipantRoleService`: читают его вместе с остальным срезом уровней, и
 * разнесённый по двум секциям срез пришлось бы собирать глазами. Ровно так же
 * устроен `SetParticipantRoleAccessZonesRequest` — только он остался у ролей,
 * потому что был там первым.
 *
 * Замена набора, а не add/remove по одной: на экране это список всех уровней
 * с галочками, и пара операций превратила бы щелчки в поток запросов, каждый со
 * своим шансом упасть посередине.
 *
 * Идемпотентно. Порядок игнорируется, повторы схлопываются — это множество.
 */
export interface SetParticipantRoleAccessLevelsRequest {
  /**
   * Полный новый набор. **Пустой массив — законное значение** (снять все
   * уровни), а не «поле не заполнили».
   *
   * Каждый id обязан называть уровень ЭТОГО мероприятия, иначе
   * `404 ACCESS_LEVEL_NOT_FOUND`. `400 VALIDATION_FAILED` с `metadata.field`
   * вида `accessLevelIds[2]` — один из id не uuid.
   */
  accessLevelIds: string[]
}

export interface SetParticipantRoleAccessLevelsResponse {
  /** Роль с уже применённым набором. */
  participantRole: ParticipantRole
}

/* ═════════════════════════════════════════════════════════════════════════
   7b-bis. СПРАВОЧНИК ПИКТОГРАММ — `PictogramService`
   ═════════════════════════════════════════════════════════════════════════

   Пиктограмма — КАРТИНКА и подпись к ней: «питание», «транспорт», «парковка».
   Четвёртый справочник мероприятия, и четвёртый вопрос:

     зона        КУДА пускают         → триграмма на цветной плашке
     уровень     НАСКОЛЬКО глубоко    → цифра
     пиктограмма ЧТО ПОЛОЖЕНО         → картинка

   ⚠️ **И это по-прежнему НЕ ПРАВА ДОСТУПА.** Роль с пиктограммой «парковка»
   не может в приложении ничего сверх роли без неё; права решают
   `auth.isAdmin` и `event.canManage`.

   ⚠️ **На бейдже печатаются ТОЛЬКО ВЫДАННЫЕ роли пиктограммы.** Элемент
   `BADGE_ELEMENT_TYPE_PICTOGRAMS` рисует ряд квадратов — по одному на
   выданную запись; невыданные на карточку не попадают вовсе. Это
   противоположность шкале уровней, где пустая ячейка и есть содержание, — см.
   `BadgePictogramsProps`.
   ═════════════════════════════════════════════════════════════════════════ */

/** `admin.v1.Pictogram` — запись справочника. */
export interface Pictogram {
  id: string
  eventId: string
  /**
   * Название — до 128 символов, уникально **в пределах мероприятия и без
   * учёта регистра**.
   *
   * Картинка различает записи на бейдже, название — на двух экранах, где по
   * ним щёлкают: в самом справочнике и в списке галочек под ролью. Два
   * «Питание» там неразличимы, отсюда уникальность.
   */
  name: string
  /**
   * Ключ загруженной иконки — `events/<eventId>/pictograms/<uuid>.<ext>` —
   * **либо пустая строка**.
   *
   * ⚠️ **Пустая строка — это значение, а не «ещё не загрузили».** Объектное
   * хранилище в этой системе необязательно (без него загрузка отвечает
   * `501 PHOTO_STORAGE_DISABLED`), и запись с одним названием — нормальная
   * строка справочника. Экран обязан её рисовать, а не показывать «битую
   * картинку».
   *
   * ⚠️ **Клиент никогда не собирает этот ключ сам.** Его отдаёт загрузка
   * (`pictogramsApi.uploadIcon`), и он же уезжает обратно в `iconKey`. Байты
   * забираются по `pictogramIconUrl(eventId, key)` — ключ туда попадает
   * последним сегментом, всё остальное собирает сервер из мероприятия,
   * которое он авторизовал.
   */
  iconKey: string
  /**
   * Позиция в списке — целое `0..9999`, меньшие первыми.
   *
   * ⚠️ **НЕ уникальна**, в отличие от номера уровня доступа, и это не
   * недосмотр: номер уровня — содержание записи, а позиция — только порядок.
   * Одинаковые позиции значат «мне всё равно, в каком порядке эти две», и
   * сервер разрешает равенство по названию.
   *
   * Ноль — обычное значение и значение по умолчанию, а не «не задано».
   *
   * Число, а не строка: из `<input>` значение приходит СТРОКОЙ, и превращает
   * его клиент — иначе `"10"` вернётся `VALIDATION_FAILED`.
   */
  sortOrder: number
  /**
   * Масштаб картинки внутри квадрата на бейдже, % — целое `50..200`.
   *
   * 100 — картинка вписана в квадрат за вычетом отступа элемента (так
   * печаталось до появления поля). Меньше — мельче; больше — занимает отступ,
   * но не выходит за квадрат. Квадрат и ряд от него не меняются.
   *
   * Нужен потому, что у каждой загруженной иконки свои поля вокруг рисунка, а
   * отступ элемента двигает их все разом.
   */
  iconScalePercent: number
  createdAt: string
  updatedAt: string
}

/**
 * `admin.v1.PictogramIcon` — ответ загрузки иконки.
 *
 * У загрузки нет RPC: байты ездят `multipart/form-data`, а не в JSON. Форма
 * кладёт `key` в `Pictogram.iconKey` и рисует превью в пропорциях картинки по
 * `width`/`height`, не дожидаясь, пока браузер её скачает.
 */
export interface PictogramIcon {
  key: string
  /** `image/jpeg` либо `image/png` — выбирается по ПИКСЕЛЯМ, а не по тому, что прислали. */
  contentType: string
  /** Размер сохранённого файла. Число, не строка: поле `uint32`. */
  bytes: number
  width: number
  height: number
}

/**
 * `GET /api/v1/events/{eventId}/pictograms` — весь справочник, **в порядке
 * сортировки**. Читает любой участник мероприятия.
 *
 * ⚠️ Порядок задаёт сервер: `(sortOrder, name)`. Пересортировывать на клиенте
 * нельзя — `sortOrder` не уникален, и сортировка «только по позиции» на
 * клиенте перетасовала бы записи с одинаковой позицией на каждой перерисовке.
 */
export interface ListPictogramsResponse {
  pictograms: Pictogram[]
  total: number
}

export interface CreatePictogramRequest {
  /** До 128 символов, свободно в этом мероприятии без учёта регистра. */
  name: string
  /** Ключ от `uploadIcon`, либо пустая строка («иконки нет»). */
  iconKey: string
  /** Целое `0..9999`. Число, не строка. */
  sortOrder: number
  /**
   * Целое `50..200`. ⚠️ 0 сервер читает как «не задано» и сохраняет 100.
   */
  iconScalePercent: number
}

export interface CreatePictogramResponse {
  pictogram: Pictogram
}

/**
 * `PUT /api/v1/events/{eventId}/pictograms/{id}`.
 *
 * PUT, а не PATCH: все поля всегда есть в форме.
 *
 * ⚠️ **Пустой `iconKey` СТИРАЕТ иконку**, а не означает «не менять» — иначе
 * убрать однажды загруженную картинку было бы нечем. И сервер при этом удаляет
 * файл: ключ хранится ровно в этой строке, назвать объект после правки больше
 * нечем.
 */
export interface UpdatePictogramRequest {
  name: string
  iconKey: string
  sortOrder: number
  /**
   * Целое `50..200`. ⚠️ 0 сервер читает как «не задано» и сохраняет 100.
   */
  iconScalePercent: number
}

export interface UpdatePictogramResponse {
  pictogram: Pictogram
}

export interface DeletePictogramResponse {
  /**
   * Удалённая запись — последнее, что о ней известно. `iconKey` в ней называет
   * файл, которого уже нет: удаление записи уносит и его.
   */
  pictogram: Pictogram
}

/**
 * `PUT /api/v1/events/{eventId}/participant-roles/{id}/pictograms` — задать
 * набор пиктограмм роли ЦЕЛИКОМ.
 *
 * Третий отдельный эндпоинт, а не третье поле в чужом запросе: три набора
 * правят тремя разными галочками и в разное время, и общий PUT означал бы, что
 * щелчок по пиктограмме переписывает зоны и уровни.
 *
 * Идемпотентно. Порядок игнорируется, повторы схлопываются — это множество.
 */
export interface SetParticipantRolePictogramsRequest {
  /**
   * Полный новый набор. **Пустой массив — законное значение** (снять все
   * пиктограммы), а не «поле не заполнили».
   *
   * Каждый id обязан называть пиктограмму ЭТОГО мероприятия, иначе
   * `404 PICTOGRAM_NOT_FOUND`. `400 VALIDATION_FAILED` с `metadata.field` вида
   * `pictogramIds[2]` — один из id не uuid.
   */
  pictogramIds: string[]
}

export interface SetParticipantRolePictogramsResponse {
  /** Роль с уже применённым набором. */
  participantRole: ParticipantRole
}

/* ═════════════════════════════════════════════════════════════════════════
   7c. ШАБЛОН БЕЙДЖА — `BadgeTemplateService`
   ═════════════════════════════════════════════════════════════════════════

   Бейдж — единственный ФИЗИЧЕСКИЙ предмет, который производит эта система.
   Всё остальное живёт на экранах; бейдж печатают на картоне, вешают человеку
   на шею и читают через стойку за секунду. Отсюда два свойства, которые
   пронизывают весь раздел и которые нельзя «упростить»:

   1. **ВСЁ В МИЛЛИМЕТРАХ.** Кроме кегля, который в пунктах, — это тоже
      физическая величина (1/72 дюйма). Ни одного пикселя в контракте нет и
      быть не должно: 105 мм остаются 105 миллиметрами на любом принтере, а
      пиксель браузер пересчитает по своему усмотрению, и между экраном 96 dpi
      и печатью 600 dpi разница в шесть раз.

   2. **КЕГЛЬ — ЭТО ДИАПАЗОН, А НЕ ЧИСЛО.** Фамилия бывает в четыре буквы и в
      двадцать восемь с дефисом, зон доступа бывает одна и четырнадцать, и
      печатается это одним шаблоном. Поэтому у текста есть `fontSizePt` (потолок)
      и `minFontSizePt` (пол), а у плашек — `chipHeightMm` и `minChipHeightMm`.
      Подбор делает клиент при отрисовке; см. `lib/badge/`.

   ⚠️ ШАБЛОН ОДИН НА МЕРОПРИЯТИЕ. Списка шаблонов нет и не планируется:
   бейдж спортсмена отличается от бейджа прессы РОЛЬЮ и НАБОРОМ ЗОН, а они уже
   рисуются элементами `ROLE` и `ZONES` из данных участника. Второй макет не
   добавил бы ни одного сведения, зато добавил бы вопрос «а этот человек по
   какому печатается» — на стойке, в день соревнования.

   ⚠️ ЗАГОТОВКИ (предгенеренные макеты) ЖИВУТ НА КЛИЕНТЕ — `lib/badge/presets.ts`.
   Сервер про них не знает и знать не должен: заготовка это начальное состояние
   формы, а не сущность. Применение заготовки — обычный `PUT`, после которого у
   мероприятия просто есть свой шаблон. Никакой связи «произошёл от заготовки
   №3» не хранится: она стала бы ложью в ту же секунду, когда человек подвинет
   первый элемент.

   ПРАВА: пишет admin или owner, **читает любой участник, включая наблюдателя**.
   Последнее не щедрость: печатает бейджи оператор аккредитации, а он viewer, и
   макет, который он не может прочитать, — это бейдж, напечатанный пустым. */

/* ─────────────────────────── ПЕРЕЧИСЛЕНИЯ ───────────────────────────── */

/** `admin.v1.BadgeImageFit` — как картинка вписывается в свой прямоугольник. */
export type BadgeImageFit =
  | 'BADGE_IMAGE_FIT_UNSPECIFIED'
  | 'BADGE_IMAGE_FIT_COVER'
  | 'BADGE_IMAGE_FIT_CONTAIN'
  | 'BADGE_IMAGE_FIT_STRETCH'
export const BADGE_IMAGE_FITS = [
  'BADGE_IMAGE_FIT_COVER',
  'BADGE_IMAGE_FIT_CONTAIN',
  'BADGE_IMAGE_FIT_STRETCH',
] as const satisfies readonly BadgeImageFit[]

export const BADGE_IMAGE_FIT_LABELS: Record<BadgeImageFit, string> = {
  BADGE_IMAGE_FIT_UNSPECIFIED: 'По умолчанию',
  BADGE_IMAGE_FIT_COVER: 'Заполнить (обрезать лишнее)',
  BADGE_IMAGE_FIT_CONTAIN: 'Вписать целиком',
  BADGE_IMAGE_FIT_STRETCH: 'Растянуть',
}

/** `admin.v1.BadgeElementType`. Определяет, какая ветка настроек заполнена. */
export type BadgeElementType =
  | 'BADGE_ELEMENT_TYPE_UNSPECIFIED'
  | 'BADGE_ELEMENT_TYPE_PHOTO'
  | 'BADGE_ELEMENT_TYPE_NAME'
  | 'BADGE_ELEMENT_TYPE_ROLE'
  | 'BADGE_ELEMENT_TYPE_ZONES'
  | 'BADGE_ELEMENT_TYPE_QR'
  | 'BADGE_ELEMENT_TYPE_TEXT'
  | 'BADGE_ELEMENT_TYPE_IMAGE'
  | 'BADGE_ELEMENT_TYPE_SHAPE'
  | 'BADGE_ELEMENT_TYPE_FIELD'
  | 'BADGE_ELEMENT_TYPE_COUNTRY'
  /** `= 11` в proto; в списках ниже стоит рядом с зонами — по смыслу, а не по номеру. */
  | 'BADGE_ELEMENT_TYPE_ACCESS_LEVELS'
  /** `= 12` в proto; в списках ниже стоит следом за уровнями — по смыслу. */
  | 'BADGE_ELEMENT_TYPE_PICTOGRAMS'

/** Типы, которые человек реально может добавить на холст. */
export const BADGE_ELEMENT_TYPES = [
  'BADGE_ELEMENT_TYPE_PHOTO',
  'BADGE_ELEMENT_TYPE_NAME',
  'BADGE_ELEMENT_TYPE_ROLE',
  'BADGE_ELEMENT_TYPE_ZONES',
  'BADGE_ELEMENT_TYPE_ACCESS_LEVELS',
  'BADGE_ELEMENT_TYPE_PICTOGRAMS',
  'BADGE_ELEMENT_TYPE_QR',
  'BADGE_ELEMENT_TYPE_TEXT',
  'BADGE_ELEMENT_TYPE_IMAGE',
  'BADGE_ELEMENT_TYPE_SHAPE',
  'BADGE_ELEMENT_TYPE_FIELD',
  'BADGE_ELEMENT_TYPE_COUNTRY',
] as const satisfies readonly BadgeElementType[]

export const BADGE_ELEMENT_TYPE_LABELS: Record<BadgeElementType, string> = {
  BADGE_ELEMENT_TYPE_UNSPECIFIED: 'Не указан',
  BADGE_ELEMENT_TYPE_PHOTO: 'Фотография',
  BADGE_ELEMENT_TYPE_NAME: 'ФИО',
  BADGE_ELEMENT_TYPE_ROLE: 'Роль',
  BADGE_ELEMENT_TYPE_ZONES: 'Зоны доступа',
  BADGE_ELEMENT_TYPE_ACCESS_LEVELS: 'Уровни доступа',
  BADGE_ELEMENT_TYPE_PICTOGRAMS: 'Пиктограммы',
  BADGE_ELEMENT_TYPE_QR: 'QR-код',
  BADGE_ELEMENT_TYPE_TEXT: 'Текст',
  BADGE_ELEMENT_TYPE_IMAGE: 'Картинка',
  BADGE_ELEMENT_TYPE_SHAPE: 'Фигура',
  BADGE_ELEMENT_TYPE_FIELD: 'Дополнительное поле',
  BADGE_ELEMENT_TYPE_COUNTRY: 'Страна',
}

/**
 * `admin.v1.BadgeFontFamily` — семейство из ЗАКРЫТОГО списка.
 *
 * ⚠️ Обещания у значений разной силы, и это надо знать. `SANS` — это Inter, и
 * он **вшивается в документ печати** data-URL'ом, то есть на бумаге будет
 * именно он. Остальные три — системный стек: что нарисуется, зависит от машины.
 *
 * Почему второе безопасно: кегль подбирается НЕ при сохранении шаблона, а в
 * момент отрисовки, тем же движком, который печатает. Значит метрики, по
 * которым подобран размер, — всегда метрики того шрифта, который сейчас
 * нарисуется. От машины к машине меняется подобранный кегль (вид), а не
 * читаемость.
 */
export type BadgeFontFamily =
  | 'BADGE_FONT_FAMILY_UNSPECIFIED'
  | 'BADGE_FONT_FAMILY_SANS'
  | 'BADGE_FONT_FAMILY_SERIF'
  | 'BADGE_FONT_FAMILY_MONO'
  | 'BADGE_FONT_FAMILY_CONDENSED'
export const BADGE_FONT_FAMILIES = [
  'BADGE_FONT_FAMILY_SANS',
  'BADGE_FONT_FAMILY_SERIF',
  'BADGE_FONT_FAMILY_MONO',
  'BADGE_FONT_FAMILY_CONDENSED',
] as const satisfies readonly BadgeFontFamily[]

export const BADGE_FONT_FAMILY_LABELS: Record<BadgeFontFamily, string> = {
  BADGE_FONT_FAMILY_UNSPECIFIED: 'По умолчанию',
  BADGE_FONT_FAMILY_SANS: 'Основной (Inter)',
  BADGE_FONT_FAMILY_SERIF: 'С засечками',
  BADGE_FONT_FAMILY_MONO: 'Моноширинный',
  BADGE_FONT_FAMILY_CONDENSED: 'Узкий',
}

/** `admin.v1.BadgeTextAlign`. */
export type BadgeTextAlign =
  | 'BADGE_TEXT_ALIGN_UNSPECIFIED'
  | 'BADGE_TEXT_ALIGN_LEFT'
  | 'BADGE_TEXT_ALIGN_CENTER'
  | 'BADGE_TEXT_ALIGN_RIGHT'
export const BADGE_TEXT_ALIGNS = [
  'BADGE_TEXT_ALIGN_LEFT',
  'BADGE_TEXT_ALIGN_CENTER',
  'BADGE_TEXT_ALIGN_RIGHT',
] as const satisfies readonly BadgeTextAlign[]

export const BADGE_TEXT_ALIGN_LABELS: Record<BadgeTextAlign, string> = {
  BADGE_TEXT_ALIGN_UNSPECIFIED: 'По умолчанию',
  BADGE_TEXT_ALIGN_LEFT: 'По левому краю',
  BADGE_TEXT_ALIGN_CENTER: 'По центру',
  BADGE_TEXT_ALIGN_RIGHT: 'По правому краю',
}

/** `admin.v1.BadgeVerticalAlign`. */
export type BadgeVerticalAlign =
  | 'BADGE_VERTICAL_ALIGN_UNSPECIFIED'
  | 'BADGE_VERTICAL_ALIGN_TOP'
  | 'BADGE_VERTICAL_ALIGN_MIDDLE'
  | 'BADGE_VERTICAL_ALIGN_BOTTOM'
export const BADGE_VERTICAL_ALIGNS = [
  'BADGE_VERTICAL_ALIGN_TOP',
  'BADGE_VERTICAL_ALIGN_MIDDLE',
  'BADGE_VERTICAL_ALIGN_BOTTOM',
] as const satisfies readonly BadgeVerticalAlign[]

export const BADGE_VERTICAL_ALIGN_LABELS: Record<BadgeVerticalAlign, string> = {
  BADGE_VERTICAL_ALIGN_UNSPECIFIED: 'По умолчанию',
  BADGE_VERTICAL_ALIGN_TOP: 'По верхнему краю',
  BADGE_VERTICAL_ALIGN_MIDDLE: 'По центру',
  BADGE_VERTICAL_ALIGN_BOTTOM: 'По нижнему краю',
}

/**
 * `admin.v1.BadgeTextOverflow` — что делать с текстом, который не помещается.
 *
 * ⚠️ Умолчание — `SHRINK`, и это главное решение всего раздела. Обрезание —
 * худший исход: волонтёр держит в руках «Константинопольск» и не может сверить
 * его с экраном, то есть задача, ради которой бейдж напечатан, не решается.
 */
export type BadgeTextOverflow =
  | 'BADGE_TEXT_OVERFLOW_UNSPECIFIED'
  | 'BADGE_TEXT_OVERFLOW_SHRINK'
  | 'BADGE_TEXT_OVERFLOW_CLIP'
  | 'BADGE_TEXT_OVERFLOW_ELLIPSIS'
export const BADGE_TEXT_OVERFLOWS = [
  'BADGE_TEXT_OVERFLOW_SHRINK',
  'BADGE_TEXT_OVERFLOW_CLIP',
  'BADGE_TEXT_OVERFLOW_ELLIPSIS',
] as const satisfies readonly BadgeTextOverflow[]

export const BADGE_TEXT_OVERFLOW_LABELS: Record<BadgeTextOverflow, string> = {
  BADGE_TEXT_OVERFLOW_UNSPECIFIED: 'По умолчанию',
  BADGE_TEXT_OVERFLOW_SHRINK: 'Уменьшать кегль',
  BADGE_TEXT_OVERFLOW_CLIP: 'Обрезать',
  BADGE_TEXT_OVERFLOW_ELLIPSIS: 'Обрезать с многоточием',
}

/** `admin.v1.BadgePhotoPlaceholder` — чем заменить фотографию, которой нет. */
export type BadgePhotoPlaceholder =
  | 'BADGE_PHOTO_PLACEHOLDER_UNSPECIFIED'
  | 'BADGE_PHOTO_PLACEHOLDER_SILHOUETTE'
  | 'BADGE_PHOTO_PLACEHOLDER_INITIALS'
  | 'BADGE_PHOTO_PLACEHOLDER_EMPTY'
export const BADGE_PHOTO_PLACEHOLDERS = [
  'BADGE_PHOTO_PLACEHOLDER_SILHOUETTE',
  'BADGE_PHOTO_PLACEHOLDER_INITIALS',
  'BADGE_PHOTO_PLACEHOLDER_EMPTY',
] as const satisfies readonly BadgePhotoPlaceholder[]

export const BADGE_PHOTO_PLACEHOLDER_LABELS: Record<BadgePhotoPlaceholder, string> = {
  BADGE_PHOTO_PLACEHOLDER_UNSPECIFIED: 'По умолчанию',
  BADGE_PHOTO_PLACEHOLDER_SILHOUETTE: 'Силуэт',
  BADGE_PHOTO_PLACEHOLDER_INITIALS: 'Инициалы',
  BADGE_PHOTO_PLACEHOLDER_EMPTY: 'Ничего',
}

/**
 * `admin.v1.BadgeNameFormat` — как собрать ФИО из трёх колонок.
 *
 * ⚠️ Отчества может не быть вовсе (`middleName === ''`), и любой формат обязан
 * это пережить, не оставив висящего пробела.
 */
export type BadgeNameFormat =
  | 'BADGE_NAME_FORMAT_UNSPECIFIED'
  | 'BADGE_NAME_FORMAT_LAST_FIRST'
  | 'BADGE_NAME_FORMAT_LAST_FIRST_MIDDLE'
  | 'BADGE_NAME_FORMAT_FIRST_LAST'
  | 'BADGE_NAME_FORMAT_LAST_ONLY'
  | 'BADGE_NAME_FORMAT_FIRST_ONLY'
  | 'BADGE_NAME_FORMAT_LAST_INITIALS'
export const BADGE_NAME_FORMATS = [
  'BADGE_NAME_FORMAT_LAST_FIRST',
  'BADGE_NAME_FORMAT_LAST_FIRST_MIDDLE',
  'BADGE_NAME_FORMAT_FIRST_LAST',
  'BADGE_NAME_FORMAT_LAST_ONLY',
  'BADGE_NAME_FORMAT_FIRST_ONLY',
  'BADGE_NAME_FORMAT_LAST_INITIALS',
] as const satisfies readonly BadgeNameFormat[]

export const BADGE_NAME_FORMAT_LABELS: Record<BadgeNameFormat, string> = {
  BADGE_NAME_FORMAT_UNSPECIFIED: 'По умолчанию',
  BADGE_NAME_FORMAT_LAST_FIRST: 'Иванов Иван',
  BADGE_NAME_FORMAT_LAST_FIRST_MIDDLE: 'Иванов Иван Иванович',
  BADGE_NAME_FORMAT_FIRST_LAST: 'Иван Иванов',
  BADGE_NAME_FORMAT_LAST_ONLY: 'Иванов',
  BADGE_NAME_FORMAT_FIRST_ONLY: 'Иван',
  BADGE_NAME_FORMAT_LAST_INITIALS: 'Иванов И. И.',
}

/** `admin.v1.BadgeNameLineBreak` — как разложить ФИО по строкам. */
export type BadgeNameLineBreak =
  | 'BADGE_NAME_LINE_BREAK_UNSPECIFIED'
  | 'BADGE_NAME_LINE_BREAK_SINGLE'
  | 'BADGE_NAME_LINE_BREAK_SURNAME_FIRST'
  | 'BADGE_NAME_LINE_BREAK_AUTO'
export const BADGE_NAME_LINE_BREAKS = [
  'BADGE_NAME_LINE_BREAK_AUTO',
  'BADGE_NAME_LINE_BREAK_SINGLE',
  'BADGE_NAME_LINE_BREAK_SURNAME_FIRST',
] as const satisfies readonly BadgeNameLineBreak[]

export const BADGE_NAME_LINE_BREAK_LABELS: Record<BadgeNameLineBreak, string> = {
  BADGE_NAME_LINE_BREAK_UNSPECIFIED: 'По умолчанию',
  BADGE_NAME_LINE_BREAK_SINGLE: 'Одной строкой',
  BADGE_NAME_LINE_BREAK_SURNAME_FIRST: 'Фамилия отдельной строкой',
  BADGE_NAME_LINE_BREAK_AUTO: 'Автоматически',
}

/**
 * `admin.v1.BadgeRoleColorMode` — куда лечь цвету роли из справочника.
 *
 * ⚠️ `UNSPECIFIED` — это `TEXT`, а не «не решено». Шаблоны, сохранённые до
 * появления настройки, ключа `colorMode` не несут вовсе, и печатать они обязаны
 * ровно то же, что печатали вчера, — цветную строку роли.
 */
export type BadgeRoleColorMode =
  | 'BADGE_ROLE_COLOR_MODE_UNSPECIFIED'
  | 'BADGE_ROLE_COLOR_MODE_TEXT'
  | 'BADGE_ROLE_COLOR_MODE_PLATE'
export const BADGE_ROLE_COLOR_MODES = [
  'BADGE_ROLE_COLOR_MODE_TEXT',
  'BADGE_ROLE_COLOR_MODE_PLATE',
] as const satisfies readonly BadgeRoleColorMode[]

export const BADGE_ROLE_COLOR_MODE_LABELS: Record<BadgeRoleColorMode, string> = {
  BADGE_ROLE_COLOR_MODE_UNSPECIFIED: 'По умолчанию',
  BADGE_ROLE_COLOR_MODE_TEXT: 'Цветом текста',
  BADGE_ROLE_COLOR_MODE_PLATE: 'Заливкой подложки',
}

/** `admin.v1.BadgeZonesLayout` — как разложить плашки зон. */
export type BadgeZonesLayout =
  | 'BADGE_ZONES_LAYOUT_UNSPECIFIED'
  | 'BADGE_ZONES_LAYOUT_WRAP'
  | 'BADGE_ZONES_LAYOUT_ROW'
  | 'BADGE_ZONES_LAYOUT_COLUMN'
  | 'BADGE_ZONES_LAYOUT_GRID'
export const BADGE_ZONES_LAYOUTS = [
  'BADGE_ZONES_LAYOUT_WRAP',
  'BADGE_ZONES_LAYOUT_ROW',
  'BADGE_ZONES_LAYOUT_COLUMN',
  'BADGE_ZONES_LAYOUT_GRID',
] as const satisfies readonly BadgeZonesLayout[]

export const BADGE_ZONES_LAYOUT_LABELS: Record<BadgeZonesLayout, string> = {
  BADGE_ZONES_LAYOUT_UNSPECIFIED: 'По умолчанию',
  BADGE_ZONES_LAYOUT_WRAP: 'В строку с переносом',
  BADGE_ZONES_LAYOUT_ROW: 'Одной строкой',
  BADGE_ZONES_LAYOUT_COLUMN: 'Одним столбцом',
  BADGE_ZONES_LAYOUT_GRID: 'Сеткой',
}

/**
 * `admin.v1.BadgeAccessLevelsLayout` — как разложить шкалу уровней.
 *
 * ⚠️ `UNSPECIFIED` — это `ONE_ROW`, а не «не решено»: у шкалы нет состояния «не
 * разложена», и документ без ключа обязан печататься одной строкой.
 *
 * Диапазона подбора числа колонок здесь НЕТ, в отличие от зон, и это не
 * упущение: ширина шкалы одинакова на всех бейджах мероприятия (она задана
 * справочником, а не участником), поэтому число колонок выводится из раскладки
 * — все ячейки либо `ceil(N/2)`.
 */
export type BadgeAccessLevelsLayout =
  | 'BADGE_ACCESS_LEVELS_LAYOUT_UNSPECIFIED'
  | 'BADGE_ACCESS_LEVELS_LAYOUT_ONE_ROW'
  | 'BADGE_ACCESS_LEVELS_LAYOUT_TWO_ROWS'
export const BADGE_ACCESS_LEVELS_LAYOUTS = [
  'BADGE_ACCESS_LEVELS_LAYOUT_ONE_ROW',
  'BADGE_ACCESS_LEVELS_LAYOUT_TWO_ROWS',
] as const satisfies readonly BadgeAccessLevelsLayout[]

export const BADGE_ACCESS_LEVELS_LAYOUT_LABELS: Record<BadgeAccessLevelsLayout, string> = {
  BADGE_ACCESS_LEVELS_LAYOUT_UNSPECIFIED: 'По умолчанию',
  BADGE_ACCESS_LEVELS_LAYOUT_ONE_ROW: 'Одной строкой, через разделители',
  BADGE_ACCESS_LEVELS_LAYOUT_TWO_ROWS: 'В две строки, ячейками',
}

/** `admin.v1.BadgeQrEcc` — уровень коррекции ошибок QR. */
export type BadgeQrEcc =
  | 'BADGE_QR_ECC_UNSPECIFIED'
  | 'BADGE_QR_ECC_L'
  | 'BADGE_QR_ECC_M'
  | 'BADGE_QR_ECC_Q'
  | 'BADGE_QR_ECC_H'
export const BADGE_QR_ECCS = [
  'BADGE_QR_ECC_L',
  'BADGE_QR_ECC_M',
  'BADGE_QR_ECC_Q',
  'BADGE_QR_ECC_H',
] as const satisfies readonly BadgeQrEcc[]

export const BADGE_QR_ECC_LABELS: Record<BadgeQrEcc, string> = {
  BADGE_QR_ECC_UNSPECIFIED: 'По умолчанию',
  BADGE_QR_ECC_L: 'L — 7 %, самая редкая сетка',
  BADGE_QR_ECC_M: 'M — 15 %, обычный выбор',
  BADGE_QR_ECC_Q: 'Q — 25 %',
  BADGE_QR_ECC_H: 'H — 30 %, самая плотная сетка',
}

/** `admin.v1.BadgeShapeKind`. */
export type BadgeShapeKind =
  | 'BADGE_SHAPE_KIND_UNSPECIFIED'
  | 'BADGE_SHAPE_KIND_RECT'
  | 'BADGE_SHAPE_KIND_ELLIPSE'
  | 'BADGE_SHAPE_KIND_LINE'
export const BADGE_SHAPE_KINDS = [
  'BADGE_SHAPE_KIND_RECT',
  'BADGE_SHAPE_KIND_ELLIPSE',
  'BADGE_SHAPE_KIND_LINE',
] as const satisfies readonly BadgeShapeKind[]

export const BADGE_SHAPE_KIND_LABELS: Record<BadgeShapeKind, string> = {
  BADGE_SHAPE_KIND_UNSPECIFIED: 'По умолчанию',
  BADGE_SHAPE_KIND_RECT: 'Прямоугольник',
  BADGE_SHAPE_KIND_ELLIPSE: 'Овал',
  BADGE_SHAPE_KIND_LINE: 'Линия',
}

/* ─────────────────────────── СТИЛИ ──────────────────────────────────── */

/** `admin.v1.BadgeBoxStyle` — прямоугольник элемента: общий для всех типов. */
export interface BadgeBoxStyle {
  /** `#rrggbb`. **Пустая строка — прозрачно**, и это самое частое значение. */
  fillColor: string
  /** 0..1. Отдельно от `opacity` элемента: полупрозрачная подложка под читаемым текстом — приём, полупрозрачный текст — нет. */
  fillOpacity: number
  /** `#rrggbb`. Пустая строка — рамки нет. */
  borderColor: string
  borderWidthMm: number
  cornerRadiusMm: number
  /** Один отступ на все четыре стороны: четыре независимых ни разу не понадобились. */
  paddingMm: number
}

/** `admin.v1.BadgeTextStyle` — типографика элементов `NAME`, `ROLE` и `TEXT`. */
export interface BadgeTextStyle {
  fontFamily: BadgeFontFamily
  /** ПОТОЛОК, а не фиксированный размер: короткое имя печатается им, длинное уменьшается от него вниз. */
  fontSizePt: number
  /**
   * Пол автоподбора.
   *
   * ⚠️ Никогда не ноль. Ноль означал бы «уменьшай сколько угодно», то есть
   * согласие напечатать фамилию четвёркой кегля — формально помещается, на
   * стойке нечитаемо. Сервер подставляет половину потолка, если прислать 0.
   */
  minFontSizePt: number
  /** 100..900 с шагом 100. */
  fontWeight: number
  italic: boolean
  /** Печатать ЗАГЛАВНЫМИ: текст приходит из базы, а не из поля ввода. */
  uppercase: boolean
  /** `#rrggbb`. Пустым не бывает — невидимый текст это не стиль. */
  color: string
  align: BadgeTextAlign
  verticalAlign: BadgeVerticalAlign
  /** Множитель кегля, 0.8..3. */
  lineHeight: number
  /** Разрядка в долях кегля, -0.1..1. */
  letterSpacingEm: number
  /** 0..8; 0 — «сколько влезет в высоту элемента». */
  maxLines: number
  overflow: BadgeTextOverflow
}

/* ─────────────────────── НАСТРОЙКИ ПО ТИПАМ ─────────────────────────── */

export interface BadgePhotoProps {
  fit: BadgeImageFit
  placeholder: BadgePhotoPlaceholder
  /** Не украшение: цветная печать двух тысяч бейджей стоит денег, а лицо в сером читается не хуже. */
  grayscale: boolean
}

export interface BadgeNameProps {
  text: BadgeTextStyle
  format: BadgeNameFormat
  lineBreak: BadgeNameLineBreak
  /**
   * Последняя мера, когда даже `minFontSizePt` не спасает: сократить отчество
   * до инициала, затем имя. Выключено по умолчанию — сокращать человеку имя
   * без спроса нехорошо, — но выбор между «Иванов-Задунайский И.» и нечитаемой
   * строкой должен делать верстальщик заранее, а не оператор в очереди.
   */
  abbreviateWhenTooLong: boolean
}

export interface BadgeRoleProps {
  text: BadgeTextStyle
  /** Что печатать, если роль не назначена (она необязательна). Пусто — ничего. */
  emptyText: string

  /**
   * Куда лечь цвету роли из справочника: на текст строки или в заливку
   * подложки элемента.
   *
   * ⚠️ **ЭТО НЕ ВЫБОР ЦВЕТА, А ВЫБОР МЕСТА.** Сам цвет по-прежнему берётся
   * только из справочника ролей и только оттуда: макет не может ни задать
   * его, ни отменить. Здесь решается ровно один вопрос — красить строку или
   * плашку под ней, — и ответ на него виден на превью целиком, в отличие от
   * снятого `color_source` (`reserved 3` в proto), который позволял макету
   * молча подставить СВОЙ цвет вместо цвета организатора.
   *
   * ⚠️ **Цвет роли ложится ровно в одно место.** В `TEXT` он не трогает `box`,
   * в `PLATE` — не красит строку. Иначе вышел бы цветной текст на той же
   * цветной заливке, то есть нечитаемый прямоугольник, напечатанный тиражом.
   *
   * ⚠️ **В `PLATE` цвет текста не выбирается вовсе — он вычисляется из
   * заливки:** белый либо почти чёрный, по яркости, тем же `contrastTextColor`,
   * что красит текст на плашках зон. `text.color` в этом режиме управляет
   * только ролями БЕЗ собственного цвета и заглушкой `emptyText` — у них
   * заливки нет и вычислять не из чего. Оставить его настраиваемым поверх
   * плашки было нельзя: цвет роли меняют в справочнике, а вёрстку делают
   * месяцем раньше, и любой фиксированный оттенок однажды совпал бы с чьей-то
   * заливкой — молча и тиражом.
   *
   * Роль без цвета и отсутствие роли вовсе обрабатываются в обоих режимах
   * одинаково: печатается ровно то, что настроено в макете.
   */
  colorMode: BadgeRoleColorMode
}

/**
 * `admin.v1.BadgeZonesProps` — плашки зон доступа.
 *
 * ⚠️ ЧИСЛО ЗОН ЗАРАНЕЕ НЕИЗВЕСТНО. У гостя одна, у главного судьи —
 * четырнадцать, печатается это одним шаблоном. Поэтому размер плашки здесь
 * диапазон: клиент подбирает самый крупный, при котором все помещаются, и
 * только если не помещаются даже минимальным — сворачивает хвост в «+N».
 *
 * ⚠️ ЦВЕТА ТЕКСТА ЗДЕСЬ НЕТ И НЕ БУДЕТ. Фон плашки — цвет зоны из справочника,
 * его выбирает человек, глядя на палитру. Цвет текста ВЫЧИСЛЯЕТСЯ из фона
 * (`lib/color.ts`, `contrastTextColor`) — ровно так же, как на экране.
 */
export interface BadgeZonesProps {
  layout: BadgeZonesLayout
  chipHeightMm: number
  minChipHeightMm: number
  gapMm: number
  cornerRadiusMm: number
  /** 1..12 для сетки; 0 — считать автоматически. */
  columns: number
  /**
   * Сколько плашек показать максимум; 0 — все.
   *
   * ⚠️ Ненулевое значение — это осознанное «часть зон на бейдже не написана».
   * Скрытая зона читается человеком на двери как «доступа нет», поэтому
   * ставить его стоит только там, где остальное проверяют по сканеру.
   */
  maxVisible: number
  fontSizePt: number
  minFontSizePt: number
  fontWeight: number
  fontFamily: BadgeFontFamily
  showName: boolean
  /** Что печатать, когда у роли нет ни одной зоны (штатное состояние). */
  emptyText: string
  /** Печатать контуром вместо заливки — для чёрно-белой печати. */
  monochrome: boolean
}

/**
 * `admin.v1.BadgeAccessLevelsProps` — шкала уровней доступа.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ЯЧЕЕК СТОЛЬКО, СКОЛЬКО В СПРАВОЧНИКЕ, А НЕ СКОЛЬКО ВЫДАНО
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Шкала — это ВСЕ целые от минимального до максимального номера в справочнике
 * уровней мероприятия. Ячейка печатает свою цифру, если роль участника этот
 * уровень даёт, и остаётся пустой, если не даёт. Номер, которого в справочнике
 * нет вовсе (дырка в шкале), тоже даёт пустую ячейку — отдельного правила для
 * него не нужно: его просто никому не выдали, и на бумаге оба случая означают
 * одно и то же — «сюда не пускают».
 *
 *     Справочник: 1,2,4,5,6      → шкала 1..6, шесть ячеек
 *     «Спортсмен» даёт 1,2,4,5,6 → 1 | 2 |   | 4 | 5 | 6
 *     «Пресса» даёт    1,5       → 1 |   |   |   | 5 |
 *
 * Отсюда всё остальное. Ширина шкалы ОДИНАКОВА на всех бейджах мероприятия,
 * поэтому здесь, в отличие от `BadgeZonesProps`, нет ни `columns`, ни
 * `maxVisible`: сворачивать нечего, число ячеек известно верстальщику заранее.
 * Подбор кегля остаётся — цифра обязана читаться и на шкале из трёх номеров, и
 * на шкале из десяти.
 *
 * ⚠️ РАМКА ЯЧЕЙКИ РИСУЕТСЯ И У ПУСТОЙ. Это не оформление: рамка — единственное,
 * что делает пустое место ВИДИМЫМ, а видимое пустое место и есть половина
 * ответа на входе. Шкала без рамок читается как «1 2 4 5», где пропуск
 * приходится считать глазами.
 *
 * ⚠️ МАКЕТ С ЭТИМ ЭЛЕМЕНТОМ ХРАНИТСЯ КАК ДОКУМЕНТ ВЕРСИИ `v: 6`
 * (`internal/data/badgedoc.go`). Версия проставляется по СОДЕРЖИМОМУ, а не по
 * сборке: пока такого элемента в макете нет, документ остаётся на прежней
 * версии, и откат развёртывания его не ломает. Как только элемент добавлен,
 * старая сборка обязана отказаться такой макет читать — она напечатала бы
 * бейдж без шкалы, целый и неправильный.
 */
export interface BadgeAccessLevelsProps {
  layout: BadgeAccessLevelsLayout
  /**
   * Желаемая высота ячейки, мм, и нижняя граница подбора.
   *
   * ⚠️ `minCellHeightMm === 0` — это «сервер подставит `max(2.5,
   * cellHeightMm * 0.6)`», а не «уменьшать сколько угодно». Клиент обязан
   * подставлять то же самое при отрисовке: иначе превью покажет один размер,
   * а сохранённый и перечитанный макет — другой.
   */
  cellHeightMm: number
  minCellHeightMm: number
  gapMm: number
  cornerRadiusMm: number
  fontSizePt: number
  /** 0 — сервер подставит `max(4, fontSizePt * 0.6)`; см. `minCellHeightMm`. */
  minFontSizePt: number
  /** 100..900. 0 — умолчание 700: цифру читают через барьер. */
  fontWeight: number
  fontFamily: BadgeFontFamily
  /**
   * Цвет цифры, `#rrggbb`. **Пустая строка запрещена** — невидимая цифра не
   * стиль.
   *
   * ⚠️ Здесь цвет текста ЕСТЬ, а у зон его нет, и это не рассогласование: у
   * плашки зоны фон приходит из справочника и цвет текста вычисляется из него
   * по яркости, а у уровня своего цвета нет нигде — единственный его источник
   * это макет.
   */
  color: string
  /** `#rrggbb` ЛИБО пустая строка — «без рамки». См. шапку: пустая ячейка тогда невидима. */
  borderColor: string
  /** 0..5 мм. 0 при непустом `borderColor` — умолчание 0.2. */
  borderWidthMm: number
  /**
   * `#rrggbb` ЛИБО пустая строка — «без заливки».
   *
   * ⚠️ ЗАЛИВАЕТСЯ ТОЛЬКО ЯЧЕЙКА С ВЫДАННЫМ УРОВНЕМ. Пустая остаётся прозрачной
   * при любом значении: залить её значило бы стереть разницу, ради которой
   * шкала и рисуется.
   */
  fillColor: string
}

export interface BadgeQrProps {
  ecc: BadgeQrEcc
  /**
   * Тихая зона в модулях.
   *
   * ⚠️ Ноль означает «не задано» и превращается сервером в 4. Выключить тихую
   * зону нельзя намеренно: она стоит ~1,5 мм, а её отсутствие — несчитанные
   * коды на входе.
   */
  quietZoneModules: number
  /**
   * ⚠️ Тёмный обязан быть темнее светлого — сервер отвергнет инверсию.
   * Светлый код на тёмном фоне читают не все сканеры, а узнают об этом на
   * напечатанном тираже.
   */
  darkColor: string
  lightColor: string
  /** Печатать 22 символа payload'а под кодом: единственный обходной путь, когда камера не берёт мятый бейдж. */
  showPayload: boolean
  payloadFontSizePt: number
}

export interface BadgeTextProps {
  text: BadgeTextStyle
  /**
   * До 256 символов. Поддерживает подстановки; неизвестная печатается как
   * есть, чтобы опечатка была видна на превью, а не превращалась в пустоту:
   *
   *     {event} {eventDates}
   *     {lastName} {firstName} {middleName} {fullName} {initials} {role}
   */
  content: string
}

export interface BadgeImageProps {
  /** Ключ объекта в нашем хранилище либо `https://…`. Пусто — рамка без картинки. */
  imageKey: string
  fit: BadgeImageFit
}

export interface BadgeShapeProps {
  kind: BadgeShapeKind
}

/**
 * `admin.v1.BadgeFieldProps` — значение дополнительного поля участника на
 * бумаге: «взвешивание пройдено», «размер футболки», «дата аккредитации».
 *
 * ⚠️ **ССЫЛКА ПО ID, А НЕ ПО НАЗВАНИЮ.** Название переименовывают
 * («Взвешивание» → «Взвешивание пройдено»), и макет, привязанный к имени, молча
 * перестал бы находить значение — то есть напечатал бы пустоту вместо отметки,
 * о которой спрашивают на входе.
 *
 * ⚠️ **ПОЛЕ МОГЛИ УДАЛИТЬ ИЗ СПРАВОЧНИКА, И ШАБЛОН ЭТО ПЕРЕЖИВЁТ.** Сервер
 * существование поля на уровне документа НЕ проверяет (шаблон живёт дольше
 * конструктора), поэтому рендер обязан пережить `participantFieldId`, которому
 * ничего не соответствует, и напечатать `emptyText` — ровно так же, как для
 * поля без значения. Различать «поле удалили» и «значение не заполнено» на
 * бумаге незачем: оператору на стойке важно только то, что печатать нечего.
 *
 * Форматирование значения в строку делает `formatFieldValue` из
 * `lib/participant-field-value.ts` — общее с формой, списком и сканером. Второй
 * копии быть не должно: бейдж, печатающий дату иначе, чем экран, — это
 * расхождение, которое замечают уже на бумаге.
 */
export interface BadgeFieldProps {
  /** UUID дополнительного поля ЭТОГО мероприятия. */
  participantFieldId: string
  text: BadgeTextStyle
  /**
   * Печатать «Название поля: значение» вместо голого значения.
   *
   * Выключено по умолчанию: на бейдже место дорогое, а «Размер футболки: L»
   * читается не лучше, чем «L», когда рядом на макете уже стоит подпись.
   */
  showLabel: boolean
  /**
   * Что печатать для `CHECKBOX`. Тексты, а не «да/нет», потому что галочка на
   * бейдже почти никогда не значит слова «да»: она значит «взвешен»,
   * «оплачено», «допущен». До 64 символов каждый.
   *
   * `checkedText` пустой — печатается «✓»; так галочка работает сразу, без
   * настройки. `uncheckedText` пустой — не печатается **ничего**, и эта
   * асимметрия намеренная: отсутствие строки читается как «отметки нет», а
   * напечатанное «нет» человек на двери читает как отказ.
   */
  checkedText: string
  uncheckedText: string
  /** Формат для `DATE`/`DATETIME`, до 32 символов. Пусто — ДД.ММ.ГГГГ и ДД.ММ.ГГГГ ЧЧ:ММ. */
  dateFormat: string
  /**
   * Что печатать, когда печатать нечего: значения нет, оно пустое, поле
   * неприменимо к роли участника — ИЛИ поле удалено из справочника. Для бумаги
   * все эти случаи одинаковы, поэтому текст один. Пусто — не печатать ничего.
   */
  emptyText: string
}

/* ─────────────────────────── ЭЛЕМЕНТ ────────────────────────────────── */

/** Общая часть элемента — всё, что не зависит от типа. */
export interface BadgeElementBase {
  /**
   * Идентификатор в пределах шаблона, `[A-Za-z0-9_-]{1,64}`. Задаёт КЛИЕНТ.
   *
   * Клиент, а не сервер, потому что элемент существует в редакторе задолго до
   * первого сохранения: у выделения, у истории отмен и у списка слоёв должен
   * быть ключ, и он обязан пережить сохранение — иначе после каждого
   * «Сохранить» редактор терял бы выделение и всю историю.
   */
  id: string
  /** Левый верхний угол и размер, мм, от левого верхнего угла бейджа. */
  xMm: number
  yMm: number
  widthMm: number
  heightMm: number
  /** По часовой стрелке относительно центра, градусы. */
  rotationDeg: number
  /** 0..1. Сервер превращает 0 в 1 — «не рисовать» это `hidden`. */
  opacity: number
  hidden: boolean
  box: BadgeBoxStyle
}

/**
 * `admin.v1.BadgeElement` — размеченное объединение по `type`.
 *
 * ⚠️ ЭТО ИМЕННО ОБЪЕДИНЕНИЕ, А НЕ ОБЪЕКТ С ВОСЕМЬЮ НЕОБЯЗАТЕЛЬНЫМИ ПОЛЯМИ. На
 * проводе это `oneof`: заполнено ровно одно поле настроек, остальных в JSON нет
 * вовсе. Тип здесь повторяет это свойство, и польза от него практическая —
 * `switch (el.type)` в рисовальщике проверяется компилятором на полноту, а
 * «элемент QR, у которого остались настройки текста от прошлой жизни»
 * невыразим.
 *
 * Смена типа в редакторе — это ЗАМЕНА элемента, а не правка поля. Так и должно
 * быть: у QR-кода нет осмысленного перевода настроек в настройки фотографии.
 */
/* ─────────────────────────── СТРАНА ─────────────────────────────────── */

/**
 * `admin.v1.BadgeCountryContent` — что печатать в элементе страны.
 *
 * Закрытый список, а не пара флажков «показывать флаг» / «показывать
 * название»: из двух флажков составимо «не печатать ничего», а на бумаге это
 * неотличимо от неисправности.
 */
export type BadgeCountryContent =
  | 'BADGE_COUNTRY_CONTENT_UNSPECIFIED'
  | 'BADGE_COUNTRY_CONTENT_FLAG'
  | 'BADGE_COUNTRY_CONTENT_FLAG_AND_CODE'
  | 'BADGE_COUNTRY_CONTENT_FLAG_AND_NAME'
  | 'BADGE_COUNTRY_CONTENT_FLAG_AND_SHORT_NAME'
  | 'BADGE_COUNTRY_CONTENT_CODE'
  | 'BADGE_COUNTRY_CONTENT_NAME'
  | 'BADGE_COUNTRY_CONTENT_SHORT_NAME'

/** Порядок для выпадающего списка. `UNSPECIFIED` в него не входит. */
export const BADGE_COUNTRY_CONTENTS = [
  'BADGE_COUNTRY_CONTENT_FLAG_AND_CODE',
  'BADGE_COUNTRY_CONTENT_FLAG_AND_SHORT_NAME',
  'BADGE_COUNTRY_CONTENT_FLAG_AND_NAME',
  'BADGE_COUNTRY_CONTENT_FLAG',
  'BADGE_COUNTRY_CONTENT_CODE',
  'BADGE_COUNTRY_CONTENT_SHORT_NAME',
  'BADGE_COUNTRY_CONTENT_NAME',
] as const satisfies readonly BadgeCountryContent[]

export const BADGE_COUNTRY_CONTENT_LABELS: Record<BadgeCountryContent, string> = {
  BADGE_COUNTRY_CONTENT_UNSPECIFIED: 'Не указано',
  BADGE_COUNTRY_CONTENT_FLAG: 'Только флаг',
  BADGE_COUNTRY_CONTENT_FLAG_AND_CODE: 'Флаг и код',
  BADGE_COUNTRY_CONTENT_FLAG_AND_NAME: 'Флаг и название',
  BADGE_COUNTRY_CONTENT_FLAG_AND_SHORT_NAME: 'Флаг и короткое название',
  BADGE_COUNTRY_CONTENT_CODE: 'Только код',
  BADGE_COUNTRY_CONTENT_NAME: 'Только название',
  BADGE_COUNTRY_CONTENT_SHORT_NAME: 'Только короткое название',
}

/** Содержимое, в котором есть флаг. */
export function badgeCountryContentHasFlag(content: BadgeCountryContent): boolean {
  return (
    content === 'BADGE_COUNTRY_CONTENT_UNSPECIFIED' ||
    content === 'BADGE_COUNTRY_CONTENT_FLAG' ||
    content === 'BADGE_COUNTRY_CONTENT_FLAG_AND_CODE' ||
    content === 'BADGE_COUNTRY_CONTENT_FLAG_AND_NAME' ||
    content === 'BADGE_COUNTRY_CONTENT_FLAG_AND_SHORT_NAME'
  )
}

/** Содержимое, в котором есть текст. */
export function badgeCountryContentHasText(content: BadgeCountryContent): boolean {
  return content !== 'BADGE_COUNTRY_CONTENT_FLAG'
}

/** `admin.v1.BadgeCountryFlagPosition` — с какой стороны от текста флаг. */
export type BadgeCountryFlagPosition =
  | 'BADGE_COUNTRY_FLAG_POSITION_UNSPECIFIED'
  | 'BADGE_COUNTRY_FLAG_POSITION_LEFT'
  | 'BADGE_COUNTRY_FLAG_POSITION_RIGHT'
  | 'BADGE_COUNTRY_FLAG_POSITION_TOP'
  | 'BADGE_COUNTRY_FLAG_POSITION_BOTTOM'

export const BADGE_COUNTRY_FLAG_POSITIONS = [
  'BADGE_COUNTRY_FLAG_POSITION_LEFT',
  'BADGE_COUNTRY_FLAG_POSITION_RIGHT',
  'BADGE_COUNTRY_FLAG_POSITION_TOP',
  'BADGE_COUNTRY_FLAG_POSITION_BOTTOM',
] as const satisfies readonly BadgeCountryFlagPosition[]

export const BADGE_COUNTRY_FLAG_POSITION_LABELS: Record<BadgeCountryFlagPosition, string> = {
  BADGE_COUNTRY_FLAG_POSITION_UNSPECIFIED: 'Не указано',
  BADGE_COUNTRY_FLAG_POSITION_LEFT: 'Слева',
  BADGE_COUNTRY_FLAG_POSITION_RIGHT: 'Справа',
  BADGE_COUNTRY_FLAG_POSITION_TOP: 'Сверху',
  BADGE_COUNTRY_FLAG_POSITION_BOTTOM: 'Снизу',
}

/** `admin.v1.BadgeCountryProps`. */
export interface BadgeCountryProps {
  /**
   * UUID поля типа `COUNTRY` этого мероприятия.
   *
   * У мероприятия может быть и «страна», и «страна клуба», поэтому макет
   * обязан сказать, которую печатает.
   */
  participantFieldId: string
  content: BadgeCountryContent
  flagPosition: BadgeCountryFlagPosition
  text: BadgeTextStyle
  /**
   * Высота флага, мм. **0 — по высоте строки текста, и это самое частое
   * значение.**
   *
   * ⚠️ Ноль здесь НЕ «не задано». Флаг, привязанный к кеглю, остаётся на месте
   * после автоподбора размера шрифта; флаг в фиксированных миллиметрах после
   * подбора повисает отдельно от текста. Не подставляйте сюда умолчание.
   */
  flagHeightMm: number
  /** Промежуток между флагом и текстом, мм. */
  gapMm: number
  /**
   * Волосяной контур вокруг флага.
   *
   * Не украшение: флаги Японии, Финляндии и ещё десятка стран по краям белые, и
   * на белом бейдже без контура от них остаётся только середина. Пустой цвет
   * или нулевая толщина — контура нет.
   */
  flagOutlineColor: string
  flagOutlineWidthMm: number
  /**
   * Что печатать, когда печатать нечего: страна не проставлена, поле
   * неприменимо роли, поле удалено — или код есть, а страны в справочнике уже
   * нет. Для бумаги это один случай. Пусто — не печатать ничего.
   */
  emptyText: string
}

/**
 * `admin.v1.BadgePictogramsProps` — ряд пиктограмм участника.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ КВАДРАТОВ СТОЛЬКО, СКОЛЬКО ВЫДАНО РОЛИ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Это ПРОТИВОПОЛОЖНОСТЬ шкале уровней, и причина в том, ЧЕМ различают ячейку.
 * Цифру различают по позиции — отсюда пустые ячейки шкалы, которые и есть её
 * содержание. Картинку различают по ней самой: пустой квадрат рядом с вилкой не
 * сообщает ничего, кроме потраченного места.
 *
 *     Справочник: обед, автобус, парковка, гостиница
 *     «Спортсмен» → [обед][автобус][гостиница]
 *     «Пресса»    → [обед]
 *     «Гость»     → элемент не рисуется вовсе
 *
 * Отсюда два следствия, и оба — договор, а не побочный эффект:
 *
 *   • ширина элемента зависит от УЧАСТНИКА. Роль без пиктограмм не рисует
 *     ничего — ни рамки, ни заглушки: пустая рамка на бейдже гостя читается как
 *     «что-то не напечаталось»;
 *   • подбор идёт по СТОРОНЕ КВАДРАТА, а не по кеглю. Не влезающие квадраты
 *     ужимаются до `minCellSizeMm`, и только ниже него клиент предупреждает.
 *
 * ⚠️ РАСКЛАДКА ОДНА — ГОРИЗОНТАЛЬНЫЙ РЯД. Поля `layout` здесь нет, в отличие от
 * шкалы уровней, и это решение: две строки нужны шкале, потому что её длину
 * задаёт справочник и она может не влезть; здесь длину задаёт роль.
 *
 * ⚠️ НЕТ НИ `maxVisible`, НИ ХВОСТА «+N», в отличие от плашек зон. Скрытая
 * пиктограмма — это не сокращённая надпись, а не выданная привилегия: человек у
 * стойки питания увидит бейдж без вилки и откажет.
 *
 * ⚠️ МАКЕТ С ЭТИМ ЭЛЕМЕНТОМ ХРАНИТСЯ КАК ДОКУМЕНТ ВЕРСИИ `v: 7`
 * (`internal/data/badgedoc.go`). Версия проставляется по СОДЕРЖИМОМУ: пока
 * такого элемента в макете нет, документ остаётся на прежней версии, и откат
 * развёртывания его не ломает.
 */
export interface BadgePictogramsProps {
  /**
   * Желаемая СТОРОНА квадрата, мм, и нижняя граница подбора.
   *
   * Одно число на обе стороны: ячейка квадратная по построению, а иконка
   * вписывается в неё в своих пропорциях — растянутая пиктограмма это другая
   * пиктограмма.
   *
   * ⚠️ `minCellSizeMm === 0` — это «сервер подставит `max(2.5, cellSizeMm *
   * 0.6)`», а не «уменьшать сколько угодно». Клиент обязан подставлять то же
   * самое при отрисовке, иначе превью и сохранённый макет разойдутся.
   */
  cellSizeMm: number
  minCellSizeMm: number
  gapMm: number
  cornerRadiusMm: number
  /**
   * Отступ картинки от края квадрата, мм. Ноль — законное значение, картинка в
   * край; он же правильный, когда у квадрата нет ни рамки, ни заливки.
   */
  iconPaddingMm: number
  /** `#rrggbb` ЛИБО пустая строка — «без рамки». Рамка всегда вокруг ячейки. */
  borderColor: string
  /** 0..5 мм. 0 при непустом `borderColor` — умолчание 0.2. */
  borderWidthMm: number
  /**
   * `#rrggbb` ЛИБО пустая строка — «без заливки».
   *
   * Заливаются ВСЕ нарисованные квадраты, в отличие от шкалы уровней: там
   * заливка отличала выданное от невыданного, а сюда невыданное не попадает.
   */
  fillColor: string
  /**
   * ⚠️ Три поля ниже описывают ЗАПАСНУЮ БУКВУ — первую букву названия, которая
   * печатается, когда у выданной пиктограммы нет иконки.
   *
   * Это НЕ путь ошибки: пиктограмма без иконки — законная запись справочника
   * (объектное хранилище в системе необязательно), и выданная привилегия обязана
   * остаться на карточке видимой. Заглушка та же, что рисует экран, — бумага и
   * экран не расходятся.
   *
   * Своего КЕГЛЯ у буквы нет намеренно: она занимает квадрат так же, как занял
   * бы значок, и отдельная настройка размера дала бы букву, не совпадающую по
   * величине с соседними картинками.
   */
  fontWeight: number
  fontFamily: BadgeFontFamily
  /** Цвет буквы, `#rrggbb`. **Пустая строка запрещена**: невидимая буква — потерянная привилегия. */
  color: string
  /**
   * К какому краю прижат ряд внутри прямоугольника элемента.
   *
   * ⚠️ ЗДЕСЬ ЭТО НУЖНЕЕ, ЧЕМ ГДЕ-ЛИБО ЕЩЁ, потому что ширина ряда зависит от
   * УЧАСТНИКА: у роли с одной пиктограммой это один квадрат, у роли с четырьмя —
   * четыре. Без выравнивания ряд можно прижать только к центру, и элемент,
   * поставленный в угол рядом с QR-кодом, у половины пачки съезжал бы от него.
   *
   * ⚠️ `UNSPECIFIED` ЗДЕСЬ — ЦЕНТР, а не левый край, в отличие от
   * `BadgeTextStyle.align`. Расхождение намеренное: у текста левый край — это
   * как читают строку, а у ряда квадратов переменной ширины центр — положение,
   * при котором соседние бейджи выглядят свёрстанными одинаково. Клиент обязан
   * трактовать пустое значение так же, иначе превью разойдётся с печатью.
   */
  align: BadgeTextAlign
}

export type BadgeElement =
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_PHOTO', photo: BadgePhotoProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_NAME', name: BadgeNameProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_ROLE', role: BadgeRoleProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_ZONES', zones: BadgeZonesProps })
  | (BadgeElementBase & {
    type: 'BADGE_ELEMENT_TYPE_ACCESS_LEVELS',
    accessLevels: BadgeAccessLevelsProps,
  })
  | (BadgeElementBase & {
    type: 'BADGE_ELEMENT_TYPE_PICTOGRAMS',
    pictograms: BadgePictogramsProps,
  })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_QR', qr: BadgeQrProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_TEXT', text: BadgeTextProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_IMAGE', image: BadgeImageProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_SHAPE', shape: BadgeShapeProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_FIELD', field: BadgeFieldProps })
  | (BadgeElementBase & { type: 'BADGE_ELEMENT_TYPE_COUNTRY', country: BadgeCountryProps })

/* ─────────────────────────── ШАБЛОН ─────────────────────────────────── */

/** `admin.v1.BadgeTemplate`. */
export interface BadgeTemplate {
  /** Мероприятие. Оно же ключ: собственного id у шаблона нет. */
  eventId: string
  /** Человеческое название вёрстки: «A6 вертикальный». Может быть пустым. */
  name: string
  /**
   * Размер карточки, мм. 20..300 каждая сторона.
   *
   * Ориентация НЕ хранится отдельно: она и есть отношение этих двух чисел.
   * Флаг «портрет/альбом» рядом с ними был бы вторым источником правды,
   * расходящимся с первым при перестановке ширины и высоты.
   */
  widthMm: number
  heightMm: number
  /** `#rrggbb`, пустым не бывает: у листа картона всегда есть цвет. */
  backgroundColor: string
  /**
   * Подложка: ключ объекта (`events/…/badge/…`) либо `https://…`. Пусто — нет.
   *
   * ⚠️ Это свойство ХОЛСТА, а не элемент. Подложка обязана покрывать бейдж
   * целиком и лежать под всем остальным; будь она элементом, её можно было бы
   * подвинуть, уменьшить и накрыть ею QR-код — и обнаружилось бы это на
   * напечатанной пачке. Логотипы и полоски — это элементы `IMAGE`, им
   * двигаться можно.
   */
  backgroundImageKey: string
  backgroundFit: BadgeImageFit
  cornerRadiusMm: number
  /**
   * Безопасное поле, мм. ⚠️ Это ПОДСКАЗКА РЕДАКТОРА, а не ограничение: сервер
   * не двигает и не отвергает элементы, залезшие в поле, потому что макеты, где
   * цветная плашка обязана доходить до края, — обычное дело.
   */
  safeMarginMm: number
  /** Вылет под обрез, мм. 0 для офисного принтера. */
  bleedMm: number
  /**
   * Элементы в порядке отрисовки: первый в списке лежит ниже всех.
   *
   * ⚠️ ПОРЯДОК В МАССИВЕ — ЭТО И ЕСТЬ Z-ПОРЯДОК. Отдельного поля `z` нет:
   * два источника порядка расходятся при первой же вставке в середину, и тогда
   * «поднять на передний план» начинает работать через раз.
   */
  elements: BadgeElement[]
  /**
   * Оборотная сторона — или её отсутствие.
   *
   * ⚠️ ОТСУТСТВИЕ ПОЛЯ И ПУСТОЙ ОБОРОТ — РАЗНЫЕ ВЕЩИ, и разница измеряется в
   * бумаге. Пустой оборот печатается (цветной картон, место для подписи,
   * плашка спонсора), отсутствующий не печатается вовсе. Поэтому здесь
   * необязательное поле, а не `back: BadgeBackSide` с пустым значением по
   * умолчанию: второе означало бы вдвое больше листов у каждого мероприятия,
   * которое про оборот не спрашивало.
   *
   * Отдельного признака «двусторонний» нет по той же причине, по которой нет
   * флага ориентации рядом с размерами: два источника правды расходятся.
   *
   * ⚠️ `null` в типе не для красоты: сервер сериализует отсутствующее сообщение
   * протокола именно как `"back": null`, а не пропуском ключа. Проверять надо
   * ПРАВДИВОСТЬ значения (`if (template.back)`), а не наличие поля — `'back' in
   * template` вернёт true у каждого одностороннего макета.
   */
  back?: BadgeBackSide | null
  createdAt: string
  updatedAt: string
}

/**
 * `admin.v1.BadgeBackSide` — оборот карточки.
 *
 * ⚠️ ЗДЕСЬ НЕТ НИ РАЗМЕРА, НИ СКРУГЛЕНИЯ, НИ ПОЛЕЙ. Оборот печатается на
 * обратной стороне того же куска картона: ширина, высота, скругление,
 * безопасное поле и вылет у него физически те же, что у лицевой. Второй набор
 * этих полей допускал бы оборот на два миллиметра шире лицевой — состояние,
 * законное в типе и невозможное на бумаге.
 *
 * Своё у оборота ровно то, что можно нарисовать иначе: фон и элементы.
 */
export interface BadgeBackSide {
  /** `#rrggbb`. Пустая строка означает `#ffffff`, как и у лицевой. */
  backgroundColor: string
  /** Ключ объекта либо `https://…`. Пусто — подложки нет. */
  backgroundImageKey: string
  backgroundFit: BadgeImageFit
  /**
   * Элементы оборота в порядке отрисовки, максимум 40 — свой запас, отдельный
   * от лицевой.
   *
   * ⚠️ Идентификаторы уникальны в пределах ВСЕГО документа, а не стороны:
   * редактор держит одно выделение на обе стороны, а `clipPath` в SVG печати
   * адресуется по id глобально.
   */
  elements: BadgeElement[]
}

/**
 * `GET /api/v1/events/{eventId}/badge-template` — читает ЛЮБОЙ участник.
 *
 * ⚠️ `404 BADGE_TEMPLATE_NOT_FOUND` — ШТАТНОЕ состояние только что заведённого
 * мероприятия, а не ошибка: на него интерфейс показывает галерею заготовок.
 * Тост об ошибке здесь был бы враньём. Стор поэтому держит `template: null` и
 * отдельно `notFound: true`, а не кладёт этот ответ в `error`.
 */
export interface GetBadgeTemplateResponse {
  badgeTemplate: BadgeTemplate
}

/**
 * `PUT /api/v1/events/{eventId}/badge-template` — записать ЦЕЛИКОМ.
 *
 * PUT, а не PATCH, и это не стилистика: шаблон — один документ, который правят
 * на одном экране. Частичное обновление означало бы слияние двух вёрсток на
 * сервере — операцию, у которой нет правильного результата, когда два человека
 * двигали разные элементы. Здесь выигрывает последний записавший.
 *
 * Создание и изменение — одна операция: собственного id у шаблона нет.
 */
export interface SetBadgeTemplateRequest {
  name: string
  widthMm: number
  heightMm: number
  backgroundColor: string
  backgroundImageKey: string
  backgroundFit: BadgeImageFit
  cornerRadiusMm: number
  safeMarginMm: number
  bleedMm: number
  /** Максимум 40. Пустой список законен: бейдж из одной подложки бывает. */
  elements: BadgeElement[]
  /**
   * Оборот — или ничего.
   *
   * ⚠️ PUT записывает документ ЦЕЛИКОМ, поэтому отправка без этого поля — это
   * и есть «сделать бейдж односторонним». Отдельной операции «удалить оборот»
   * нет и не нужно.
   */
  back?: BadgeBackSide
}

export interface SetBadgeTemplateResponse {
  /**
   * Записанный шаблон — уже НОРМАЛИЗОВАННЫЙ сервером (цвета в нижнем регистре,
   * умолчания подставлены). Класть в стор надо ЕГО, а не то, что отправили,
   * иначе редактор и сервер разойдутся в первом же умолчании.
   */
  badgeTemplate: BadgeTemplate
}

export interface DeleteBadgeTemplateResponse {
  /** Удалённый шаблон — последнее, что о нём известно. */
  badgeTemplate: BadgeTemplate
}

/* ──────────────────── БИБЛИОТЕКА СОХРАНЁННЫХ МАКЕТОВ ─────────────────── */

/**
 * `admin.v1.BadgeLibraryTemplate` — сохранённый макет из ОБЩЕЙ библиотеки.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ЭТО ЗАГОТОВКА, А НЕ ВТОРОЙ ШАБЛОН МЕРОПРИЯТИЯ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Правило «один макет на мероприятие» не отменяется. Строка отсюда — это
 * ровно то же, чем всегда были заготовки в `lib/badge/presets.ts`, только
 * написанная живым организатором и лежащая на сервере: её ПРИМЕНЯЮТ, то есть
 * перезаписывают ею единственный макет своего мероприятия, и после этого связи
 * между ними нет.
 *
 * ⚠️ ПОЛЯ ВЁРСТКИ СОВПАДАЮТ С `BadgeTemplate` ПО ИМЕНАМ, НО ЭТО ДРУГОЙ ТИП, и
 * подменять один другим нельзя. У шаблона есть `id` и нет `eventId`, потому
 * что он не принадлежит мероприятию; ключи его картинок лежат под
 * `badge-library/…`, и эндпоинт мероприятия их не отдаст. Совпадение имён
 * нужно ровно для одного — чтобы `draftToTemplate`-подобная сборка и
 * рисовальщик работали с обоими без ветвлений.
 */
export interface BadgeLibraryTemplate {
  /** Собственный id. Он же — сегмент владельца в ключах картинок шаблона. */
  id: string
  /**
   * Название в галерее. Обязательное и уникальное на всю систему без учёта
   * регистра — в отличие от `BadgeTemplate.name`, которое может быть пустым:
   * макет мероприятия называет само мероприятие, а строку в общем списке —
   * больше нечего.
   */
  name: string
  /**
   * Кто сохранил. Именно id: справочник учётных записей открыт только
   * администратору, поэтому имя автора клиенту неоткуда взять и незачем — по
   * этому полю экран решает лишь одно, показывать ли кнопку удаления.
   */
  authorId: string

  widthMm: number
  heightMm: number
  backgroundColor: string
  /** Ключ вида `badge-library/<id>/…` либо внешний `https://…`. */
  backgroundImageKey: string
  backgroundFit: BadgeImageFit
  cornerRadiusMm: number
  safeMarginMm: number
  bleedMm: number
  elements: BadgeElement[]
  back?: BadgeBackSide

  createdAt: string
  updatedAt: string
}

/**
 * `admin.v1.BadgeLibraryDroppedElement` — элемент, не переживший применение.
 *
 * ⚠️ ЭТО ОТЧЁТ, А НЕ ОШИБКА. Элементы «поле» и «страна» ссылаются на
 * дополнительное поле конкретного мероприятия; при применении они ищутся в
 * конструкторе целевого ПО НАЗВАНИЮ, и то, чему пары не нашлось, из макета
 * удаляется. Применение при этом состоялось. Экран обязан назвать удалённое
 * вслух: иначе ответ на вопрос «а куда делся клуб?» человек получит с
 * напечатанной пачки.
 *
 * ⚠️ НЕОДНОЗНАЧНОЕ НАЗВАНИЕ НЕ РЕЗОЛВИТСЯ ВОВСЕ — ЭЛЕМЕНТ ПОПАДАЕТ СЮДА.
 * Название поля уникально в РОЛИ, а не в мероприятии (см.
 * `ParticipantField.name`), поэтому «Взвешивание» в целевом мероприятии может
 * оказаться и полем спортсмена, и полем тренера. Сервер в этом случае не
 * выбирает: он отбрасывает элемент и называет его здесь.
 *
 * Так решено намеренно. Отбрасывание ЗАМЕТНО — про него написано в этом
 * отчёте, — а привязка «наугад» молчит: половина бейджей напечаталась бы
 * пустой, и узнали бы об этом с готовой пачки. Минута на то, чтобы поставить
 * элемент руками, дешевле тиража.
 */
export interface BadgeLibraryDroppedElement {
  /** Id элемента в сохранённом документе. Для разбора обращений, не для показа. */
  elementId: string
  /** Только `FIELD` или `COUNTRY`: больше ничто не ссылается на поле. */
  type: BadgeElementType
  /** Название поля в исходном мероприятии — по нему человек и узнаёт элемент. */
  fieldName: string
  /** Был ли элемент на обороте: сообщение должно сказать, куда смотреть. */
  back: boolean
}

/** `GET /api/v1/badge-template-library` — читает любой аутентифицированный. */
export interface ListBadgeLibraryTemplatesResponse {
  badgeLibraryTemplates: BadgeLibraryTemplate[]
}

/**
 * `POST /api/v1/badge-template-library` — сохранить макет мероприятия. admin
 * или owner ЭТОГО мероприятия.
 *
 * ⚠️ Сохраняется то, что ЛЕЖИТ НА СЕРВЕРЕ, а не черновик на экране. Кнопку
 * поэтому нельзя показывать активной при несохранённых правках: человек нажмёт
 * её, ожидая сохранить то, что видит.
 */
export interface SaveBadgeLibraryTemplateRequest {
  eventId: string
  /** 1..128 символов, уникально без учёта регистра. */
  name: string
}

export interface SaveBadgeLibraryTemplateResponse {
  badgeLibraryTemplate: BadgeLibraryTemplate
}

/**
 * `POST /api/v1/events/{eventId}/badge-template/apply` — взять сохранённый
 * шаблон за основу. admin или owner.
 *
 * ⚠️ ЭТО ПЕРЕЗАПИСЬ. Спрашивать подтверждение обязан клиент: только он знает,
 * смотрит ли человек прямо сейчас на вёрстку, которую потеряет.
 */
export interface ApplyBadgeLibraryTemplateRequest {
  templateId: string
}

export interface ApplyBadgeLibraryTemplateResponse {
  /** Макет мероприятия, каким он стал: нормализованный сервером. В стор — его. */
  badgeTemplate: BadgeTemplate
  /** Элементы, которым в этом мероприятии не нашлось пары. Обычно пусто. */
  dropped: BadgeLibraryDroppedElement[]
  /** Сколько картинок скопировано в мероприятие. Внешние ссылки не считаются. */
  copiedAssets: number
}

export interface DeleteBadgeLibraryTemplateResponse {
  badgeLibraryTemplate: BadgeLibraryTemplate
}

/**
 * `admin.v1.BadgeAsset` — загруженная картинка бейджа.
 *
 * ⚠️ У ЗАГРУЗКИ И ОТДАЧИ КАРТИНОК НЕТ RPC и нет их в `openapi.yaml`: и вверх, и
 * вниз едут байты. Маршруты написаны руками и проходят ту же цепочку
 * аутентификации:
 *
 *     POST /api/v1/events/{eventId}/badge-assets         multipart, поле `file`
 *     GET  /api/v1/events/{eventId}/badge-assets/{name}  сами байты
 *
 * Второй требует `Authorization: Bearer`, поэтому `<img src>` на него навести
 * нельзя — байты забираются axios'ом и заворачиваются в data-URL. Точно та же
 * история, что с фотографиями участников (`useParticipantPhoto`), и здесь она
 * даже строже: документ печати обязан быть самодостаточным.
 */
export interface BadgeAsset {
  /** Ключ, который кладётся в шаблон: `events/<eventId>/badge/<uuid>.<ext>`. */
  key: string
  /** `image/jpeg` либо `image/png` — выбирается сервером ПО ПИКСЕЛЯМ, а не по тому, что прислали. */
  contentType: string
  bytes: number
  /** Размеры сохранённой картинки: редактор ставит логотип в его собственных пропорциях. */
  width: number
  height: number
}

/* ═════════════════════════════════════════════════════════════════════════
   8. КОНСТРУКТОР ФОРМЫ УЧАСТНИКА — `ParticipantFieldService`
   ═════════════════════════════════════════════════════════════════════════

   Это то, что в scan-backend было тремя жёстко зашитыми колонками — «прошёл
   взвешивание», «оплатил взнос», «предоставил справку». Здесь их нет в схеме:
   организатор сам заводит нужные ему поля, потому что на одних соревнованиях
   взвешивания нет, а на других есть страховка и допуск врача.

   ⚠️ СЛЕДСТВИЕ ДЛЯ ФРОНТА, КОТОРОЕ ПРОНИЗЫВАЕТ ВЕСЬ ЭКРАН УЧАСТНИКОВ: колонки
   таблицы, поля формы, фильтры и столбцы шаблона импорта **строятся в рантайме**
   из ответа `ListParticipantFields`. Ни одного захардкоженного названия графы
   в этом фронтенде нет и быть не должно. */

/** `admin.v1.ParticipantField` — определение дополнительного поля. */
export interface ParticipantField {
  id: string
  eventId: string
  /**
   * Название, оно же **заголовок столбца в шаблоне импорта**. Непустое,
   * максимум 128.
   *
   * ⚠️ УНИКАЛЬНО В ПРЕДЕЛАХ ОБЛАСТИ ВИДИМОСТИ, А НЕ МЕРОПРИЯТИЯ. Сравнение
   * без учёта регистра. Правило целиком:
   *
   *   • у одной роли двух одинаковых названий быть не может;
   *   • среди общих полей (`participantRoleId === ''`) — тоже не может;
   *   • общее поле и ролевое с одним названием — тоже нельзя.
   *
   * А вот две РАЗНЫЕ роли своё «Взвешивание» иметь могут, и это законно: у
   * спортсмена и у тренера это разные графы с разными значениями, запрет здесь
   * означал бы гонку организаторов за свободными названиями.
   *
   * Третий пункт — не придирка, и именно он держит импорт. Из него следует,
   * что у ЛЮБОГО КОНКРЕТНОГО участника (поля его роли плюс общие,
   * `fieldsForRole()`) все названия по-прежнему различны — а импорт
   * сопоставляет столбец файла с полем по заголовку именно в этом наборе, роль
   * строки уже известна. Без запрета участник с этой ролью увидел бы в форме
   * две одинаковые графы, а строка файла — две строки значений.
   *
   * ⚠️ СЛЕДСТВИЕ ДЛЯ UI: в списке ПОПЕРЁК РОЛЕЙ (привязка элемента бейджа,
   * шапки колонок таблицы участников и предпросмотра импорта, заголовки групп
   * предупреждений) одного названия уже недостаточно — два пункта
   * «Взвешивание» неразличимы. Подпись для таких мест собирает
   * `lib/participant-field-label.ts`: роль приписывается ТОЛЬКО к
   * неоднозначному названию.
   *
   * Отказ при сохранении — `409 PARTICIPANT_FIELD_ALREADY_EXISTS`, и он
   * приезжает в трёх разновидностях; какая именно, говорит
   * `metadata.conflict_scope` (см. таблицу `metadata` ниже).
   */
  name: string
  type: ParticipantFieldType
  /**
   * Подсвечивать это поле при сканировании QR-кода участника.
   *
   * Сервер только хранит флаг — как именно подсветить, решает клиент. Смысл
   * флага в том, что на входе важны две-три графы из десяти, и человеку на
   * двери нужно увидеть именно их, не читая всю карточку.
   */
  highlightOnScan: boolean
  /**
   * Роль участника, к которой привязано поле. **Пустая строка — «применимо ко
   * всем»**, включая участников вообще без роли.
   *
   * ⚠️ ЭТО НЕ «РОЛЬ НЕИЗВЕСТНА». Пустое значение — полноценный смысл, и на нём
   * держится вся фича: спортсмен проходит взвешивание и платит взнос, гость —
   * ничего из этого, но бейдж получают оба. «Бейдж выдан» — поле без привязки,
   * «взвешивание» — поле роли «спортсмен».
   *
   * Прочтение пустой строки как «ни к кому не относится» отключило бы каждое
   * поле, заведённое до появления привязок.
   *
   * Отбор делает `fieldsForRole()` — не сравнивайте вручную.
   */
  participantRoleId: string

  /**
   * Варианты для `SELECT` и **только для него** — у остальных типов `[]`.
   *
   * Непустой список, не более `LIMITS.participantFieldOptionsMax` значений,
   * каждое непустое и не длиннее `LIMITS.participantFieldOptionMax`,
   * уникальные без учёта регистра. Прислать варианты у галочки —
   * `VALIDATION_FAILED`.
   *
   * ⚠️ ВАРИАНТ МОГ ИСЧЕЗНУТЬ ИЗ СПИСКА ПОСЛЕ ТОГО, КАК ЕГО ВЫБРАЛИ. Сервер
   * разрешает удалять варианты (иначе опечатку в справочнике на тысячу
   * человек нельзя было бы исправить вообще), и значение у участника остаётся
   * как есть. Значит, `textValue`, которого нет в `options`, — это штатное
   * состояние, а не рассинхрон: UI показывает его как «неизвестный вариант»,
   * а не как пустоту.
   */
  options: string[]

  /**
   * Обязательно ли поле в форме.
   *
   * Для `CHECKBOX` «обязательно» означает «галочка должна стоять» (согласие с
   * регламентом), а не «поле должно быть упомянуто».
   *
   * ⚠️ При ИМПОРТЕ обязательность не роняет строку — сервер даёт warning:
   * файл федерации неполон по определению, и уронить из-за этого четыреста
   * человек было бы худшим из возможных ответов.
   */
  required: boolean

  /**
   * Порядок в форме и в столбцах шаблона импорта. Меньше — выше.
   *
   * ⚠️ ЗНАЧЕНИЕ НЕ УНИКАЛЬНО И НЕ ОБЯЗАНО БЫТЬ ПЛОТНЫМ: сортировка на сервере
   * добирает `createdAt` вторым ключом (поля, заведённые до появления
   * `position`, приехали с нулём). Поэтому сортировать список на клиенте ПО
   * ОДНОМУ `position` нельзя — порядок таких полей запрыгал бы. Список уже
   * приходит отсортированным; его просто не трогают.
   */
  position: number

  /** Подсказка под полем в форме. `''` — подсказки нет. Максимум 256. */
  description: string

  createdAt: string
  updatedAt: string
}

/**
 * Указывает ли `photoUrl` на НАШЕ объектное хранилище, а не на чужой сервер.
 *
 * В колонке живут две формы:
 *
 *     https://…    внешняя ссылка, вписанная руками. Отдаём её `<img>` как есть
 *                  — байтов у нас нет;
 *     events/…     ключ объекта, записанный загрузкой. Забирается через
 *                  `.../participants/{id}/photo` с токеном.
 *
 * ⚠️ Это правило ДОСЛОВНО повторяет `biz.IsStoredPhotoKey` на сервере, и они
 * обязаны совпадать: разойдутся — фронт попросит у бэкенда чужую ссылку и
 * получит 404 там, где фотография на самом деле есть.
 */
export function isStoredPhotoKey(photoUrl: string): boolean {
  const v = photoUrl.trim()
  return v !== '' && !v.includes('://') && v.startsWith('events/')
}

/**
 * Версия хранимой фотографии — то, что делает её адрес РАЗНЫМ после замены.
 *
 * ⚠️ БЕЗ ЭТОГО ФОТОГРАФИЯ НЕ ОБНОВЛЯЕТСЯ У ДРУГИХ. Байты лежат за адресом
 * `.../participants/{id}/photo`, который сам по себе не меняется никогда, а
 * сервер отдаёт их с `Cache-Control: immutable` на сутки. Браузер того, кто
 * однажды открыл карточку, после этого не ходит в сеть вообще — и продолжает
 * показывать своё старое фото, хотя в хранилище давно лежит чужое новое.
 *
 * Ключ содержит свежий uuid на каждую загрузку (`biz.UploadPhoto`), поэтому
 * версия берётся из имени файла: сменилась фотография — сменился адрес — кэш
 * промахнулся. Это единственная инвалидация, которая работает сквозь чужие
 * кэши, и поэтому её обязан подставлять КАЖДЫЙ, кто просит байты.
 *
 * `null` — версии нет (внешняя ссылка или пустое поле); запрашивать нечего.
 */
export function photoVersion(photoUrl: string): string | null {
  if (!isStoredPhotoKey(photoUrl)) return null
  const file = photoUrl.trim().split('/').pop() ?? ''
  const version = file.replace(/\.[^.]*$/, '')
  return version === '' ? null : version
}

/**
 * Поля, применимые к участнику с данной ролью, **в исходном порядке**.
 *
 * ⚠️ ЭТО ФИЛЬТР, А НЕ СОРТИРОВКА, И ЭТО ВАЖНО. Порядок задан сервером
 * (`position`, затем `createdAt` — см. `ParticipantField.position`), и по нему
 * строятся форма, столбцы таблицы, колонки шаблона импорта и колонки выгрузки.
 * Пересортировка здесь — хоть по алфавиту, хоть по одному лишь `position` —
 * развела бы эти четыре экрана между собой, и заметить расхождение можно было
 * бы только сверив файл с таблицей глазами.
 *
 * `roleId` — роль участника: `null`/`''`, если роли нет. Такому участнику
 * достаются только поля без привязки, и это правильно: раз он не назван
 * спортсменом, взвешивать его никто не собирался.
 */
export function fieldsForRole(
  fields: readonly ParticipantField[],
  roleId: string | null | undefined,
): ParticipantField[] {
  return fields.filter((f) => !f.participantRoleId || f.participantRoleId === roleId)
}

/**
 * `GET /api/v1/events/{eventId}/participant-fields` — все поля мероприятия,
 * **в порядке формы**. Читает любой участник: без определений полей нельзя ни
 * нарисовать форму, ни показать список.
 *
 * Порядок — `(position, createdAt)`, и это ЕДИНСТВЕННЫЙ порядок, который
 * существует. По нему строятся и форма, и столбцы шаблона импорта, и столбцы
 * выгрузки, поэтому «сортировка на клиенте» здесь не вариант: три экрана
 * разошлись бы между собой при первом же несовпадении локали. Менять порядок
 * можно только через `reorder` — см. `ReorderParticipantFieldsRequest`.
 */
export interface ListParticipantFieldsResponse {
  participantFields: ParticipantField[]
  total: number
}

export interface CreateParticipantFieldRequest {
  /** Непустое, максимум 128. */
  name: string
  /**
   * ⚠️ Задаётся ОДИН РАЗ: `Update` тип не меняет.
   * `PARTICIPANT_FIELD_TYPE_UNSPECIFIED` сервер читает как `CHECKBOX`, но
   * полагаться на это умолчание не стоит — присылайте выбранное значение.
   */
  type: ParticipantFieldType
  /**
   * Роль, к которой привязывается поле. Пустая строка — применимо ко всем.
   * Роль из ЧУЖОГО мероприятия — `404 PARTICIPANT_ROLE_NOT_FOUND`.
   */
  participantRoleId: string
  highlightOnScan: boolean
  /**
   * Варианты для `SELECT`. Для остальных типов обязан быть **пуст** — иначе
   * `VALIDATION_FAILED`. См. `ParticipantField.options`.
   */
  options: string[]
  /** Обязательность в форме. См. `ParticipantField.required`. */
  required: boolean
  /** Подсказка под полем, максимум 256. Пустая строка — подсказки нет. */
  description: string
}

export interface CreateParticipantFieldResponse {
  participantField: ParticipantField
}

/**
 * `PUT /api/v1/events/{eventId}/participant-fields/{id}` — название и подсветка.
 *
 * ⚠️ **Тип поля не меняется**, и в запросе его нет: у уже проставленных значений
 * нет осмысленного перевода из галочки в текст. Нужен другой тип — заводится
 * другое поле.
 */
export interface UpdateParticipantFieldRequest {
  name: string
  /**
   * ⚠️ Поле НЕ частичное: пустая строка **снимает** привязку, а не «оставляет
   * как было». Присылайте желаемое состояние целиком — иначе правка названия
   * поля «взвешивание» молча сделает его общим для всех.
   */
  participantRoleId: string
  highlightOnScan: boolean
  /**
   * ⚠️ **ПОЛНЫЙ СПИСОК ВАРИАНТОВ, А НЕ ДЕЛЬТА**: присланное значение
   * становится списком целиком. Вариант, исчезнувший из списка, у участников
   * НЕ стирается — значение остаётся как есть, и UI показывает его как
   * «неизвестный вариант». Стирать чужие ответы при правке справочника нельзя:
   * опечатку исправляют одним движением, а восстановить тысячу ответов нечем.
   *
   * Для не-`SELECT` обязан быть пуст.
   */
  options: string[]
  required: boolean
  /** Пустая строка **очищает** подсказку — поле, как и остальные здесь, не частичное. */
  description: string
}

export interface UpdateParticipantFieldResponse {
  participantField: ParticipantField
}

/**
 * `POST /api/v1/events/{eventId}/participant-fields:reorder` — admin или owner.
 *
 * ⚠️ **СПИСОК ОБЯЗАН БЫТЬ ПОЛНЫМ**: ровно все поля мероприятия, каждое ровно
 * один раз, в желаемом порядке. Частичный список, повтор или чужой id —
 * `VALIDATION_FAILED`, а не «переставить только эти».
 *
 * Причина в том, что позиция — это не свойство одного поля, а порядок всего
 * списка. Приняв половину, сервер обязан был бы додумать, куда девать
 * остальных: в начало, в конец или дырами в нумерации. Любой из этих ответов
 * правильный ровно для одного клиента, а расплачивается за него человек, у
 * которого форма перетасовалась сама.
 *
 * Практическое следствие для UI: перетаскивание внутри отфильтрованного
 * списка (например, только полей одной роли) присылать нельзя — собирайте
 * порядок из ПОЛНОГО `items` стора.
 */
export interface ReorderParticipantFieldsRequest {
  fieldIds: string[]
}

/**
 * Список целиком и в новом порядке — тем же, что вернул бы `list()` сразу
 * после. Клиент не собирает результат сам: он и сервер разошлись бы в первом
 * же случае, когда запрос отвергнут.
 */
export interface ReorderParticipantFieldsResponse {
  participantFields: ParticipantField[]
  total: number
}

/**
 * `DELETE /api/v1/events/{eventId}/participant-fields/{id}`.
 *
 * ⚠️ Вместе с полем каскадом уходят **все проставленные по нему значения** у
 * всех участников мероприятия: «удалить графу взвешивания» стирает отметки о
 * взвешивании. Это осознанно — поле, которого больше нет, не может иметь
 * значений, — но подтверждение обязано сказать это словами.
 */
export interface DeleteParticipantFieldResponse {
  participantField: ParticipantField
}

/* ═════════════════════════════════════════════════════════════════════════
   9. УЧАСТНИКИ — `ParticipantService`
   ═════════════════════════════════════════════════════════════════════════

   Участник — это **не учётная запись**: у него нет ни логина, ни пароля, ни
   сессий. Он существует, чтобы попасть в стартовый протокол и быть
   отсканированным на входе. */

/**
 * `admin.v1.ParticipantFieldFile` — приложенный к полю файл, как его видит
 * клиент.
 *
 * ⚠️ **КЛЮЧА В ХРАНИЛИЩЕ ЗДЕСЬ НЕТ, И ЭТО НАМЕРЕННО.** Ключ — внутренний
 * адрес объекта, а единственный способ получить содержимое — наш же эндпоинт
 * `GET .../fields/{fieldId}/file`, который проверяет права на мероприятие.
 * MinIO наружу не опубликован и опубликован быть не должен: приложенная
 * справка — это медицинские данные. Значит, ссылку на файл в разметке
 * СОБРАТЬ НЕЛЬЗЯ — качать только через `participantsApi.fetchFieldFile`.
 */
export interface ParticipantFieldFile {
  /** Исходное имя файла, как его назвал загрузивший. Оно же в `Content-Disposition`. */
  name: string
  /** Размер в байтах. */
  size: number
  /** MIME-тип, каким его определил сервер при загрузке. */
  contentType: string
}

/**
 * `admin.v1.ParticipantFieldValue` — значение одного дополнительного поля.
 *
 * ⚠️ **ОТСУТСТВИЕ СТРОКИ ЗНАЧЕНИЯ == ПУСТО, И ЭТО ВЕРНО ДЛЯ ВСЕХ СЕМИ ТИПОВ.**
 * Запись пустого значения не создаёт строку, а УДАЛЯЕТ её (для `file` — вместе
 * с объектом в хранилище). Только что созданный участник не имеет ни одной
 * строки значений, и это то же самое, что все галочки сняты и все поля пусты.
 * Отсюда два правила, которые нельзя нарушать в UI:
 *
 *   • читать значение только через хелперы, отдающие «пусто» на отсутствие
 *     (`findFieldValue` здесь, `isFieldValueEmpty`/`formatFieldValue` в
 *     `lib/participant-field-value.ts`) — `p.fieldValues.find(...)!.boolValue`
 *     падает на каждом свежесозданном участнике;
 *   • фильтр «кто не заплатил» — это `fieldUnticked`, а не `fieldTicked=false`:
 *     второго на проводе просто нет, и придумать его нельзя.
 *
 * ⚠️ **ЗАПОЛНЕНО РОВНО ОДНО ИЗ ПОЛЕЙ НИЖЕ** — то, которое соответствует типу
 * поля; остальные приезжают нулевыми и НЕ несут смысла. Сервер обнуляет всё
 * неприменимое к типу при записи (`biz.ParticipantFieldValue.NormalizeFor`),
 * поэтому `boolValue: false` у текстового поля означает «здесь не bool», а не
 * «снятая галочка». Выбор нужного поля по типу делает ОДИН модуль —
 * `lib/participant-field-value.ts`, и второй копии этой логики быть не должно.
 *
 * Тип один на обе стороны провода: он же едет в `fieldValues` запросов
 * создания и обновления участника. Собирать его руками не нужно — для этого
 * есть `emptyFieldValue(field)` из того же модуля, который заполняет все ключи
 * разом и не даёт забыть обнулить чужой.
 */
export interface ParticipantFieldValue {
  fieldId: string
  /** Значение для полей типа `CHECKBOX`. */
  boolValue: boolean
  /**
   * Значение для `TEXT`, `TEXTAREA` и `SELECT`. Максимум
   * `LIMITS.participantFieldTextMax` символов.
   *
   * Для `SELECT` это **текст** выбранного варианта, а не его индекс: индекс
   * сломался бы при первой же перестановке списка, и «Мужской» задним числом
   * стал бы «Женский». Текста может уже не быть в `options` — см.
   * `ParticipantField.options`.
   */
  textValue: string
  /**
   * Значение для `DATE` и `DATETIME`, RFC3339 (`Timestamp` ⇒ **`null`**, когда
   * не заполнено).
   *
   * ⚠️ Для `DATE` время всегда `00:00:00 UTC` — сервер его обнуляет. Показывать
   * такую дату локальными компонентами НЕЛЬЗЯ: в отрицательных таймзонах
   * «01.01.2000 00:00 UTC» превратится в 31 декабря. Разбор и сборку делает
   * `lib/participant-field-value.ts`, и только он.
   */
  dateValue: string | null
  /** Описание приложенного файла для `FILE`. `null`, когда файла нет. */
  file: ParticipantFieldFile | null
}

/**
 * ⚠️ **ТОЛЬКО ДЛЯ `CHECKBOX`.** Отмечено ли поле, с правильной трактовкой
 * отсутствия строки значения.
 *
 * Живёт здесь, а не в `lib/`, намеренно: это часть прочтения контракта, а не
 * форматирование. У поля любого другого типа вернёт `false` — потому что
 * сервер обнуляет неприменимое к типу, и `boolValue` текстового поля не значит
 * ничего. Для остальных типов есть `isFieldValueEmpty` и `formatFieldValue` из
 * `lib/participant-field-value.ts`.
 */
export function fieldValueOf(values: readonly ParticipantFieldValue[], fieldId: string): boolean {
  return values.find((value) => value.fieldId === fieldId)?.boolValue ?? false
}

/**
 * Строка значения поля у участника, либо `undefined`, если её нет.
 *
 * `undefined` здесь — полноценный ответ «поле не заполнено», а не сбой (см.
 * шапку `ParticipantFieldValue`), поэтому все читатели значения принимают
 * `v?: ParticipantFieldValue` и умеют работать без него.
 */
export function findFieldValue(
  values: readonly ParticipantFieldValue[],
  fieldId: string,
): ParticipantFieldValue | undefined {
  return values.find((value) => value.fieldId === fieldId)
}

/** `admin.v1.Participant`. */
export interface Participant {
  /** UUIDv4. Он же — источник `qrPayload`. */
  id: string
  eventId: string
  /** Фамилия. Непустая, максимум 128. */
  lastName: string
  /** Имя. Непустое, максимум 128. */
  firstName: string
  /** Отчество. **Скаляр** ⇒ `''` у тех, у кого его нет (не `null`). Максимум 128. */
  middleName: string
  /**
   * Роль из справочника мероприятия. Пустая строка — роль не назначена.
   *
   * Роль **необязательна** намеренно: файл, который присылает федерация, часто
   * содержит только ФИО, и требование роли остановило бы импорт целиком ради
   * сведения, которое проставят потом.
   */
  participantRoleId: string
  /** Развёрнутая роль, чтобы список не требовал второго запроса. `null`, если роль не назначена. */
  participantRole: ParticipantRole | null
  /** Ссылка на фотографию. Пустая строка — фотографии нет. Максимум 1024. */
  photoUrl: string
  status: ParticipantStatus
  /** Выдана ли аккредитация. Отдельно от `status` — см. `AccreditationStatus`. */
  accreditationStatus: AccreditationStatus
  /**
   * Сколько раз бейдж ОТПРАВЛЯЛСЯ НА ПЕЧАТЬ.
   *
   * ⚠️ **Только для чтения.** Поля для него нет ни в create-, ни в
   * update-запросе, и не появится: значение увеличивает один
   * `registerBadgePrint`. Это учётный факт, и возможность вписать его руками
   * лишила бы его смысла.
   *
   * Считаются ОТПРАВКИ, а не листы: браузер не сообщает, подтвердил оператор
   * системный диалог печати или отменил. Подпись обязана говорить «отправлено
   * на печать», а не «напечатано».
   */
  badgePrintCount: number
  /**
   * Порядковый номер в мероприятии, 1…N. Выдаёт сервер при создании, и он
   * НЕ МЕНЯЕТСЯ. Номер удалённого никому не переходит, так что пропуски в
   * нумерации — норма, а не ошибка.
   */
  number: number
  /** Только проставленные значения — см. `ParticipantFieldValue`. */
  fieldValues: ParticipantFieldValue[]
  /**
   * Содержимое QR-кода: `base64url(16 сырых байт UUID)` без паддинга,
   * ровно 22 символа.
   *
   * **Вычисляется из `id`** и отдельной колонкой не хранится — иначе появилась
   * бы вторая правда об одном и том же и возможность их рассинхронизировать.
   * Сырые байты, а не текст UUID: 22 символа против 48 дают заметно более
   * редкую сетку QR и уверенное чтение с телефона.
   */
  qrPayload: string
  createdAt: string
  updatedAt: string
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  СОРТИРОВКА СПИСКА УЧАСТНИКОВ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Сортировать можно по ЛЮБОЙ колонке экрана, включая графы конструктора.
 *
 * ⚠️ Дефолт (`""`) — **`last_name`** по возрастанию, а не `-created_at`: людей
 * в списке ищут глазами по алфавиту, а не по дате появления в базе.
 *
 * ⚠️ ВСЁ УМЕЩАЕТСЯ В ОДИН ПАРАМЕТР, включая id графы. Сортировка едет в
 * ссылке — той самой, которую человек копирует коллеге, — и тем же параметром
 * пользуется выгрузка, где порядок строк в файле обязан совпадать с экраном.
 * Второй параметр `orderByFieldId` дал бы четыре состояния вместо двух, и
 * половина из них («ключ базовый, но id тоже прислали») бессмысленна.
 *
 * ⚠️ НЕИЗВЕСТНАЯ СОРТИРОВКА — ЭТО УМОЛЧАНИЕ, А НЕ ОШИБКА, и так же ведёт себя
 * сервер: удалённая графа, поле чужого мероприятия, файл — всё это молча даёт
 * `last_name`. Это список: сюда приходят по вчерашней закладке, и отказ показал
 * бы пустой экран вместо участников. */

/** Колонки самого участника — без направления. */
export const PARTICIPANT_ORDER_COLUMNS = [
  'number',
  'last_name',
  /** НАЗВАНИЕ роли из справочника, а не её id. */
  'role',
  'status',
  'accreditation_status',
  /** Своей колонки на экране нет: ключ старше остальных и остаётся в контракте. */
  'created_at',
] as const
export type ParticipantOrderColumn = typeof PARTICIPANT_ORDER_COLUMNS[number]

/** Префикс сортировки по дополнительному полю: `field:<uuid>`. */
export const PARTICIPANT_ORDER_FIELD_PREFIX = 'field:'

export type ParticipantOrderKey = ParticipantOrderColumn | `field:${string}`

/** Значение `orderBy` на проводе. Префикс `-` — по убыванию. */
export type ParticipantOrderBy = '' | ParticipantOrderKey | `-${ParticipantOrderKey}`

/**
 * ⚠️ ПО ПОЛЮ ТИПА `FILE` СОРТИРОВКИ НЕТ, и это не забытая ветка. В строке
 * значения у него лежит сгенерированный ключ объекта в хранилище: сортировка
 * по нему перемешала бы список случайным образом и выглядела бы при этом
 * работающей. Вопрос «есть справка / нет справки» — это фильтр
 * `fieldFilled`/`fieldEmpty`, и он уже есть.
 *
 * Сервер отвечает тем же: такой `orderBy` он молча заменяет умолчанием.
 */
export function isSortableFieldType(type: ParticipantFieldType): boolean {
  return type !== 'PARTICIPANT_FIELD_TYPE_FILE'
}

/**
 * Собрать `orderBy` из ключа и направления.
 *
 * Конструктор, а не склейка строк по месту: префикс, направление и написание
 * uuid — это один формат, и знать его должен ровно один файл.
 */
export function participantOrderBy(key: ParticipantOrderKey, desc: boolean): ParticipantOrderBy {
  return desc ? `-${key}` : key
}

/** `orderBy` для сортировки по дополнительному полю. */
export function participantOrderByField(fieldId: string, desc: boolean): ParticipantOrderBy {
  return participantOrderBy(`${PARTICIPANT_ORDER_FIELD_PREFIX}${fieldId}`, desc)
}

/**
 * Разобрать `orderBy` на ключ и направление.
 *
 * ⚠️ `null` — СОРТИРОВКА НЕ ЗАДАНА, и это отдельное состояние, а не «то же
 * самое, что `last_name`». Порядок по умолчанию у списка действительно ФИО по
 * возрастанию, но «я ничего не выбирал» и «я выбрал ФИО по возрастанию» —
 * разные ответы на вопрос шапки: в первом случае ни одна колонка не помечена
 * стрелкой, и третье нажатие по любому заголовку возвращает экран именно в это
 * состояние. Схлопни мы их в одно — «сбросить» у колонки ФИО не отличалось бы
 * от «оставить как есть».
 */
export function parseParticipantOrderBy(
  value: string,
): { key: ParticipantOrderKey, desc: boolean } | null {
  if (value === '') return null

  const desc = value.startsWith('-')
  const key = desc ? value.slice(1) : value
  return { key: key as ParticipantOrderKey, desc }
}

/**
 * Годен ли `orderBy` — проверка ФОРМЫ, как и на сервере.
 *
 * Принадлежит ли поле этому мероприятию и можно ли по нему сортировать, здесь
 * не проверить: справочник полей грузится отдельно и к моменту разбора адресной
 * строки может быть ещё пуст. Негодное по существу значение отсеет сервер —
 * тем же молчаливым откатом к умолчанию.
 */
export function isParticipantOrderBy(value: string): value is ParticipantOrderBy {
  // Пустая строка — «сортировка сброшена», законное значение; разбор вернёт
  // на неё `null`, поэтому отвечаем до него.
  const parsed = parseParticipantOrderBy(value)
  if (parsed === null) return true

  const { key } = parsed
  if (key.startsWith(PARTICIPANT_ORDER_FIELD_PREFIX)) {
    return key.length > PARTICIPANT_ORDER_FIELD_PREFIX.length
  }
  return (PARTICIPANT_ORDER_COLUMNS as readonly string[]).includes(key)
}

/**
 * `GET /api/v1/events/{eventId}/participants`. Едет query-строкой.
 *
 * ⚠️ ДВА ПЛОСКИХ СПИСКА ID ВМЕСТО ПАР (ПОЛЕ, ЗНАЧЕНИЕ) — не стиль, а следствие
 * транспорта: повторяющееся вложенное сообщение невозможно передать в
 * query-строке GET-запроса (form-декодер kratos отвергает
 * `fieldFilters[0].fieldId`), а «кто ещё не заплатил» — это именно ссылка,
 * которую нужно уметь открыть и переслать.
 *
 *   `fieldTicked`   — у кого ВСЕ перечисленные поля отмечены;
 *   `fieldUnticked` — у кого НИ ОДНО из перечисленных не отмечено,
 *                     **включая тех, у кого записи значения нет вовсе**.
 *
 * Оба списка объединяются по И между собой и внутри себя. Одно и то же поле в
 * обоих списках даст заведомо пустую выдачу — UI обязан это исключать.
 *
 * axios сериализует массивы как `?fieldTicked=a&fieldTicked=b` (повторяющийся
 * ключ), что kratos и ждёт; менять `paramsSerializer` не нужно.
 */
export interface ListParticipantsRequest {
  page?: number
  pageSize?: number
  /** Подстрока по фамилии, имени и отчеству. Максимум 255. */
  search?: string
  /** Невыставленный фильтр не фильтрует. */
  status?: ParticipantStatus
  /** Пустая строка — любая роль. */
  participantRoleId?: string
  /**
   * `ACCREDITATION_STATUS_UNSPECIFIED` или отсутствие — любая аккредитация.
   *
   * Отдельный фильтр от `status`: «покажи допущенных, кому ещё не выдали
   * бейдж» — самый частый запрос на стойке, и ему нужны оба сразу.
   */
  accreditationStatus?: AccreditationStatus
  /** У кого ВСЕ перечисленные галочки отмечены. Только `CHECKBOX`. */
  fieldTicked?: string[]
  /** У кого НИ ОДНА из перечисленных не отмечена, включая тех, у кого строки нет. */
  fieldUnticked?: string[]
  /**
   * У кого ВСЕ перечисленные поля **заполнены** — любым непустым значением,
   * какого бы типа поле ни было.
   *
   * ⚠️ Это НЕ то же, что `fieldTicked`, даже для галочки: галочка, снятая
   * руками, заполненной не считается (строки значения после этого нет). Два
   * разных вопроса, и слить их в один значило бы отвечать на тот, которого
   * никто не задавал.
   */
  fieldFilled?: string[]
  /** У кого НИ ОДНО из перечисленных не заполнено, включая отсутствие строки. */
  fieldEmpty?: string[]
  /**
   * Точное совпадение текстового значения: ключ — id поля, значение — искомый
   * текст. Осмысленно для `SELECT` и `TEXT`.
   *
   * ⚠️ Сравнение ТОЧНОЕ, по `textValue`: это фильтр «покажи всех, кто выбрал
   * „Мужской“», а не поиск. За поиском — `fieldContains` ниже; сделать
   * равенство подстрочным «чтобы было удобнее» нельзя, оно перестало бы
   * отвечать на свой вопрос — „Мужской“ нашёлся бы по запросу „М“.
   *
   * На проводе — `fieldEquals[<uuid>]=Мужской`. axios сериализует вложенный
   * объект именно так, и ровно этого ждёт form-декодер kratos; `paramsSerializer`
   * трогать не нужно.
   */
  fieldEquals?: Record<string, string>
  /**
   * Поиск по подстроке: ключ — id поля, значение — искомый текст. Регистр не
   * важен. Осмысленно для `TEXT` и `TEXTAREA`.
   *
   * ⚠️ **Отдельный фильтр, а не режим `fieldEquals`.** Вопросы разные и задают
   * их разные люди: «кто выбрал размер XL» — это равенство по варианту
   * справочника, и «XL (детский)» ответом быть не должен; «у кого в графе
   * „клуб“ встречается „Динамо“» — это поиск по тексту, который вводили
   * руками, и „Динамо-2“ ответом быть обязан.
   *
   * Ищется по `textValue`, поэтому у графы-даты, файла и галочки не найдётся
   * ничего: текста у них нет вовсе. Панель фильтров показывает это поле только
   * там, где оно осмысленно, но сервер спросить не запрещает.
   *
   * На проводе — `fieldContains[<uuid>]=Динамо`, как и `fieldEquals`.
   */
  fieldContains?: Record<string, string>
  orderBy?: ParticipantOrderBy
}

/**
 * `GET /api/v1/events/{eventId}/participants/export/xlsx` — те же фильтры, что
 * у списка, плюс явный список отмеченных строк.
 *
 * `page`/`pageSize` отсутствуют намеренно: выгружается весь отфильтрованный
 * список, а не страница. Сервер читает его батчами.
 */
export type ExportParticipantsRequest = Omit<ListParticipantsRequest, 'page' | 'pageSize'> & {
  /**
   * Выгрузить ТОЛЬКО этих участников. Пусто или отсутствует — «по фильтрам».
   *
   * ⚠️ **Непустой список ОТМЕНЯЕТ фильтры, а не сужает их.** Это отметки,
   * поставленные руками по видимым строкам: человек выбрал именно этих людей, и
   * файл обязан содержать ровно их. Пересечение с фильтрами роняло бы
   * отмеченного участника молча — скажем, потому что рядом кто-то снял ему
   * галочку взвешивания, пока список висел открытым, — и отметивший троих
   * получил бы двоих, не узнав, куда делся третий.
   *
   * Правило применяет СЕРВЕР (`ListParticipantsQuery.Normalize`), поэтому
   * послать заодно и фильтры не ошибка. Клиент их всё равно не шлёт: запрос,
   * в котором видно только то, что на него влияет, читается в консоли сети без
   * знания этого правила.
   *
   * Сортировка едет вместе со списком: порядок строк в файле обязан совпадать с
   * порядком на экране, откуда отметки и ставили.
   */
  ids?: string[]
}

export interface ListParticipantsResponse {
  participants: Participant[]
  /** ⚠️ `PageInfo | null` — см. `ListUsersResponse`. */
  pageInfo: PageInfo | null
}

export interface GetParticipantResponse {
  participant: Participant
}

/**
 * `POST /api/v1/events/{eventId}/participants` — admin или owner.
 *
 * Значения дополнительных полей передаются сразу: форма участника — это базовые
 * поля плюс то, что организатор добавил конструктором, и заполняется она за
 * один раз.
 */
export interface CreateParticipantRequest {
  lastName: string
  firstName: string
  /** Может быть пустым — легальное «отчества нет». */
  middleName: string
  /** Пустая строка — роль не назначена. Иначе должна существовать в справочнике ЭТОГО мероприятия. */
  participantRoleId: string
  /** Пустая строка — фотографии нет. */
  photoUrl: string
  /** `PARTICIPANT_STATUS_UNSPECIFIED` означает `PARTICIPATING`. */
  status: ParticipantStatus
  /** Каждое `fieldId` должно быть полем ЭТОГО мероприятия. */
  fieldValues: ParticipantFieldValue[]
}

export interface CreateParticipantResponse {
  participant: Participant
}

/**
 * `PATCH /api/v1/events/{eventId}/participants/{id}` — частичное обновление
 * через proto3 `optional`. Правила те же, что у `UpdateUserRequest`:
 * отсутствие ключа = не менять, `null` тоже = не менять, пустое значение
 * пишется пустой строкой (`{"middleName": ""}` — это и есть «отчества нет»,
 * `{"participantRoleId": ""}` снимает роль).
 *
 * ⚠️ `fieldValues` ведут себя **иначе, чем остальные поля**: присланные
 * значения проставляются, остальные не трогаются, пустой массив — «не менять ни
 * одного поля». Снять галочку можно только прислав `{fieldId, boolValue: false}`.
 *
 * Статус здесь не меняется — для него отдельный RPC.
 */
export interface UpdateParticipantRequest {
  lastName?: string
  firstName?: string
  middleName?: string
  participantRoleId?: string
  photoUrl?: string
  /**
   * Выдача аккредитации. `ACCREDITATION_STATUS_UNSPECIFIED` — не менять.
   *
   * Не `optional`, в отличие от соседей: у enum уже есть значение «не указано»,
   * и вторая форма отсутствия означала бы то же самое дважды.
   */
  accreditationStatus?: AccreditationStatus
  fieldValues?: ParticipantFieldValue[]
}

export interface UpdateParticipantResponse {
  participant: Participant
}

/**
 * `POST /api/v1/events/{eventId}/participants/register-badge-print`.
 *
 * Фиксирует, что бейджи ушли на печать: `badgePrintCount` каждого из
 * перечисленных увеличивается на единицу.
 *
 * ⚠️ **Доступен наблюдателю** — бейджи печатает оператор аккредитации, а он
 * viewer. Требуй эндпоинт прав на запись, печать падала бы с 403 у тех, для
 * кого она и существует.
 *
 * Не идемпотентен намеренно: два нажатия «Печать» — две отправки на принтер.
 */
export interface RegisterBadgePrintRequest {
  participantIds: string[]
}

export interface RegisterBadgePrintResponse {
  /**
   * Обновлённые участники с новыми значениями счётчика.
   *
   * Может быть КОРОЧЕ запрошенного списка: участника могли удалить, пока
   * бейджи готовились. Это не ошибка — бумага уже вышла.
   */
  participants: Participant[]
}

/**
 * `PUT /api/v1/events/{eventId}/participants/{id}/status`.
 *
 * Отдельный RPC, потому что это кнопка на экране сканера, а не форма
 * редактирования: «снять с соревнований» делается одним действием и не должно
 * требовать отправки всей карточки.
 */
export interface SetParticipantStatusRequest {
  /** `PARTICIPANT_STATUS_UNSPECIFIED` запрещён. */
  status: ParticipantStatus
}

export interface SetParticipantStatusResponse {
  participant: Participant
}

/**
 * `POST /api/v1/events/{eventId}/participants/set-accreditation-status`.
 *
 * Отметка ВЫДАЧИ АККРЕДИТАЦИИ — одному участнику или сразу пачке.
 *
 * ⚠️ ЭТО НЕ `SetParticipantStatusRequest`. Тот отвечает, участвует ли человек в
 * соревновании; этот — отдали ли ему бейдж на руки.
 *
 * ⚠️ СПИСОК ДАЖЕ ДЛЯ ОДНОГО. Отметка в строке — это тот же запрос с массивом из
 * одного элемента: отдельного эндпоинта под неё на сервере нет намеренно, чтобы
 * «отметить выделенных» и «отметить этого» не разошлись в поведении.
 *
 * Повторы схлопываются, чужие id молча пропускаются, пустой список — успех.
 * `ACCREDITATION_STATUS_UNSPECIFIED` сервер отвергает: ноль означает «не
 * менять», и принять его за умолчание значило бы СНЯТЬ аккредитацию всем
 * перечисленным.
 *
 * ⚠️ Требует прав на запись (`canManage`), в отличие от печати бейджей: печать
 * регистрирует событие, а выдача пишет то же поле карточки, что и `update()`.
 */
export interface SetParticipantAccreditationStatusRequest {
  participantIds: string[]
  /** `ACCREDITATION_STATUS_UNSPECIFIED` запрещён. */
  accreditationStatus: AccreditationStatus
}

export interface SetParticipantAccreditationStatusResponse {
  /**
   * Изменённые участники — список подменяет ими свои строки без второго
   * запроса.
   *
   * Может быть КОРОЧЕ запрошенного: участника могли удалить, пока держали
   * выделение. Это не ошибка — остальным статус выставлен.
   */
  participants: Participant[]
}

/**
 * `PUT /api/v1/events/{eventId}/participants/{id}/fields/{fieldId}` — проставить
 * ОДНО дополнительное поле.
 *
 * ⚠️ Отдельный узкий эндпоинт, а не `PATCH` участника, именно потому, что его
 * жмут **параллельно**: весовщик отмечает взвешивание в тот же момент, когда
 * кассир отмечает взнос. Запись идёт одной строкой значения (upsert по паре
 * `(participant_id, field_id)`), поэтому две отметки не затирают друг друга —
 * чего не было бы, отправляй каждый из них карточку целиком.
 *
 * Из этого следует правило для UI: галочка в таблице обязана дёргать **этот**
 * метод, а не собирать `UpdateParticipant`.
 */
export interface SetParticipantFieldValueRequest {
  /** Значение для `CHECKBOX`. */
  boolValue?: boolean
  /** Значение для `TEXT`, `TEXTAREA`, `SELECT`. Тримится; для `SELECT` обязано быть из `options`. */
  textValue?: string
  /** Значение для `DATE`/`DATETIME`, RFC3339. Для `DATE` сервер обнуляет время до 00:00 UTC. */
  dateValue?: string | null
  /**
   * Явная очистка: строка значения удаляется целиком (для `FILE` — вместе с
   * объектом в хранилище).
   *
   * ⚠️ НУЖЕН ПОТОМУ, ЧТО «ПУСТО» И «НЕ ЗАДАНО» В PROTO3 НЕРАЗЛИЧИМЫ: пустая
   * строка в `textValue` — это и «сотри», и «клиент не заполнил это поле
   * запроса». Флаг делает намерение явным. Когда `clear: true`, остальные
   * значения игнорируются.
   *
   * ⚠️ Для поля типа `FILE` это ЕДИНСТВЕННОЕ, что принимает данный RPC:
   * запись значения он отвергает (`VALIDATION_FAILED`, «используйте загрузку
   * файла»). Тело собирает `setFieldValueBody()` из
   * `lib/participant-field-value.ts` — руками эту развилку не повторяйте.
   */
  clear?: boolean
}

export interface SetParticipantFieldValueResponse {
  participant: Participant
}

/**
 * `DELETE /api/v1/events/{eventId}/participants/{id}` — удаление **жёсткое**:
 * участник не владеет ни сессиями, ни историей, а строка, которую надо убрать,
 * — это обычно опечатка из импортированного файла.
 *
 * Поэтому в интерфейсе здесь честное «Удалить», а «снять с соревнований» — это
 * смена статуса, а не удаление.
 */
export interface DeleteParticipantResponse {
  /** Удалённый участник — последнее, что о нём известно. */
  participant: Participant
}

/**
 * `POST /api/v1/events/{eventId}/participants/resolve-qr` — читает любой
 * участник мероприятия: сканирование на входе это чтение.
 *
 * POST, а не GET, хотя ничего не пишет: payload не должен оседать в логах
 * прокси и в истории браузера строкой запроса.
 *
 * ⚠️ Две разные ошибки, которые обязаны выглядеть по-разному:
 *   `400 INVALID_QR_PAYLOAD`   — это вообще не наш код («отсканируйте ещё раз»);
 *   `404 PARTICIPANT_NOT_FOUND` — код наш, но такого участника в ЭТОМ
 *                                 мероприятии нет («вас нет в списке»).
 * Человек на двери действует на них по-разному.
 */
export interface ResolveParticipantQrRequest {
  /** Ровно то, что прочитал сканер: `base64url(16 байт)` без паддинга. */
  qrPayload: string
}

export interface ResolveParticipantQrResponse {
  participant: Participant
}

/* ═════════════════════════════════════════════════════════════════════════
   9.1 ИМПОРТ — ДВЕ ФАЗЫ, ПОТОМУ ЧТО УЧАСТНИК НИЧЕМ НЕ УНИКАЛЕН
   ═════════════════════════════════════════════════════════════════════════

   У участника нет ни одного уникального поля: ни почты, ни номера, ни
   идентификатора в файле. Полные тёзки в списке на несколько сотен человек —
   норма, а не аномалия. Поэтому сервер **никогда не сопоставляет строку файла с
   существующей записью сам**: угадав не того, он переписал бы допуски живому
   участнику, и узнали бы об этом на регистрации.

   Отсюда две фазы, и границу между ними держит контракт:

     ФАЗА 1 — `POST /api/v1/events/{eventId}/participants/import`
       (multipart/form-data, поле `file`). Разбирает файл и **ничего не пишет**.
       Возвращает все строки в исходном порядке, а к каждой — список
       существующих участников с тем же ФИО (`matches`). Это подсказка, а не
       решение: список бывает пустым и бывает длиннее одного.

     ФАЗА 2 — `POST /api/v1/events/{eventId}/participants/import/apply`
       (обычный JSON). Единственный путь, которым импорт пишет в базу. Файл сюда
       не едет: строки уже разобраны, и повторный разбор применил бы решения
       оператора к другим байтам, если оператор успел поправить файл на диске.

   ⚠️ ФАЗЫ 1 И ШАБЛОНА НЕТ В `openapi.yaml` — и это не упущение. У
   `ImportParticipants` и `DownloadImportTemplate` нет аннотации
   `google.api.http`: они возят файлы, и сгенерированный маршрут потребовал бы
   прислать 5 МиБ xlsx строкой base64 внутри JSON. HTTP для них написан руками
   в `internal/server/participant_files.go`, проходит ту же цепочку
   аутентификации и те же проверки прав — просто gnostic его не видит.

   ⚠️ АВТОРИЗАЦИЯ ИМПОРТА — MANAGE, А НЕ ЧТЕНИЕ. Шаблон тоже: скачивание
   шаблона идёт через те же справочники, что и загрузка, и viewer'у недоступно
   ни то, ни другое. */

/** Форматы шаблона: `GET .../import/template?format=…`. */
export const IMPORT_TEMPLATE_FORMATS = ['xlsx', 'csv'] as const
export type ImportTemplateFormat = typeof IMPORT_TEMPLATE_FORMATS[number]

/**
 * Лимит размера файла импорта, байты (5 MiB) — копия
 * `participantimport.MaxFileBytes`. Дублируется осознанно: это подсказка
 * **до** загрузки, а не источник правды. Превышение всё равно вернётся как
 * `IMPORT_FILE_TOO_LARGE` (413).
 */
export const IMPORT_MAX_FILE_BYTES = 5 * 1024 * 1024

/** Потолок строк данных в файле — копия `participantimport.maxRows`. */
export const IMPORT_MAX_ROWS = 10_000

/**
 * Потолок решений в одном применении — копия `biz.MaxImportDecisions`.
 * Превышение отвергает запрос **целиком**, то есть не запишется ни одна строка.
 */
export const IMPORT_MAX_DECISIONS = 2000

/**
 * `admin.v1.ImportFieldValue` — ячейка файла, относящаяся к дополнительному
 * полю.
 *
 * ⚠️ Отдельное сообщение, а не значение, потому что у ячейки **три** состояния,
 * и разница между ними — это разница между «импорт ничего не тронул» и «импорт
 * обнулил оплату всему списку»:
 *
 *   • колонки нет в файле         → элемента нет в списке вовсе;
 *   • колонка есть, ячейка пуста  → `raw: ''`;
 *   • колонка есть, что-то есть   → `raw` — то, что в ней написано.
 *
 * Файл федерации без колонки «Взнос» ничего не сообщает об оплате, и обновление
 * по нему не должно снимать галочку, которую оператор поставил руками. Спутать
 * «колонки нет» с «нет» в интерфейсе — самая дорогая ошибка этого экрана,
 * поэтому первое рисуется отдельным знаком, а не как снятая галочка.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ РАЗБОР ЯЧЕЙКИ ЖИВЁТ НА СЕРВЕРЕ (`biz.ParseImportFieldValue`)
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Пока тип был один, сюда ехал уже разобранный `bool`. С семью типами разбор —
 * доменное правило («какие написания даты мы принимаем», «как сопоставить
 * ячейку с вариантом select»), и знает его только тот, у кого есть определение
 * поля. Фронт, разбирающий ячейку сам, обязан был бы повторить эти правила
 * слово в слово — а разойдясь с сервером, тихо импортировал бы не то, что
 * показал в предпросмотре. Поэтому наверх едет **сырой текст**, а то, во что
 * он прочитался, приезжает ЭХОМ рядом с ним — и только в ответе фазы 1.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЭХО РАЗБОРА НАЗАД НЕ ЕДЕТ — ЭТО ПРАВИЛО БЕЗОПАСНОСТИ, А НЕ ЭКОНОМИЯ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `parsed` / `boolParsed` / `textParsed` / `dateParsed` заполняет **сервер** в
 * ответе предпросмотра, чтобы экран показывал не текст ячейки, а то, что будет
 * записано: «12.05.2026» разобранное в дату и «12.05.2026», которое не
 * разобралось, выглядят в файле одинаково, а весь смысл двух фаз — увидеть
 * результат ДО записи.
 *
 * Применение (`ApplyParticipantImport`) разбирает `raw` заново теми же
 * правилами и клиентскому эху **не верит**: поверить ему значило бы дать
 * клиенту прислать `raw: 'не дата'` рядом с `dateParsed: 01.01.1990` и записать
 * в карточку участника значение, которого в файле не было. Поэтому в фазу 2
 * уезжает `ImportFieldValueInput` — `fieldId` и `raw`, и ничего сверх.
 *
 * ⚠️ Запрет держится ТИПОМ, а не памятью: поля эха объявлены в
 * `ImportFieldValueInput` как `?: never`, поэтому `ImportFieldValue` в него не
 * присваивается и «отправлю строку предпросмотра как есть» не компилируется.
 * Тест `TestApplyImportCannotTrustAClientEcho` на сервере страхует с той
 * стороны — обе страховки нужны, потому что нарушение здесь однострочное и
 * молчаливое.
 */
export interface ImportFieldValue {
  fieldId: string
  /** СЫРОЙ текст ячейки, как он был в файле. `''` — «колонка есть, ячейка пуста». */
  raw: string
  /**
   * @deprecated Разобранное значение галочки.
   *
   * Сервер читает его ТОЛЬКО когда `raw` пуст и поле — `CHECKBOX`, ради
   * клиента, который ещё не обновился. Новый код присылает `raw` и не
   * заполняет это поле никогда; читать его для отображения тоже не нужно —
   * показывать надо разобранное эхо (`boolParsed`).
   */
  boolValue: boolean
  /**
   * Разобралась ли ячейка под тип поля. **Только в ответе фазы 1.**
   *
   * `false` означает, что значение НЕ прочиталось и записано не будет; почему
   * именно — сказано в предупреждении строки с тем же `fieldId`
   * (`UNPARSABLE` / `UNKNOWN_OPTION` / `NOT_IMPORTABLE`).
   *
   * ⚠️ `true` НЕ означает «будет записано»: прочитанное значение всё ещё может
   * быть отброшено как неприменимое к роли участника — тогда у строки есть
   * предупреждение `INAPPLICABLE` с тем же `fieldId`. Это два разных вопроса
   * («поняли ли мы ячейку» и «относится ли колонка к этому человеку»), и
   * склеить их в один флаг значит потерять ответ на второй.
   */
  parsed: boolean
  /** Разобранная галочка (`CHECKBOX`). Осмысленна только при `parsed`. */
  boolParsed: boolean
  /**
   * Разобранный текст (`TEXT` / `TEXTAREA` / `SELECT`). Осмыслен только при
   * `parsed`.
   *
   * Для `SELECT` это **каноническое** написание варианта из справочника поля,
   * а не то, что было в ячейке: записано будет именно оно, и предпросмотр
   * обязан показывать то же самое. Для `TEXT`/`TEXTAREA` — уже обрезанный по
   * лимиту текст (см. предупреждение `TRUNCATED`).
   */
  textParsed: string
  /**
   * Разобранная дата (`DATE` / `DATETIME`), RFC3339. Осмысленна только при
   * `parsed`; `null` — даты нет.
   *
   * ⚠️ Для `DATE` время уже обнулено до полуночи UTC — ровно так значение и
   * ляжет в базу, и показывать его надо UTC-компонентами (см.
   * `lib/participant-field-value.ts`).
   */
  dateParsed: string | null
}

/**
 * Ячейка файла в том виде, в каком она уезжает в **фазу 2**: `fieldId` и `raw`.
 *
 * ⚠️ `?: never` на полях эха — не украшение типа, а единственное, что мешает
 * отправить строку предпросмотра «как есть». См. шапку `ImportFieldValue`:
 * сервер эхо игнорирует, но клиент, который его шлёт, выглядит как клиент,
 * которому оно нужно, — и следующая правка сервера «раз уж прислали, зачем
 * парсить дважды» становится дырой.
 */
export interface ImportFieldValueInput {
  fieldId: string
  /** СЫРОЙ текст ячейки. Именно его сервер разберёт заново. */
  raw: string
  parsed?: never
  boolParsed?: never
  textParsed?: never
  dateParsed?: never
  boolValue?: never
}

/**
 * `admin.v1.ParticipantImportInput` — значения одной строки файла.
 *
 * На проводе это ОДНО сообщение на обе стороны: назад в превью
 * (`ImportRow.input`) и вперёд в решение (`ImportDecision.data`). Два
 * зеркальных типа разъехались бы, и импорт показывал бы одно, а записывал
 * другое.
 *
 * ⚠️ Здесь типов всё-таки два, и разница между ними ровно одна — носитель
 * ячейки. В превью это `ImportFieldValue` (сырой текст **плюс эхо разбора**),
 * в решении — `ImportFieldValueInput` (только `fieldId` и `raw`). Эхо в фазу 2
 * не едет по правилу безопасности; см. шапку `ImportFieldValue`. Всё
 * остальное — ФИО, роль, статус — у обоих одно и то же поле в поле.
 */
export interface ParticipantImportInput {
  lastName: string
  firstName: string
  middleName: string
  /**
   * Роль, **уже сопоставленная** со справочником мероприятия по названию.
   * Пустая строка — колонки нет, ячейка пуста либо название не нашлось (тогда
   * у строки будет заполнен `unresolvedRole`, и это НЕ ошибка).
   */
  participantRoleId: string
  /** `PARTICIPANT_STATUS_UNSPECIFIED` — колонки нет или ячейка пуста. */
  status: ParticipantStatus
  /** Только те поля, чьи **колонки есть в файле**. См. `ImportFieldValue`. */
  fieldValues: ImportFieldValue[]
}

/**
 * То же сообщение в сторону сервера: значения строки в `ImportDecision.data`.
 *
 * Отличается только `fieldValues` — см. `ImportFieldValueInput`. Собирается из
 * `ParticipantImportInput` явно, полем за полем: `{ ...input }` протащил бы эхо
 * молча, а `?: never` ловит это на компиляции.
 */
export interface ParticipantImportInputPayload {
  lastName: string
  firstName: string
  middleName: string
  participantRoleId: string
  status: ParticipantStatus
  fieldValues: ImportFieldValueInput[]
}

/**
 * `admin.v1.ImportWarningKind` — вид предупреждения предпросмотра.
 *
 * ⚠️ ЭТО ТО, ПО ЧЕМУ КЛИЕНТ ГРУППИРУЕТ И ФИЛЬТРУЕТ. Фраза для человека едет
 * рядом, в `message`, и её формулировка может измениться в любом релизе:
 * раньше фронт разбирал эту фразу регулярками, и каждое переписывание текста
 * на сервере молча превращало сводку «нераспознанная дата в колонке X —
 * 380 строк» обратно в простыню из 380 одинаковых предложений. Разбирать
 * `message` больше нельзя ничем и нигде.
 *
 * ⚠️ `UNPARSABLE` и `UNKNOWN_OPTION` — РАЗНЫЕ виды, хотя обе про «значение не
 * записалось»: во втором случае ячейка прочитана прекрасно, это справочник её
 * не знает, и следующий шаг оператора другой — не «поправить написание», а
 * «добавить вариант». `INAPPLICABLE` отдельно по той же причине: там ячейка
 * разобралась и отброшена уже по роли участника.
 */
export type ImportWarningKind =
  | 'IMPORT_WARNING_KIND_UNSPECIFIED'
  | 'IMPORT_WARNING_KIND_UNPARSABLE'
  | 'IMPORT_WARNING_KIND_UNKNOWN_OPTION'
  | 'IMPORT_WARNING_KIND_TRUNCATED'
  | 'IMPORT_WARNING_KIND_INAPPLICABLE'
  | 'IMPORT_WARNING_KIND_REQUIRED_MISSING'
  | 'IMPORT_WARNING_KIND_NOT_IMPORTABLE'

/**
 * `admin.v1.ImportWarning` — одно предупреждение: машинная часть плюс фраза.
 *
 * ⚠️ Значения `kind`, которых нет в объединении выше, — штатное будущее, а не
 * поломка: сервер обновляется раньше вкладки. Читающий код обязан
 * ДЕГРАДИРОВАТЬ (показать `message` как есть), а не терять предупреждение; как
 * именно — см. `lib/import-warnings.ts`.
 */
export interface ImportWarning {
  kind: ImportWarningKind
  /**
   * Поле, к которому относится предупреждение. **Пусто — предупреждение о
   * строке целиком**, а не о конкретной колонке.
   */
  fieldId: string
  /**
   * Готовая фраза на русском — то, что показывают человеку. Разбирать её
   * НЕЛЬЗЯ: всё машинное лежит в `kind` и `fieldId`.
   */
  message: string
}

/**
 * `admin.v1.ImportRow` — одна строка предпросмотра.
 *
 * • `row` — номер строки **как его видит человек в Excel**, 1-based: строка 1 —
 *   заголовок, первая строка данных — 2.
 * • `raw` — исходные ячейки, склеенные через `" | "`, чтобы человек нашёл
 *   строку в своём файле глазами.
 * • `input` — разобранные значения. Message-поле ⇒ формально `null`; на
 *   строке с ошибкой сервер всё равно отдаёт то, что успел разобрать.
 * • `matches` — существующие участники, которыми эта строка **может** быть:
 *   совпадение по фамилии и имени без учёта регистра, не более 10.
 *   ⚠️ Отчество в сравнение НЕ входит (файл федерации часто вовсе без колонки
 *   «Отчество»), но у кандидатов оно есть — именно оно и позволяет оператору
 *   различить двух тёзок.
 * • `error` — ошибка **уровня строки**, по-русски: пустое ФИО, непонятное
 *   «да/нет», роль не из справочника. Строка всё равно возвращается — оператору
 *   нужен построчный отчёт, а не отказ целиком из-за одной опечатки в списке на
 *   тысячу человек.
 */
export interface ImportRow {
  row: number
  raw: string
  input: ParticipantImportInput | null
  matches: Participant[]
  /** Пустая строка — строка годна. */
  error: string
  /**
   * Название роли **из файла**, которого нет в справочнике. Пустая строка —
   * колонки не было, ячейка пуста либо название нашлось.
   *
   * ⚠️ **Это не ошибка, а состояние, из которого есть выход.** Строка полностью
   * пригодна к импорту — просто без роли, пока оператор не решит иначе.
   */
  unresolvedRole: string
  /**
   * Что в строке будет молча изменено или прочитано не так, как ждал оператор.
   *
   * Не ошибки — строка импортируется, — но и не пустяки: «почему у гостя не
   * проставилось взвешивание» и «почему пустая колонка с датами» заслуживают
   * ответа ДО записи, а не после.
   *
   * ⚠️ Группировать и привязывать к колонке нужно по `kind` + `fieldId`, а
   * `message` только показывать. См. `ImportWarning`.
   */
  warnings: ImportWarning[]
}

/** Ответ фазы 1. `total` — сколько строк файла разобрано, включая строки с ошибками. */
export interface ImportParticipantsResponse {
  rows: ImportRow[]
  total: number
  /**
   * Названия ролей из файла, которых нет в справочнике, — по одному разу,
   * по алфавиту.
   *
   * Сводка поверх построчного `unresolvedRole`: решение принимается ПО РОЛИ,
   * а не по каждой из четырёхсот строк, где она встретилась.
   */
  unknownRoles: string[]
}

/** `admin.v1.ImportAction` — что оператор решил сделать со строкой. */
export type ImportAction =
  | 'IMPORT_ACTION_UNSPECIFIED'
  | 'IMPORT_ACTION_CREATE'
  | 'IMPORT_ACTION_UPDATE'
  | 'IMPORT_ACTION_SKIP'
export const IMPORT_ACTIONS = [
  'IMPORT_ACTION_UNSPECIFIED',
  'IMPORT_ACTION_CREATE',
  'IMPORT_ACTION_UPDATE',
  'IMPORT_ACTION_SKIP',
] as const satisfies readonly ImportAction[]

/**
 * Действия, которые оператор реально может выбрать.
 *
 * ⚠️ `IMPORT_ACTION_UNSPECIFIED` сервер отвергает: «решение не принято» по
 * проводу непредставимо. Из этого следует важное для интерфейса — нерешённую
 * строку нельзя «отправить как есть», её надо либо решить, либо не отправлять.
 * У каждого умолчания здесь есть цена: либо дубликат, либо перезапись живого
 * человека.
 */
export const DECIDABLE_IMPORT_ACTIONS = [
  'IMPORT_ACTION_CREATE',
  'IMPORT_ACTION_UPDATE',
  'IMPORT_ACTION_SKIP',
] as const satisfies readonly ImportAction[]
export type DecidableImportAction = typeof DECIDABLE_IMPORT_ACTIONS[number]

export const IMPORT_ACTION_LABELS: Record<DecidableImportAction, string> = {
  IMPORT_ACTION_CREATE: 'Создать нового',
  IMPORT_ACTION_UPDATE: 'Обновить существующего',
  IMPORT_ACTION_SKIP: 'Пропустить',
}

/**
 * `admin.v1.ImportDecision` — решение оператора по одной строке.
 *
 * • `row` — номер строки из предпросмотра. Нужен, только чтобы назвать строку
 *   в отчёте.
 * • `targetId` — **обязателен при `IMPORT_ACTION_UPDATE` и запрещён при
 *   остальных**. Условное правило контракт не выражает, проверка стоит в biz,
 *   поэтому «обновить, не сказав кого» компилируется и падает на сервере —
 *   ловить это обязан интерфейс.
 * • `data` — значения строки. При `IMPORT_ACTION_SKIP` игнорируются, поэтому
 *   строку, которая не разобралась (`ImportRow.input === null`), можно честно
 *   отправить пропуском и не терять её из арифметики отчёта.
 *   ⚠️ Тип — `ParticipantImportInputPayload`, а не строка предпросмотра: эхо
 *   разбора обратно не едет (см. `ImportFieldValue`).
 */
export interface ImportDecision {
  row: number
  action: DecidableImportAction
  targetId?: string
  data?: ParticipantImportInputPayload
}

/** Запрос фазы 2. Максимум `IMPORT_MAX_DECISIONS` решений за вызов. */
export interface ApplyParticipantImportRequest {
  decisions: ImportDecision[]
}

/** Отвергнутая строка. */
export interface ImportRowError {
  row: number
  /** Имя значения `ErrorReason` — тот же ключ локализации, что и в обычном ответе об ошибке. */
  reason: string
  /**
   * Пояснение для оператора, **по-русски**. В отличие от `message` в конверте
   * ошибки, это текст для человека, и показывать его можно — он единственное
   * объяснение, которое оператор получит.
   */
  message: string
}

/**
 * Ответ фазы 2 — единственный ответ импорта, за которым стоит запись в базу.
 *
 * ⚠️ `created + updated > 0 && failed > 0` — **частичный успех**,
 * самостоятельный исход, а не «почти успех» и не «почти провал»: часть людей
 * уже в базе, и повторять импорт целиком нельзя.
 *
 * `errors` перечисляет **только отвергнутые** строки: на списке в тысячу
 * человек ответ иначе весил бы больше файла, из которого пришёл.
 */
export interface ApplyParticipantImportResponse {
  created: number
  updated: number
  skipped: number
  failed: number
  errors: ImportRowError[]
}

/* ═════════════════════════════════════════════════════════════════════════
   10. ОШИБКИ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Конверт ошибки kratos (`errors.Error`) — единая форма для **всех** не-2xx.
 *
 * • `code` — число, равное HTTP-статусу.
 * • `reason` — **ключ локализации**, по нему и только по нему ветвится фронт.
 * • `message` — текст для разработчика. **Никогда не показывать пользователю.**
 * • `metadata` — `map`, а значит присутствует всегда (`{}`, когда пусто):
 *   читается безусловно, `err.metadata.email` без `?.` не упадёт.
 *
 * `reason` типизирован `string`, а не `ErrorReason`, намеренно — см. ниже.
 * Нормализация в `ApiError` — задача `lib/api-error.ts`.
 */
export interface ErrorResponse {
  code: number
  reason: string
  message: string
  metadata: Record<string, string>
}

/**
 * Полный список `admin.v1.ErrorReason` — 32 значения. Это и есть словарь
 * i18n-ключей фронта. Источник истины — `api/admin/v1/error_reason.proto`;
 * в `openapi.yaml` его нет физически (gnostic эмитит только схемы, достижимые
 * из RPC, а конверт ошибки не является ответом ни одного RPC).
 *
 * HTTP-статусы:
 *   400 — `VALIDATION_FAILED`, `WRONG_CURRENT_PASSWORD`, `WEAK_PASSWORD`,
 *         `INVALID_QR_PAYLOAD`, `IMPORT_FILE_INVALID`
 *   401 — `INVALID_CREDENTIALS`, `ACCESS_TOKEN_*`, `REFRESH_TOKEN_*`,
 *         `SESSION_REVOKED`
 *   403 — `FORBIDDEN`, `USER_DISABLED`
 *   404 — `SESSION_NOT_FOUND`, `USER_NOT_FOUND`, `EVENT_NOT_FOUND`,
 *         `EVENT_MEMBER_NOT_FOUND`, `PARTICIPANT_*_NOT_FOUND`
 *   409 — `*_ALREADY_EXISTS`, `CANNOT_MODIFY_SELF`, `LAST_ADMIN`
 *   413 — `IMPORT_FILE_TOO_LARGE`
 *   429 — `RATE_LIMITED`
 *   500 — `UNKNOWN`
 */
export const ERROR_REASONS = [
  'UNKNOWN',
  'VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'ACCESS_TOKEN_MISSING',
  'ACCESS_TOKEN_INVALID',
  'ACCESS_TOKEN_EXPIRED',
  'REFRESH_TOKEN_INVALID',
  'REFRESH_TOKEN_EXPIRED',
  'REFRESH_TOKEN_REUSED',
  'SESSION_REVOKED',
  'SESSION_NOT_FOUND',
  'FORBIDDEN',
  'USER_NOT_FOUND',
  'EMAIL_ALREADY_EXISTS',
  'USERNAME_ALREADY_EXISTS',
  'USER_DISABLED',
  'WRONG_CURRENT_PASSWORD',
  'WEAK_PASSWORD',
  'CANNOT_MODIFY_SELF',
  'RATE_LIMITED',
  'EVENT_NOT_FOUND',
  'EVENT_MEMBER_NOT_FOUND',
  'EVENT_MEMBER_ALREADY_EXISTS',
  'LAST_ADMIN',
  'PARTICIPANT_ROLE_NOT_FOUND',
  'PARTICIPANT_ROLE_ALREADY_EXISTS',
  'PARTICIPANT_NOT_FOUND',
  'INVALID_QR_PAYLOAD',
  'PARTICIPANT_FIELD_NOT_FOUND',
  'PARTICIPANT_FIELD_ALREADY_EXISTS',
  'IMPORT_FILE_INVALID',
  'IMPORT_FILE_TOO_LARGE',
  'DEVICE_TICKET_INVALID',
  'ACCESS_ZONE_NOT_FOUND',
  'ACCESS_ZONE_ALREADY_EXISTS',
  'ACCESS_ZONE_ABBREVIATION_ALREADY_EXISTS',

  /* — справочник уровней доступа: тот же скоуп и те же права, что у зон — */
  'ACCESS_LEVEL_NOT_FOUND',
  /**
   * ⚠️ 409 с `metadata.number` — занятый НОМЕР, а не описание. Форма обязана
   * подсветить поле номера: описание менять бессмысленно, конфликт не в нём, и
   * ошибка под описанием отправит человека переписывать текст, который ни при
   * чём.
   */
  'ACCESS_LEVEL_ALREADY_EXISTS',

  /* — справочник пиктограмм: тот же скоуп и те же права — */
  'PICTOGRAM_NOT_FOUND',
  /**
   * ⚠️ 409 с `metadata.name` — занятое НАЗВАНИЕ. Сравнение без учёта регистра,
   * как у зон и ролей; в `metadata` приезжает то название, которое ОТПРАВИЛИ, а
   * не то, что лежит в базе, — иначе подсказка выглядела бы как потерянный
   * ввод.
   */
  'PICTOGRAM_ALREADY_EXISTS',
  /**
   * 404 на байты иконки. Отдельная причина от `BADGE_ASSET_NOT_FOUND`: там
   * клиент рисует бейдж без подложки, здесь — строку справочника без картинки.
   */
  'PICTOGRAM_ICON_NOT_FOUND',
  'PICTOGRAM_ICON_INVALID',
  /** 400 с `metadata.max_bytes`. */
  'PICTOGRAM_ICON_TOO_LARGE',
  /** 404 с `metadata.number`: номер не выдавался или его владельца удалили. */
  'PARTICIPANT_NUMBER_NOT_FOUND',

  /* — объектное хранилище: фотографии участников и картинки бейджа — */
  'PARTICIPANT_PHOTO_NOT_FOUND',
  'PARTICIPANT_PHOTO_INVALID',
  'PARTICIPANT_PHOTO_TOO_LARGE',
  /**
   * ⚠️ 501, и имя обманывает: причина ОБЩАЯ для всего, что лежит в объектном
   * хранилище, — фотографий участников и подложек бейджа одинаково. Второй код
   * для того же состояния дал бы два ключа локализации на одну строку, поэтому
   * текст на экране пишется про хранилище, а не про фотографии.
   */
  'PHOTO_STORAGE_DISABLED',

  /* — шаблон бейджа — */
  /**
   * ⚠️ 404, и это ШТАТНОЕ состояние нового мероприятия, а не поломка: шаблон
   * никто не создаёт автоматически, организатор выбирает заготовку. Стор
   * держит его отдельным флагом и НЕ кладёт в `error`, иначе на каждом заходе
   * в новое мероприятие всплывал бы красный тост.
   */
  'BADGE_TEMPLATE_NOT_FOUND',
  'BADGE_ASSET_NOT_FOUND',
  'BADGE_ASSET_INVALID',
  'BADGE_ASSET_TOO_LARGE',

  /* — общая библиотека сохранённых макетов — */
  /**
   * ⚠️ В отличие от `BADGE_TEMPLATE_NOT_FOUND` это НЕ штатное состояние.
   * Библиотека общая и видна всем целиком, поэтому «нет такой строки» значит
   * ровно одно: пока галерея висела на экране, автор шаблон удалил. Экран
   * обязан перечитать список, а не показывать пустое место.
   */
  'BADGE_LIBRARY_TEMPLATE_NOT_FOUND',
  'BADGE_LIBRARY_TEMPLATE_ALREADY_EXISTS',
] as const
export type ErrorReason = typeof ERROR_REASONS[number]

/**
 * ⚠️ **`reason` с провода может НЕ входить в `ErrorReason`.**
 *
 * Источники ошибок выше нашего кода приносят свои собственные `reason`:
 *
 *   `CODEC`        — тело не разобралось: битый JSON или **отсутствующий
 *                    `Content-Type`** (400). См. мину в шапке файла.
 *   `VALIDATOR`    — middleware валидации запроса (400).
 *   `UNAUTHORIZED` — встроенный auth-middleware kratos (401).
 *
 * Фронт **обязан быть защитным**: он старше или новее бэкенда ровно на один
 * деплой. Поэтому `ErrorResponse.reason` — `string`, а не union; неизвестный
 * ключ должен давать общий текст, а не падать и не показывать `message`.
 */
export const UNNORMALIZED_ERROR_REASONS = ['VALIDATOR', 'CODEC', 'UNAUTHORIZED'] as const
export type UnnormalizedErrorReason = typeof UNNORMALIZED_ERROR_REASONS[number]

/**
 * Полезная нагрузка `metadata` по `reason` — сервер кладёт туда ровно то, что
 * нужно, чтобы не разбирать `message` текстом:
 *
 * | `reason`                           | ключ                     | что внутри |
 * |------------------------------------|--------------------------|------------|
 * | `EMAIL_ALREADY_EXISTS`             | `email`                  | занятый адрес — прямо в `setErrors({ email })` |
 * | `USERNAME_ALREADY_EXISTS`          | `username`               | занятое имя |
 * | `EVENT_MEMBER_ALREADY_EXISTS`      | `user_id`                | конфликтующий пользователь |
 * | `PARTICIPANT_ROLE_ALREADY_EXISTS`  | `name`                   | конфликтующее название |
 * | `PARTICIPANT_FIELD_ALREADY_EXISTS` | `name`                   | конфликтующее название |
 * | `PARTICIPANT_FIELD_ALREADY_EXISTS` | `conflict_scope`          | что именно столкнулось — см. ниже |
 * | `PARTICIPANT_FIELD_ALREADY_EXISTS` | `conflict_role_id`         | только при `otherRole`: чья роль заняла название |
 * | `ACCESS_ZONE_ALREADY_EXISTS`       | `name`                   | конфликтующее название |
 * | `ACCESS_ZONE_ABBREVIATION_ALREADY_EXISTS` | `abbreviation`    | занятая триграмма |
 * | `ACCESS_LEVEL_ALREADY_EXISTS`      | `number`                 | занятый номер уровня, **строкой** |
 * | `PICTOGRAM_ALREADY_EXISTS`         | `name`                   | занятое название, как его ОТПРАВИЛИ |
 * | `PICTOGRAM_ICON_TOO_LARGE`         | `max_bytes`              | лимит, **строкой** |
 * | `WEAK_PASSWORD`                    | `rule`                   | какое правило нарушено |
 * | `RATE_LIMITED`                     | `retry_after_s`          | секунды, **строкой** — `Number(...)` |
 * | `VALIDATION_FAILED`                | `field`                  | имя поля в camelCase, как в JSON |
 * | `IMPORT_FILE_INVALID`              | `detail`                 | что именно не так с файлом |
 * | `IMPORT_FILE_TOO_LARGE`            | `max_bytes`              | лимит, **строкой** |
 *
 * Ключи `metadata` — snake_case (это обычный `map<string,string>`, а не поля
 * сообщения, camelCase-конвертация protojson на них не распространяется).
 * Значения — всегда строки, даже числовые.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  `PARTICIPANT_FIELD_ALREADY_EXISTS.conflict_scope` — ТРИ РАЗНЫХ ОТКАЗА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Название поля уникально в области видимости, а не в мероприятии (см.
 * `ParticipantField.name`), поэтому под одной причиной живут три ситуации, и
 * человеку в каждой надо идти в разное место:
 *
 *   `scope`     — двойник В ТОЙ ЖЕ области: два поля этой роли либо два общих.
 *                 Это же значение приходит, когда сработал уникальный индекс
 *                 БД, а не пред-проверка (две вкладки сохранили разом);
 *   `shared`    — сохраняют поле РОЛИ, а название занято ОБЩИМ полем. Общее
 *                 применимо ко всем, значит у этой роли имя уже есть — искать
 *                 надо в общих полях, а не по ролям;
 *   `otherRole` — сохраняют ОБЩЕЕ поле, а название занято полем какой-то роли.
 *                 Тогда же приезжает `conflict_role_id`.
 *
 * ⚠️ ИМЯ РОЛИ В `metadata` НЕ ПРИХОДИТ НАМЕРЕННО, только id: скопированное в
 * ошибку имя протухает при первом переименовании роли, а справочник ролей у
 * клиента и так загружен. Резолвит его `ParticipantFieldDialog` через
 * `participantRoles.nameOf()`; роль могли удалить, пока экран открыт, — тогда
 * текст остаётся безымянным, но **сырой uuid не показывается никогда**.
 *
 * ⚠️ Исключение — `VALIDATION_FAILED.field`: `internal/service/validate.go`
 * передаёт туда имя поля **как оно выглядит в JSON** (`fullName`,
 * `refreshToken`), а не как в `.proto`. Поэтому `useServerErrors` вешает его на
 * поле формы напрямую, а snake→camel остаётся страховкой на случай
 * `new_password`-подобных имён из будущих проверок.
 *
 * ⚠️ Второе исключение — **имена с индексом**: у эндпоинтов, принимающих
 * массив id (`accessZoneIds[2]`, `accessLevelIds[0]`), в `field` приезжает имя
 * элемента, а не поля формы. Поля с таким именем на экране нет и быть не
 * может — это список галочек, — так что вешать ошибку некуда: такой ответ
 * показывается тостом. Практически он означает битый id в сторе, то есть
 * ошибку клиента, а не ввода.
 */
export const WEAK_PASSWORD_RULES = [
  'min_length',
  'max_length',
  'needs_lower',
  'needs_upper',
  'needs_digit',
  'contains_login',
  'control_chars',
] as const
export type WeakPasswordRule = typeof WEAK_PASSWORD_RULES[number]

/* ═════════════════════════════════════════════════════════════════════════
   11. ГРАНИЦЫ ПОЛЕЙ — КОПИИ СЕРВЕРНЫХ КОНСТАНТ
   ═════════════════════════════════════════════════════════════════════════

   Дублируются осознанно: это подсказка **до** запроса (`maxlength` на инпуте,
   zod-схема формы), а не источник правды. Авторитет — сервер, и он ответит
   `VALIDATION_FAILED` независимо от того, что решила форма. */

export const LIMITS = {
  /** `validate.go`: minLoginLen / maxLoginLen. */
  loginMin: 3,
  loginMax: 255,
  /** `biz.PasswordMinLength` / `PasswordMaxLength`. */
  passwordMin: 8,
  passwordMax: 128,
  emailMax: 255,
  /** `usernamePattern` — `^[a-zA-Z0-9._-]{3,64}$`. */
  usernameMin: 3,
  usernameMax: 64,
  fullNameMax: 128,
  searchMax: 255,
  /** `biz.MaxEventNameLen`. */
  eventNameMax: 255,
  /** `biz.MaxParticipantNameLen` — на каждую из трёх частей ФИО. */
  participantNameMax: 128,
  /** `biz.MaxPhotoURLLen`. */
  photoUrlMax: 1024,
  /** `biz.MaxParticipantRoleNameLen`. */
  participantRoleNameMax: 128,
  /** `biz.ParticipantRoleColorLen` — ширина `#rrggbb`, единственной формы. */
  participantRoleColorLen: 7,
  /** `biz.MaxParticipantFieldNameLen`. */
  participantFieldNameMax: 128,
  /** `biz.MaxParticipantFieldDescriptionLen` — подсказка под полем в форме. */
  participantFieldDescriptionMax: 256,
  /**
   * `biz.MaxParticipantFieldOptions` — сколько вариантов может быть у `SELECT`.
   *
   * Шестьдесят четыре — это не «сколько влезет», а граница, за которой список
   * перестаёт быть выбором: сто вариантов в выпадающем списке никто не
   * дочитывает, и такое поле должно быть текстом со справочником, а не селектом.
   */
  participantFieldOptionsMax: 64,
  /** `biz.MaxParticipantFieldOptionLen` — длина одного варианта. */
  participantFieldOptionMax: 128,
  /** `biz.MaxParticipantFieldTextLen` — ширина колонки `text_value`, общая для `TEXT`/`TEXTAREA`/`SELECT`. */
  participantFieldTextMax: 4096,
  /** `biz.MaxAccessZoneNameLen`. */
  accessZoneNameMax: 128,
  /**
   * `biz.MaxAccessZoneAbbreviationLen`.
   *
   * Восемь, а не три, хотя поле называется «триграмма»: `LASK` и `ZHZH` — по
   * четыре буквы, и жёсткая тройка отвергла бы половину реальной схемы.
   */
  accessZoneAbbreviationMax: 8,

  /** `biz.MaxAccessLevelDescriptionLen` — пояснение уровня для организатора. */
  accessLevelDescriptionMax: 256,
  /**
   * `biz.MinAccessLevelNumber` / `MaxAccessLevelNumber` — границы номера,
   * который печатается на бейдже.
   *
   * ⚠️ Ноль — законная НИЖНЯЯ граница, а не «значение не задано»: шкала,
   * начинающаяся с нуля, встречается, и форма не имеет права считать `0`
   * незаполненным полем.
   *
   * Потолок в 99, а не в 999, — это не про размер `uint32`, а про бейдж:
   * трёхзначный номер на плашке уже не читается через стойку, ради чего он и
   * существует.
   */
  accessLevelNumberMin: 0,
  accessLevelNumberMax: 99,

  /** `biz.MaxPictogramNameLen`. */
  pictogramNameMax: 128,
  /**
   * `biz.MinPictogramSortOrder` / `MaxPictogramSortOrder` — границы позиции.
   *
   * ⚠️ Ноль — законная НИЖНЯЯ граница и значение по умолчанию, а не «не
   * задано». Потолок в 9999 — заслон от опечатки в форме, а не про размер
   * `uint32`: позицию набирают руками, расставляя записи по 10, 20, 30.
   */
  pictogramSortOrderMin: 0,
  pictogramSortOrderMax: 9999,
  /** `biz.MinPictogramIconScale` / `MaxPictogramIconScale`, %. */
  pictogramIconScaleMin: 50,
  pictogramIconScaleMax: 200,
  /**
   * `badgeimage.MaxUploadBytes` — предел на файл иконки, тот же, что у картинок
   * бейджа (12 МиБ). Один лимит на оба маршрута: разбор multipart у них общий.
   */
  pictogramIconMaxBytes: 12 * 1024 * 1024,

  /* — шаблон бейджа, копии констант из `biz/badgetemplate.go` — */
  /** `biz.MaxBadgeTemplateNameLen`. */
  badgeTemplateNameMax: 128,
  /** `biz.MinBadgeSideMM` / `MaxBadgeSideMM` — сторона карточки в мм. */
  badgeSideMinMm: 20,
  badgeSideMaxMm: 300,
  /** `biz.MaxBadgeElements`. Сорок — далеко за пределами любого реального бейджа. */
  badgeElementsMax: 40,
  /** `biz.MinBadgeFontSizePt` / `MaxBadgeFontSizePt`. */
  badgeFontSizeMinPt: 4,
  badgeFontSizeMaxPt: 200,
  /** `biz.MaxBadgeTextContentLen`. */
  badgeTextContentMax: 256,
  /** `biz.MaxBadgeZoneChips` / `MaxBadgeZoneColumns`. */
  badgeZoneMaxVisibleMax: 64,
  badgeZoneColumnsMax: 12,
  /**
   * `biz.MaxBadgeAccessLevelBorderMM` — рамка ячейки шкалы уровней.
   *
   * Пять миллиметров — это не «толстая рамка», а рамка вместо ячейки: на шкале
   * с высотой ячейки 6 мм от цифры не остаётся ничего.
   */
  badgeAccessLevelBorderMaxMm: 5,
  /** `biz.MaxBadgePictogramBorderMM` — рамка квадрата пиктограммы. */
  badgePictogramBorderMaxMm: 5,
  /**
   * `biz.MaxBadgePictogramPaddingMM` — отступ картинки внутри квадрата.
   *
   * ⚠️ Это заслон от опечатки, а не предел вёрстки: настоящую границу
   * (`40 %` стороны) ставит раскладка, где сторона квадрата уже известна —
   * см. `lib/badge/pictograms.ts`.
   */
  badgePictogramPaddingMaxMm: 10,
  /** `biz.MaxBadgeQrQuietZone`. */
  badgeQrQuietZoneMax: 8,
  /** `biz.MaxBadgeFieldTextLen` — подстановочные тексты элемента `FIELD`. */
  badgeFieldTextMax: 64,
  /** `biz.MaxBadgeFieldDateFormatLen` — строка формата даты элемента `FIELD`. */
  badgeFieldDateFormatMax: 32,
} as const

/**
 * Лимит размера картинки бейджа, байты (12 MiB) — копия
 * `badgeimage.MaxUploadBytes`. Дублируется осознанно: это подсказка **до**
 * загрузки, а не источник правды. Превышение всё равно вернётся как
 * `BADGE_ASSET_TOO_LARGE`.
 *
 * Больше, чем у фотографии участника (8 MiB), и это не небрежность: подложка
 * под обрез на A6 в 300 dpi — это 1240×1748 точек, то есть 5–8 МБ ещё до того,
 * как кто-то сделал что-то неправильно.
 */
export const BADGE_ASSET_MAX_FILE_BYTES = 12 * 1024 * 1024

/**
 * Потолок файла, приложенного к дополнительному полю, байты (10 MiB) — копия
 * `biz.MaxParticipantFieldFileBytes`. Подсказка **до** отправки, а не источник
 * правды: превышение вернётся как `PARTICIPANT_FIELD_FILE_TOO_LARGE` (413).
 *
 * ⚠️ Содержимое сервер НЕ перекодирует — в отличие от фотографии участника,
 * которую он пересобирает в jpeg. Здесь хранятся ровно те байты и ровно тот
 * `contentType`, что прислал клиент, поэтому файл и отдаётся обратно только
 * как `attachment` с `nosniff`. Открывать его `inline` (в `<iframe>`,
 * `window.open` на blob-URL с исходным типом) — значит снять эту защиту:
 * загруженный «application/pdf» может оказаться html и выполниться в origin'е
 * админки.
 */
export const PARTICIPANT_FIELD_FILE_MAX_BYTES = 10 * 1024 * 1024

/** `^#[0-9a-f]{6}$` — копия `accessZoneColorPattern`, для строчной строки. */
export const ACCESS_ZONE_COLOR_PATTERN = /^#[0-9a-f]{6}$/



/** `^[a-zA-Z0-9._-]{3,64}$` — копия `usernamePattern` из `validate.go`. */
export const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,64}$/

/**
 * Копия `emailPattern` из `validate.go` — намеренно нестрогая.
 *
 * Это не попытка реализовать RFC 5322 (знаменито плохая идея): отвергаются
 * ровно четыре вещи, которые адресом точно не являются, — нет `@`, ничего до
 * него, ничего после, пробелы. Форма обязана вести себя так же, как сервер,
 * иначе появляется адрес, который принимает одна сторона и отвергает другая.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* ═══════════════════════════════════════════════════════════════════════════
   СПРАВОЧНИК СТРАН — ОБЩИЙ ДЛЯ ВСЕЙ СИСТЕМЫ
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * `admin.v1.Country` — строка справочника стран.
 *
 * ⚠️ **Единственный справочник, который НЕ принадлежит мероприятию.** Роли и
 * зоны локальны, потому что у каждого соревнования свои роли и своё здание.
 * Флаг Беларуси одинаков везде, и копия справочника на мероприятие означала бы
 * 249 строк, заведённых заново каждым организатором.
 *
 * Отсюда права: **читают все аутентифицированные** (список нужен карточке
 * участника и печати в каждом мероприятии), **пишет только `auth.isAdmin`**.
 *
 * Список **поставляется**, а не наполняется: сервер заливает ISO 3166-1 при
 * старте. Создать страну через API нельзя — такой ручки нет.
 */
export interface Country {
  id: string
  /**
   * ISO 3166-1 alpha-3, заглавными: `BLR`.
   *
   * ⚠️ Это значение лежит в поле участника типа `COUNTRY` — не UUID строки.
   * Изменить его нельзя ничем.
   */
  alpha3: string
  /** ISO 3166-1 alpha-2, заглавными: `BY`. По нему берётся флаг из набора. */
  alpha2: string
  /** Печатное название: «Беларусь». */
  name: string
  /**
   * Короткая форма: «США», «ОАЭ».
   *
   * ⚠️ **Пустая строка — нормальное значение**, а не пропуск: она значит
   * «печатать `name`». У большинства стран короткой формы в русском нет.
   */
  shortName: string
  /**
   * Ключ загруженного флага, либо пусто.
   *
   * ⚠️ **Пусто у 249 строк из 249** — это значит «рисовать флаг из
   * поставляемого набора по `alpha2`», а не «флага нет». Непустой ключ
   * появляется, только когда администратор заменил флаг своим файлом; адрес
   * картинки собирается из последнего сегмента ключа.
   */
  flagKey: string
  /**
   * Скрыта из выпадающих списков.
   *
   * На уже проставленные значения не влияет: скрытая страна продолжает
   * печататься у тех, кому её успели проставить, и продолжает сохраняться.
   */
  hidden: boolean
  /**
   * Когда строку последний раз правил человек; `null` — никто не правил.
   *
   * ⚠️ По этому полю рисуется и метка «изменено вручную», и доступность кнопки
   * сброса. Смысл его такой: пока здесь `null`, сервер обновляет строку из
   * поставляемого списка при каждом старте; как только не `null` — больше
   * никогда её не трогает.
   */
  customizedAt: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Файловая часть ключа загруженного флага, либо `null`, если флаг не заменяли.
 *
 * ⚠️ ЭТО НЕ АДРЕС ДЛЯ `<img src>`, И ПОДСТАВЛЯТЬ ЕГО ТУДА НЕЛЬЗЯ. Маршрут флага
 * закрыт аутентификацией, а она в этом приложении едет заголовком
 * `Authorization: Bearer`, которого браузер к `<img>` не добавит: получится 401
 * и битая картинка. Байты забираются `countriesApi.fetchFlag` и превращаются в
 * `data:`-URL — ровно так же, как фотография участника, и по той же причине.
 *
 * В имени лежит uuid загрузки, поэтому оно же служит ключом кэша: заменённый
 * флаг получает новое имя.
 */
export function countryFlagName(country: Country): string | null {
  if (!country.flagKey) return null
  return country.flagKey.split('/').pop() ?? null
}

export interface ListCountriesResponse {
  countries: Country[]
  total: number
}

export interface UpdateCountryRequest {
  /** 1..128 символов. */
  name: string
  /** До 32 символов. Пусто — печатать `name`. */
  shortName: string
  hidden: boolean
}

export interface UpdateCountryResponse {
  country: Country
}

export interface ResetCountryResponse {
  country: Country
}

export interface DeleteCountryFlagResponse {
  country: Country
}
